import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { Tooltip, OverlayTrigger, Popover, Modal } from "react-bootstrap";
import EmojiPicker, { Theme } from 'emoji-picker-react';
import { API_BASE_URL } from "../../../../environment";
import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
  useMemo,
} from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import "./chat.scss";
import CollapseHeader from "../../../../core/common/collapse-header";
import { ChatService } from "./chatService";
import Select from "react-select";
import { useChatNotificationContext } from "./ChatNotificationContext";
import { AuthContext } from "../../../../context/authcontext";
import {
  ExternalChatEndUser,
  ExternalChatMessage,
  AssignedTeam,
  MessageWithUser,
  MessageStatus,
  ConversationFilter,
  StatusFilter,
  ExternalChatEndUserStatusOption,
  GoogleChatAttachment,
  ExternalChatGroupMember,
  ConversationsResponse,
  ThreadMessage,
  EngatiTenantBotParams
} from "./chatTypes";
import dummy from '../../../../assets/male.png'
import { useLocation, useNavigate as useRouterNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { Grid } from 'ldrs/react'
import 'ldrs/react/Grid.css'
import { ImageOff } from "lucide-react";

const MySwal = withReactContent(Swal);


const ImageWithLoader: React.FC<{
  src: string;
  alt: string;
  className?: string;
  onClick?: () => void;
  style?: React.CSSProperties;
}> = ({ src, alt, className, onClick, style }) => {
  const [isImgLoading, setIsImgLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [currentSrc, setCurrentSrc] = useState(src);
  const imgRef = useRef<HTMLImageElement>(null);
  const previousSrcRef = useRef(src);

  useEffect(() => {
    if (previousSrcRef.current === src) return;
    previousSrcRef.current = src;
    setCurrentSrc(src);
    setIsImgLoading(true);
    setHasError(false);
    setRetryCount(0);
  }, [src]);

  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) {
      setIsImgLoading(false);
    }
  }, [currentSrc]);

  const handleError = () => {
    if (retryCount < 2) {
      setTimeout(() => {
        setRetryCount(prev => prev + 1);
        const separator = src.includes('?') ? '&' : '?';
        setCurrentSrc(`${src}${separator}retry=${retryCount + 1}`);
      }, 2000);
    } else {
      setIsImgLoading(false);
      setHasError(true);
    }
  };

  if (hasError) {
    return (
      <div className="msg-image-fallback" style={style?.width ? { width: style.width } : undefined} title="Image unavailable">
        <ImageOff size={40} strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div className="image-with-loader-wrapper" style={{
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '120px',
      minHeight: '120px',
      backgroundColor: isImgLoading ? '#f8f9fa' : 'transparent',
      borderRadius: '8px',
      overflow: 'hidden',
      ...style
    }}>
      {isImgLoading && (
        <div className="image-loader-container" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255, 255, 255, 0.7)',
          zIndex: 1
        }}>
          <Grid size="40" speed="0.9" color="#824EEB" />
        </div>
      )}
      <img
        ref={imgRef}
        src={currentSrc}
        alt={alt}
        className={className}
        onLoad={() => setIsImgLoading(false)}
        onError={handleError}
        onClick={onClick}
        style={{
          ...style,
          opacity: isImgLoading ? 0 : 1,
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          transition: 'opacity 0.2s ease-in'
        }}
      />
    </div>
  );
};

const handleAuthError = (err: any, platformKey: string, navigate: any) => {
  if (err instanceof Error && err.message === "UN_AUTHORIZED_BOT") {
    const formattedPlatform = platformKey
      ? platformKey.charAt(0).toUpperCase() + platformKey.slice(1).toLowerCase()
      : "Engati";

    MySwal.fire({
      title: "Authorization Error",
      text: `The ${formattedPlatform} bot is not authorized. Do you want to re-authorize now?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Re-Authorize",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        if (!platformKey || formattedPlatform.toLowerCase() === "engati" || platformKey.toLowerCase() === "engati") {
          navigate(all_routes.chatSettings);
          return;
        }

        try {
          const data = await ChatService.reauthorizePlatform(platformKey);
          if (data?.auth_url) {
            window.location.href = data.auth_url;
          } else {
            MySwal.fire({
              title: "Error",
              text: "Failed to connect integration.",
              icon: "error",
              confirmButtonText: "OK",
            });
          }
        } catch (e) {
          MySwal.fire({
            title: "Error",
            text: "An error occurred while re-authorizing.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
    return true;
  }
  return false;
};

// Websocket payloads can carry naive timestamps (no timezone suffix) while the API returns UTC with "Z".
// JS parses naive strings as local time, which skews ordering and displayed time, so treat them as UTC.
const normalizeMessageDatetime = (value?: string | null) => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (/([zZ]|[+-]\d{2}:?\d{2})$/.test(trimmed)) return trimmed;
  return `${trimmed.replace(" ", "T")}Z`;
};

const getMessageTime = (value?: string | null) => {
  const time = new Date(normalizeMessageDatetime(value) ?? "").getTime();
  return Number.isNaN(time) ? 0 : time;
};

// Newest first: .messages-container uses column-reverse, so index 0 renders at the bottom.
const compareMessagesNewestFirst = (a: MessageWithUser, b: MessageWithUser) =>
  getMessageTime(b.created_datetime) - getMessageTime(a.created_datetime) ||
  b.external_chat_message_id - a.external_chat_message_id;

const getAssignedTeams = (conversation?: ExternalChatEndUser | null): AssignedTeam[] => {
  if (!conversation) return [];
  if (conversation.assigned_teams?.length) return conversation.assigned_teams;
  if (conversation.assigned_team?.assigned_team_id) {
    return [{ assigned_team_id: conversation.assigned_team.assigned_team_id, name: conversation.assigned_team.name }];
  }
  if (conversation.assigned_channel_team?.team_id) {
    return [{ assigned_team_id: conversation.assigned_channel_team.team_id, name: conversation.assigned_channel_team.team_name }];
  }
  return [];
};

const compareConversationsByLastMessage = (a: ExternalChatEndUser, b: ExternalChatEndUser) =>
  getMessageTime(b.last_message?.created_datetime) - getMessageTime(a.last_message?.created_datetime);

// Engati sends NEW_USER_WELCOME as a user message when a conversation starts; it is a trigger, not chat content.
const isHiddenChatMessage = (message: ExternalChatMessage) =>
  message.external_chat_packet_type?.external_chat_packet_type_key === "NEW_USER_WELCOME" ||
  (message.external_chat_platform_packet_type?.external_chat_platform_packet_type_key === "USER_MESSAGE" &&
    message.external_chat_message_value?.trim() === "NEW_USER_WELCOME");

const getFileNameFromUrl = (url: string) => {
  if (!url || url.startsWith("blob:")) return "";
  const path = url.split(/[?#]/)[0];
  const name = path.substring(path.lastIndexOf("/") + 1);
  try {
    return decodeURIComponent(name);
  } catch (e) {
    return name;
  }
};

// Signed S3/GCS links carry their own expiry (Engati attachments expire after 30 minutes).
const isExpiredSignedUrl = (url: string) => {
  try {
    const params = new URL(url).searchParams;
    const signedAt = params.get("X-Amz-Date") || params.get("X-Goog-Date");
    const ttlSeconds = params.get("X-Amz-Expires") || params.get("X-Goog-Expires");
    const parts = signedAt?.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
    if (parts && ttlSeconds) {
      const signedAtMs = Date.UTC(+parts[1], +parts[2] - 1, +parts[3], +parts[4], +parts[5], +parts[6]);
      return signedAtMs + Number(ttlSeconds) * 1000 < Date.now();
    }
    const legacyExpires = params.get("Expires");
    if (legacyExpires && /^\d+$/.test(legacyExpires)) return Number(legacyExpires) * 1000 < Date.now();
  } catch (e) {
    // not an absolute URL
  }
  return false;
};

const sanitizeNextUrl = (url?: string | null): string | null => {
  if (!url) return null;
  try {
    const baseParsed = new URL(API_BASE_URL);
    const parsed = new URL(url, API_BASE_URL);
    if (parsed.protocol === "http:" && (baseParsed.protocol === "https:" || (typeof window !== "undefined" && window.location.protocol === "https:"))) {
      parsed.protocol = "https:";
    }
    parsed.host = baseParsed.host;
    parsed.port = baseParsed.port;
    return parsed.toString();
  } catch {
    return url.replace(/^http:\/\//i, "https://");
  }
};

const getConversationPlatformKey = (conv?: ExternalChatEndUser | null): string => {
  if (!conv) return "";
  return (
    conv.external_chat_tenant_bot?.external_chat_platform?.external_chat_platform_key ||
    conv.external_chat_channel?.external_chat_channel_key ||
    conv.external_chat_channel_key ||
    ""
  );
};

const getConversationChannelKey = (conv?: ExternalChatEndUser | null): string => {
  if (!conv) return "";
  return (
    conv.external_chat_channel?.external_chat_channel_key ||
    conv.external_chat_channel_key ||
    conv.last_message?.external_chat_channel?.external_chat_channel_key ||
    conv.external_chat_bot_channel_account?.account_name ||
    ""
  );
};

interface ThreadActivity {
  unread: number;
  lastReplyAt: string;
  senderName: string;
  senderAvatar: string | null;
}

const formatThreadReplyTime = (datetime: string) => {
  const time = getMessageTime(datetime);
  if (!time) return "";
  const diffMinutes = Math.floor((Date.now() - time) / 60000);
  if (diffMinutes < 1) return "Now";
  if (diffMinutes < 60) return `${diffMinutes} min`;
  const date = new Date(time);
  if (date.toDateString() === new Date().toDateString()) {
    return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const RelativeReplyTime: React.FC<{ datetime: string }> = ({ datetime }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const intervalId = setInterval(() => setTick((tick) => tick + 1), 30000);
    return () => clearInterval(intervalId);
  }, []);
  return <span className="thread-indicator-time">{formatThreadReplyTime(datetime)}</span>;
};

const BrokenFileIcon = () => (
  <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 12V6.8C20 5.11984 20 4.27976 19.673 3.63803C19.3854 3.07354 18.9265 2.6146 18.362 2.32698C17.7202 2 16.8802 2 15.2 2H8.8C7.11984 2 6.27976 2 5.63803 2.32698C5.07354 2.6146 4.6146 3.07354 4.32698 3.63803C4 4.27976 4 5.11984 4 6.8V17.2C4 18.8802 4 19.7202 4.32698 20.362C4.6146 20.9265 5.07354 21.3854 5.63803 21.673C6.27976 22 7.11984 22 8.8 22H12M16 16L21 21M21 16L16 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FileViewer: React.FC<{ url: string; label?: string; mimeType?: string }> = ({ url, label, mimeType }) => {
  const [isBroken, setIsBroken] = useState(() => isExpiredSignedUrl(url));
  const objectRef = useRef<HTMLObjectElement>(null);

  const fileLabel = label || getFileNameFromUrl(url) || "Open File";
  const isPdf = mimeType === "application/pdf" || /\.pdf$/i.test(fileLabel) || /\.pdf$/i.test(url.split(/[?#]/)[0]);
  // Browsers without a built-in PDF viewer (e.g. mobile Chrome) render <object> fallback content even for valid PDFs.
  const canPreviewPdf = navigator.pdfViewerEnabled !== false;
  const showPreview = isPdf && canPreviewPdf && !isBroken;

  useEffect(() => {
    setIsBroken(isExpiredSignedUrl(url));
  }, [url]);

  // React doesn't attach error listeners to <object>, so listen natively for failed loads (403/404).
  useEffect(() => {
    const el = objectRef.current;
    if (!showPreview || !el) return;
    const handleError = () => setIsBroken(true);
    el.addEventListener("error", handleError);
    return () => el.removeEventListener("error", handleError);
  }, [showPreview, url]);

  if (!url) return null;

  const documentIcon = (
    <div className="msg-file-icon-box">
      <i className="bi bi-file-earmark-text" />
    </div>
  );

  const brokenIcon = (
    <div className="msg-file-icon-box" title="File unavailable">
      <BrokenFileIcon />
    </div>
  );

  return (
    <div className="msg-file-viewer">
      <div className="msg-file-preview" onClick={() => window.open(url, "_blank")}>
        {showPreview ? (
          <object
            ref={objectRef}
            data={`${url}#toolbar=0&navpanes=0&scrollbar=0`}
            type="application/pdf"
            className="msg-file-object"
            aria-label={fileLabel}
          >
            {/* Rendered by the browser when the PDF fails to load */}
            {brokenIcon}
          </object>
        ) : isBroken ? brokenIcon : documentIcon}
      </div>
      <a href={url} target="_blank" rel="noopener noreferrer" className="msg-file-title" title={fileLabel}>
        <i className="bi bi-file-earmark-text me-1" />
        <span className="msg-file-name">{fileLabel}</span>
      </a>
    </div>
  );
};

interface ChatProps {
  tenant_id?: number | string | null;
  contact_id?: number | string | null;
  isContactPage?: boolean;
}

const Chat: React.FC<ChatProps> = ({
  tenant_id,
  contact_id,
  isContactPage,
}) => {
  const { selectedTenantId, auth, isTeamManager } = useContext(AuthContext);
  const userId = auth?.user_id;
  const location = useLocation();
  const routerNavigate = useRouterNavigate();
  const { setActiveConversationId, registerMessageListener, refetchUnreadCount } =
    useChatNotificationContext();

  const isClientAdmin =
    auth?.tenants?.some(
      (t: any) =>
        t.role_key?.toLowerCase() === "client_admin" ||
        t.role_name?.toLowerCase() === "client admin" ||
        t.role_name?.toLowerCase() === "clientadmin",
    ) || false;

  const isSalesUser =
    auth?.tenants?.some(
      (t: any) =>
        t.role_key?.toUpperCase() === "SALESUSER" ||
        t.role_key?.toLowerCase() === "sales_user" ||
        t.role_name?.toLowerCase() === "sales user",
    ) || false;

  const activeTenant =
    auth?.tenants?.find(
      (t: any) => String(t.tenant_id) === String(selectedTenantId)
    ) || auth?.tenants?.[0];

  const userIsTeamManager =
    Boolean(isTeamManager) || Boolean(activeTenant?.team_manager);

  const canAssignTeamAndUser =
    isClientAdmin || (isSalesUser && userIsTeamManager);

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [activeChannel, setActiveChannel] = useState("sms");
  const [filterTab, setFilterTab] = useState<ConversationFilter>("all");
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState<number | null>(null);
  const [isTransferringChat, setIsTransferringChat] = useState(false);
  const [selectedStatus, setSelectedStatus] =
    useState<ExternalChatEndUserStatusOption | null>(null);
  const [statusOptions, setStatusOptions] = useState<
    ExternalChatEndUserStatusOption[]
  >([]);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);

  const [optionsMenuOpen, setOptionsMenuOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [mobileActivePanel, setMobileActivePanel] = useState<
    "left" | "center" | "right"
  >("left");
  const [teamFilterId, setTeamFilterId] = useState<number | "">("");
  const [assignedUserFilterId, setAssignedUserFilterId] = useState<number | "">("");
  const [accountFilter, setAccountFilter] = useState("");
  const [accountOptions, setAccountOptions] = useState<any[]>([]);
  const [platformFilter, setPlatformFilter] = useState("");
  const [platformOptions, setPlatformOptions] = useState<any[]>([]);
  const [expandedFilterSections, setExpandedFilterSections] = useState<{
    status: boolean;
    team: boolean;
    assignedUser: boolean;
    account: boolean;
    platform: boolean;
  }>({ status: false, team: false, assignedUser: false, account: false, platform: false });
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [debouncedGroupMembersSearch, setDebouncedGroupMembersSearch] = useState("");

  const [conversations, setConversations] = useState<ExternalChatEndUser[]>(
    [],
  );
  const [nextConversationsUrl, setNextConversationsUrl] = useState<string | null>(null);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  const loadingMoreConversationsRef = useRef(false);
  const hasLoadedMoreConversationsRef = useRef(false);
  const [messages, setMessages] = useState<MessageWithUser[]>([]);
  const [selectedConversation, setSelectedConversation] =
    useState<ExternalChatEndUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [nextMessagesUrl, setNextMessagesUrl] = useState<string | null>(null);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [conversationsFirstLoading, setConversationsFirstLoading] = useState(true);
  const hasLoadedConversationsOnce = useRef(false);
  const [isChatbotIntegrated, setIsChatbotIntegrated] = useState(false);
  const [hasAuthorizedExternalBot, setHasAuthorizedExternalBot] = useState(false);
  const [hasAuthorizedInternalBot, setHasAuthorizedInternalBot] = useState(false);
  const [internalBotId, setInternalBotId] = useState<number | null>(null);
  const internalBotIdRef = useRef<number | null>(null);

  const [messageInput, setMessageInput] = useState("");
  const [messageVisibility, setMessageVisibility] = useState<"ALL" | "INTERNAL">(
    (localStorage.getItem("chat_messageVisibility") as "ALL" | "INTERNAL") || "ALL"
  );
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [selectedGroupUserId, setSelectedGroupUserId] = useState<number | "">("");
  const [selectedGroupUserRole, setSelectedGroupUserRole] = useState<string>("Sales Person");
  const [creatingGroupMember, setCreatingGroupMember] = useState(false);
  const [deletingGroupMemberId, setDeletingGroupMemberId] = useState<number | null>(null);

  const handleDeleteGroupMember = async (id: number) => {
    try {
      setDeletingGroupMemberId(id);
      await ChatService.deleteGroupMember(id);
      setGroupMembers(prev => prev.filter(m => m.external_chat_group_member_id !== id));
      MySwal.fire({
        icon: "success",
        title: "Member removed",
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    } catch (err) {
      MySwal.fire({
        icon: "error",
        title: "Error removing member",
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    } finally {
      setDeletingGroupMemberId(null);
    }
  };

  const [sending, setSending] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [gcPendingFiles, setGcPendingFiles] = useState<File[]>([]);
  const [gcPendingFilePreviews, setGcPendingFilePreviews] = useState<{ url: string; name: string; type: string; size: number }[]>([]);
  const [fetchingGroupMembers, setFetchingGroupMembers] = useState(false);

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState<number[]>([]);
  const [hoveredMessageId, setHoveredMessageId] = useState<number | null>(null);
  const [messageMenuOpen, setMessageMenuOpen] = useState<number | null>(null);
  const [reactionPickerMessageId, setReactionPickerMessageId] = useState<number | null>(null);

  const [showMediaPreview, setShowMediaPreview] = useState(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [viewerImageUrl, setViewerImageUrl] = useState("");
  const [imageZoom, setImageZoom] = useState(1);

  const [showContactModal, setShowContactModal] = useState(false);
  const [showAssignedUserModal, setShowAssignedUserModal] = useState(false);
  const [showAssignedTeamModal, setShowAssignedTeamModal] = useState(false);

  const [contacts, setContacts] = useState<any[]>([]);
  const [tenantUsers, setTenantUsers] = useState<any[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<number | null>(
    null,
  );
  const [selectedAssignedUserId, setSelectedAssignedUserId] = useState<
    number | null
  >(null);
  const [selectedAssignedTeamIds, setSelectedAssignedTeamIds] = useState<number[]>([]);
  const [updatingTeams, setUpdatingTeams] = useState(false);
  const [removedTeamIds, setRemovedTeamIds] = useState<number[]>([]);
  const [teams, setTeams] = useState<any[]>([]);

  const [displayedUsers, setDisplayedUsers] = useState<any[]>([]);
  const [loadingAssignableUsers, setLoadingAssignableUsers] = useState(false);
  const assignableUsersRequestRef = useRef(0);

  const [contactNameFilter, setContactNameFilter] = useState("");
  const [contactEmailFilter, setContactEmailFilter] = useState("");
  const [contactCompanyFilter, setContactCompanyFilter] = useState("");
  const [contactCurrentPage, setContactCurrentPage] = useState(1);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [totalContactsCount, setTotalContactsCount] = useState(0);
  const contactPageSize = 10;

  const [showConvertToContactModal, setShowConvertToContactModal] = useState(false);
  const [convertContactName, setConvertContactName] = useState("");
  const [convertContactEmail, setConvertContactEmail] = useState("");
  const [convertContactPhone, setConvertContactPhone] = useState("");
  const [convertAssignedUserId, setConvertAssignedUserId] = useState<number | null>(null);
  const [convertAssignedTeamId, setConvertAssignedTeamId] = useState<number | null>(null);
  const [convertingContact, setConvertingContact] = useState(false);
  const [convertEmailError, setConvertEmailError] = useState("");
  const [convertPhoneError, setConvertPhoneError] = useState("");
  const [creatingContactAndLead, setCreatingContactAndLead] = useState(false);
  const [assigningContactAndLead, setAssigningContactAndLead] = useState(false);

  const [chatTypeFilter, setChatTypeFilter] = useState<string>(
    isContactPage ? "EXTERNAL" : (localStorage.getItem("lastChatTab") || "EXTERNAL")
  );
  const [rightPanelMode, setRightPanelMode] = useState<'DEFAULT' | 'SPACE' | 'SENDER' | 'THREAD'>('DEFAULT');
  const [groupMembers, setGroupMembers] = useState<ExternalChatGroupMember[]>([]);
  const [spaceMemberCount, setSpaceMemberCount] = useState<number>(0);
  const [selectedSender, setSelectedSender] = useState<{
    name: string;
    email: string;
    image: string;
    external_chat_group_member_id?: number;
    external_chat_end_user_id?: number;
  } | null>(null);
  const [editingNameType, setEditingNameType] = useState<"header" | "member" | "sender" | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [tempNameValue, setTempNameValue] = useState("");
  const [groupMembersLoading, setGroupMembersLoading] = useState(false);
  const [groupMembersSearch, setGroupMembersSearch] = useState("");
  const [groupMembersPage, setGroupMembersPage] = useState(1);
  const [hasMoreMembers, setHasMoreMembers] = useState(false);
  const [loadingNextMembers, setLoadingNextMembers] = useState(false);

  const [threadMessages, setThreadMessages] = useState<ThreadMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadParentMessage, setThreadParentMessage] = useState<MessageWithUser | null>(null);
  const [threadInput, setThreadInput] = useState("");
  const [threadPendingFiles, setThreadPendingFiles] = useState<{ file: File; url: string }[]>([]);
  const [threadActivity, setThreadActivity] = useState<Record<number, ThreadActivity>>({});
  const countedThreadReplyIdsRef = useRef<Set<number>>(new Set());
  const [threadSending, setThreadSending] = useState(false);
  const [replyingToMessage, setReplyingToMessage] = useState<MessageWithUser | ThreadMessage | null>(null);
  const [threadReplyingToMessage, setThreadReplyingToMessage] = useState<MessageWithUser | ThreadMessage | null>(null);

  const getMessagePreviewText = (msg: any) => {
    if (!msg) return "";
    let text = "";
    const pktKey = msg.external_chat_packet_type?.external_chat_packet_type_key;
    if (pktKey === "TEXT") {
      text = msg.google_chat_meeting_link ? "Meeting invite" : msg.google_chat_card ? "Google Chat Card" : (msg.external_chat_message_value || "");
    } else if (pktKey === "STICKER") {
      text = "Sticker";
    } else if (pktKey === "GOOGLE_CHAT_CARD") {
      text = "Google Chat Card";
    } else if (["FILE", "FILE_RECEIVED", "APPLICATION", "DOCUMENT"].includes(pktKey)) {
      text = "File";
    } else if (pktKey === "GOOGLE_CHAT_FILE") {
      text = msg.external_chat_message_value || "Attachment";
    } else {
      text = pktKey || "Attachment";
    }
    if (!text && msg.attachments?.length) {
      text = "Attachment";
    }
    return text.length > 25 ? text.substring(0, 25) + "..." : text;
  };



  const getAvatarColor = (name: string) => {
    const colors = [
      '#824EEB', '#00C292', '#FB9678', '#01C0C8', '#AB8CE4', '#00c292', '#03a9f3', '#fb9678', '#e46a76'
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const getAgentOrBotName = (message: any, fallback: string = "Agent") => {
    if (message?.sender) {
      return [message.sender.first_name, message.sender.last_name].filter(Boolean).join(" ");
    }
    if (message?.raw_message) {
      try {
        const raw = JSON.parse(message.raw_message);
        if (raw.sender === "bot" || raw.sender === "Bot") {
          return "Bot";
        }
      } catch (e) {
        // ignore
      }
    }
    return fallback;
  };

  const getEngatiTenantBotParams = (conversation: ExternalChatEndUser): EngatiTenantBotParams => ({
    tenant_id: conversation.tenant?.tenant_id ?? (selectedTenantId ? Number(selectedTenantId) : undefined),
    external_chat_tenant_bot_id: conversation.external_chat_tenant_bot?.external_chat_tenant_bot_id,
  });

  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [chatTemplates, setChatTemplates] = useState<{ chat_template_message_id: number; title: string; message: string; short_code: string }[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<{ chat_template_message_id: number; title: string; message: string; short_code: string }[]>([]);

  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [newChatEmail, setNewChatEmail] = useState("");
  const [newChatText, setNewChatText] = useState("");
  const [newChatFile, setNewChatFile] = useState<File | null>(null);
  const [newChatSending, setNewChatSending] = useState(false);
  const [newChatError, setNewChatError] = useState("");
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [isNewChatMemberSelected, setIsNewChatMemberSelected] = useState(false);

  const isValidEmail = (email: string): boolean => {
    if (!email.trim()) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPhone = (phone: string): boolean => {
    if (!phone.trim()) return true;
    const digitsOnly = phone.replace(/\D/g, "");
    if (digitsOnly.length < 7 || digitsOnly.length > 15) return false;
    const phoneRegex = /^(\+)?[\d\-\s\(\)]+$/;
    return phoneRegex.test(phone);
  };

  const filterPhoneInput = (value: string): string => {
    let filtered = value.replace(/[^\d\s\+\(\)\-]/g, "");
    const digits = filtered.replace(/\D/g, "");
    if (digits.length <= 15) return filtered;
    let digitCount = 0;
    let result = "";
    for (const ch of filtered) {
      if (/\d/.test(ch)) {
        if (digitCount >= 15) continue;
        digitCount++;
      }
      result += ch;
    }
    return result;
  };

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const gcPendingFilePreviewsRef = useRef<string[]>([]);
  const previewUrlRef = useRef<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const threadInputRef = useRef<HTMLInputElement>(null);
  const threadFileInputRef = useRef<HTMLInputElement>(null);
  const threadOsRef = useRef<any>(null);
  const threadBodyRef = useRef<HTMLDivElement>(null);
  const threadStickToBottomRef = useRef(true);
  const isThreadPanelVisibleRef = useRef(false);
  const previousThreadMessageCountRef = useRef(0);
  const selectedConversationRef = useRef<ExternalChatEndUser | null>(null);
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const optionsMenuRef = useRef<HTMLLIElement>(null);
  const statusDropdownRef = useRef<HTMLLIElement>(null);

  const osRef = useRef<any>(null);
  const sidebarLeftRef = useRef<HTMLDivElement>(null);
  const memberListStateRef = useRef<any>(null);
  const messageMenuRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const fetchConversationsRef = useRef<() => Promise<void>>();
  const fetchMessagesRef = useRef<(conv: ExternalChatEndUser, showLoading?: boolean, nextUrl?: string | null, preventScroll?: boolean) => Promise<void>>();

  const conversationsRequestVersionRef = useRef(0);
  const messagesRequestVersionRef = useRef(0);
  const previousMessagesLengthRef = useRef(0);
  const threadParentMessageRef = useRef<MessageWithUser | null>(null);
  const fetchThreadMessagesRef = useRef<(parentId: number) => Promise<void>>();
  const pendingThreadRootMessageIdRef = useRef<number | null>(null);
  const isLazyLoadingRef = useRef(false);
  const isInitialScrollRef = useRef(true);


  const toggleDetailsPanel = () => {
    setIsDetailsOpen(!isDetailsOpen);
    if (!isDetailsOpen) {
      setMobileActivePanel("right");
    }
    if (isDetailsOpen) {
      setRightPanelMode("DEFAULT");
    }
  };

  const handleHeaderClick = async () => {
    if (!selectedConversation) return;

    const platformKey = getConversationPlatformKey(selectedConversation);
    const isGoogleChat = platformKey === "GOOGLE_CHAT";
    const chatType = selectedConversation.external_chat_bot_channel_account?.chat_type;

    if (isGoogleChat) {
      // Toggle if already open in the correct mode
      const targetMode = chatType === "SPACE" ? "SPACE" : "SENDER";
      if (isDetailsOpen && rightPanelMode === targetMode) {
        setIsDetailsOpen(false);
        return;
      }

      if (chatType === "SPACE") {
        setRightPanelMode("SPACE");
        setIsDetailsOpen(true);
        setMobileActivePanel("right");
        setGroupMembersLoading(true);
        if (selectedConversation.external_chat_bot_channel_account?.external_chat_bot_channel_account_id) {
          fetchGroupMembers(selectedConversation.external_chat_bot_channel_account.external_chat_bot_channel_account_id, debouncedGroupMembersSearch, 1, false);
        }
      } else {
        // DIRECT_MESSAGE
        const userMessage = messages.find(m => m.external_chat_platform_packet_type?.external_chat_platform_packet_type_key === "USER_MESSAGE");
        const groupMemberId = userMessage?.external_chat_group_member_id;

        setSelectedSender({
          name: selectedConversation.username || `${selectedConversation.first_name} ${selectedConversation.last_name}`,
          email: selectedConversation.end_user_email || "No Email",
          image: selectedConversation.end_user_profile_image_url || dummy,
          external_chat_end_user_id: selectedConversation.external_chat_end_user_id,
          external_chat_group_member_id: groupMemberId || undefined
        });
        setRightPanelMode("SENDER");
        setIsDetailsOpen(true);
        setMobileActivePanel("right");
      }
    } else {
      toggleDetailsPanel();
    }
  };

  const handleSenderClick = (senderInfo: {
    name: string;
    email: string;
    image: string;
    external_chat_group_member_id?: number;
    external_chat_end_user_id?: number;
  }) => {
    let finalSenderInfo = { ...senderInfo };

    if (!finalSenderInfo.external_chat_group_member_id && !finalSenderInfo.external_chat_end_user_id) {
      if (selectedConversation?.external_chat_bot_channel_account?.chat_type === "SPACE") {
        const member = groupMembers.find(m => m.member_email === senderInfo.email);
        if (member) {
          finalSenderInfo.external_chat_group_member_id = member.external_chat_group_member_id;
        }
      } else if (selectedConversation?.external_chat_bot_channel_account?.chat_type === "DIRECT_MESSAGE") {
        if (senderInfo.email === selectedConversation.end_user_email || senderInfo.name === (selectedConversation.username || `${selectedConversation.first_name} ${selectedConversation.last_name}`)) {
          finalSenderInfo.external_chat_end_user_id = selectedConversation.external_chat_end_user_id;
          if (!finalSenderInfo.external_chat_group_member_id) {
            const userMessage = messages.find(m => m.external_chat_platform_packet_type?.external_chat_platform_packet_type_key === "USER_MESSAGE");
            finalSenderInfo.external_chat_group_member_id = userMessage?.external_chat_group_member_id || undefined;
          }
        }
      }
    }

    setSelectedSender(finalSenderInfo);
    setRightPanelMode("SENDER");
    setIsDetailsOpen(true);
    setMobileActivePanel("right");
  };

  const handleNameSave = async (type: "header" | "member" | "sender", id?: number, value?: string) => {
    const finalValue = value?.trim();
    if (!finalValue) {
      setEditingNameType(null);
      setEditingId(null);
      return;
    }

    try {
      let targetId: number | undefined;
      let currentName: string | undefined;
      let updateType: "end_user" | "group_member" | null = null;

      if (type === "header") {
        targetId = selectedConversation?.external_chat_end_user_id;
        currentName = selectedConversation?.username;
        updateType = "end_user";
      } else if (type === "member" || type === "sender") {
        updateType = "group_member";
        if (type === "member") {
          targetId = id;
          currentName = groupMembers.find(m => m.external_chat_group_member_id === targetId)?.member_name;
        } else {
          targetId = selectedSender?.external_chat_group_member_id;
          currentName = selectedSender?.name;
        }
      }

      if (!targetId || !updateType) {
        return;
      }

      if (finalValue === currentName) {
        return;
      }

      if (updateType === "end_user") {
        const response = await ChatService.updateEndUser(targetId, {
          username: finalValue,
          tenant_id: Number(selectedTenantId)
        });

        if (chatTypeFilter === "INTERNAL") {
          const externalChatBotChannelAccountId = response?.external_chat_bot_channel_account?.external_chat_bot_channel_account_id;
          if (externalChatBotChannelAccountId) {
            await ChatService.updateBotChannelAccount(externalChatBotChannelAccountId, {
              account_name: finalValue,
              tenant_id: Number(selectedTenantId)
            });
          }
        }

        if (selectedConversation && selectedConversation.external_chat_end_user_id === targetId) {
          const updatedConversation = { ...selectedConversation, username: finalValue };
          if (chatTypeFilter === "INTERNAL" && updatedConversation.external_chat_bot_channel_account) {
            updatedConversation.external_chat_bot_channel_account = {
              ...updatedConversation.external_chat_bot_channel_account,
              account_name: finalValue
            };
          }
          setSelectedConversation(updatedConversation);
        }
        if (selectedSender && selectedSender.external_chat_end_user_id === targetId) {
          setSelectedSender({ ...selectedSender, name: finalValue });
        }
        fetchConversations();
      } else {
        await ChatService.updateGroupMember(targetId, {
          member_name: finalValue,
          tenant_id: Number(selectedTenantId)
        });

        setGroupMembers(prev => prev.map(m => m.external_chat_group_member_id === targetId ? { ...m, member_name: finalValue } : m));
        if (selectedSender && (selectedSender.external_chat_group_member_id === targetId || selectedSender.external_chat_end_user_id === targetId)) {
          setSelectedSender({ ...selectedSender, name: finalValue });
        }

        if (selectedConversation && selectedConversation.external_chat_bot_channel_account?.chat_type === "DIRECT_MESSAGE" && (selectedConversation.external_chat_end_user_id === targetId || (selectedSender && selectedSender.email === selectedConversation.end_user_email))) {
          setSelectedConversation({ ...selectedConversation, username: finalValue });
        }

        fetchConversations();
      }
    } catch (err) {
      MySwal.fire({
        icon: "error",
        title: "Update Failed",
        text: "Could not update the name. Please try again.",
      });
    } finally {
      setEditingNameType(null);
      setEditingId(null);
    }
  };

  const handleMobileBackToConversations = () => {
    setMobileActivePanel("left");
    setSelectedConversation(null);
  };

  const closeRightPanel = () => {
    setIsDetailsOpen(false);
    setMobileActivePanel("center");
  };

  const handleMobileBackToMessages = () => {
    closeRightPanel();
  };



  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setActiveConversationId(selectedConversation?.external_chat_end_user_id || null);
  }, [selectedConversation, setActiveConversationId]);

  useEffect(() => {
    localStorage.setItem("lastChatTab", chatTypeFilter);
  }, [chatTypeFilter]);

  const chatConversationsRef = useRef<ExternalChatEndUser[]>([]);
  useEffect(() => {
    chatConversationsRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    if (chatTypeFilter !== "INTERNAL" || !userId || !selectedTenantId) return;
    const intervalId = setInterval(() => {
      const currentConvs = chatConversationsRef.current || [];
      const unreadConvs = currentConvs.filter((c: any) => c.unread_count && c.unread_count > 0);
      if (unreadConvs.length === 0) return;
      const botId = unreadConvs[0]?.external_chat_tenant_bot?.external_chat_tenant_bot_id;
      if (!botId) return;
      const accountIds = Array.from(new Set(unreadConvs.map((c: any) => c.external_chat_bot_channel_account?.external_chat_bot_channel_account_id).filter(Boolean)));
      if (accountIds.length > 0) {
        ChatService.syncReadStatusBatch(Number(userId), botId, accountIds as number[])
          .then(async () => {
            if (fetchConversationsRef.current) {
              await fetchConversationsRef.current();
            }
            refetchUnreadCount();
          })
          .catch(() => { });
      }
    }, 60000);
    return () => clearInterval(intervalId);
  }, [chatTypeFilter, userId, selectedTenantId, refetchUnreadCount]);

  useEffect(() => {
    const botId = selectedConversation?.external_chat_tenant_bot?.external_chat_tenant_bot_id;
    const accountId = selectedConversation?.external_chat_bot_channel_account?.external_chat_bot_channel_account_id;
    const currentUserId = userId !== undefined && userId !== null ? Number(userId) : null;

    if (chatTypeFilter !== "INTERNAL" || !botId || !accountId || !currentUserId) return;

    const intervalId = setInterval(() => {
      ChatService.markSingleRead(currentUserId, botId, accountId).catch(() => { });
    }, 30000);

    return () => {
      clearInterval(intervalId);
      ChatService.markSingleRead(currentUserId, botId, accountId).catch(() => { });
    };
  }, [selectedConversation?.external_chat_end_user_id, chatTypeFilter, userId]);

  useEffect(() => {
    if (!newChatEmail.trim() || isNewChatMemberSelected) {
      if (!isNewChatMemberSelected) {
        setFilteredUsers([]);
        setShowUserDropdown(false);
      }
      return;
    }

    const timer = setTimeout(async () => {
      setSearchingUsers(true);
      try {
        const res = await ChatService.getExternalChatGroupMembersByEmail(newChatEmail.trim());
        const seen = new Set<string>();
        const unique = (res.results || []).filter((u: any) => {
          if (seen.has(u.member_email)) return false;
          seen.add(u.member_email);
          return true;
        });
        setFilteredUsers(unique);
        setShowUserDropdown(true);
      } catch (error) {
        console.error(error);
      } finally {
        setSearchingUsers(false);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [newChatEmail, isNewChatMemberSelected]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedGroupMembersSearch(groupMembersSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [groupMembersSearch]);

  const fetchGroupMembers = useCallback(async (botChannelAccountId: number, search?: string, page: number = 1, append: boolean = false) => {
    if (page === 1) {
      setGroupMembersLoading(true);
    } else {
      setLoadingNextMembers(true);
    }
    try {
      const res = await ChatService.getExternalChatGroupMembers(botChannelAccountId, search, page);
      if (append) {
        setGroupMembers(prev => {
          const newMembers = res.results || [];
          const existingIds = new Set(prev.map(m => m.external_chat_group_member_id));
          const uniqueNewMembers = newMembers.filter((m: any) => !existingIds.has(m.external_chat_group_member_id));
          return [...prev, ...uniqueNewMembers];
        });
      } else {
        setGroupMembers(res.results || []);
      }
      setSpaceMemberCount(res.count || 0);
      setHasMoreMembers(!!res.next);
      setGroupMembersPage(page);
    } catch (err) {
      console.error(err);
    } finally {
      setGroupMembersLoading(false);
      setLoadingNextMembers(false);
    }
  }, []);

  useEffect(() => {
    if (rightPanelMode === "SPACE" && selectedConversation?.external_chat_bot_channel_account?.external_chat_bot_channel_account_id) {
      fetchGroupMembers(selectedConversation.external_chat_bot_channel_account.external_chat_bot_channel_account_id, debouncedGroupMembersSearch, 1, false);
    }
  }, [debouncedGroupMembersSearch, rightPanelMode, selectedConversation?.external_chat_bot_channel_account?.external_chat_bot_channel_account_id, fetchGroupMembers]);

  useEffect(() => {
    if (selectedConversation?.is_group && selectedConversation?.external_chat_end_user_id && selectedTenantId) {
      const fetchConversationMembers = async () => {
        setGroupMembersLoading(true);
        setFetchingGroupMembers(true);
        try {
          const res = await ChatService.getExternalChatGroupMembersByEndUserId(selectedConversation.external_chat_end_user_id, Number(selectedTenantId));
          setGroupMembers(res.results || (Array.isArray(res) ? res : []));
        } catch (err) {
          console.error("Failed to fetch conversation group members:", err);
        } finally {
          setGroupMembersLoading(false);
          setFetchingGroupMembers(false);
        }
      };
      fetchConversationMembers();
    }
  }, [selectedConversation?.is_group, selectedConversation?.external_chat_end_user_id, selectedTenantId]);

  useEffect(() => {
    memberListStateRef.current = {
      hasMoreMembers,
      loadingNextMembers,
      groupMembersPage,
      debouncedGroupMembersSearch,
      selectedConversation,
      rightPanelMode
    };
  }, [hasMoreMembers, loadingNextMembers, groupMembersPage, debouncedGroupMembersSearch, selectedConversation, rightPanelMode]);

  const getInternalBotId = useCallback(async (): Promise<number | null> => {
    if (internalBotIdRef.current) return internalBotIdRef.current;
    if (!selectedTenantId) return null;
    try {
      const response = await ChatService.getExternalChatTenantBot(Number(selectedTenantId));
      const results: any[] = response?.results || [];
      const internalBot = results.find(
        (bot: any) => bot.is_authorized === true && bot.chat_type?.chat_type_key === "INTERNAL"
      );
      if (internalBot?.external_chat_tenant_bot_id) {
        internalBotIdRef.current = internalBot.external_chat_tenant_bot_id;
        setInternalBotId(internalBot.external_chat_tenant_bot_id);
        return internalBot.external_chat_tenant_bot_id;
      }
    } catch (_) { }
    return null;
  }, [selectedTenantId]);

  const fetchConversations = useCallback(async (reset: boolean = false) => {
    if (!selectedTenantId) {
      return;
    }

    const currentVersion = ++conversationsRequestVersionRef.current;
    setError(null);

    if (reset) {
      hasLoadedMoreConversationsRef.current = false;
      setNextConversationsUrl(null);
    }

    const loadedCount = chatConversationsRef.current.length;
    const refreshLimit = hasLoadedMoreConversationsRef.current && loadedCount > 0 ? loadedCount : undefined;

    if (!hasLoadedConversationsOnce.current) {
      setConversationsFirstLoading(true);
    }

    try {
      const currentTenant = auth?.tenants?.find(
        (t: any) => String(t.tenant_id) === String(selectedTenantId)
      );
      const currentUserRole = currentTenant?.role_key;

      let activeInternalBotId: number | undefined = undefined;
      if (chatTypeFilter === "INTERNAL") {
        const botId = await getInternalBotId();
        if (botId) {
          activeInternalBotId = botId;
        }
      }

      const response = await ChatService.getConversations(
        Number(selectedTenantId),
        userId,
        {
          search: debouncedSearchTerm,
          end_user_statuses:
            selectedFilter.length > 0 ? selectedFilter.join(",") : undefined,
          assigned_user_id: assignedUserFilterId ? Number(assignedUserFilterId) : undefined,
          current_user_role: currentUserRole,
          assigned_team_id: teamFilterId ? Number(teamFilterId) : undefined,
          external_chat_bot_channel_account_id: accountFilter || undefined,
          contact_id: contact_id || undefined,
          external_chat_platform_id: platformFilter || undefined,
          chat_type_key: chatTypeFilter,
          limit: refreshLimit,
          page_size: 12,
          external_chat_tenant_bot_id: activeInternalBotId,
        },
      );

      const sortedConversations = response.results.sort(compareConversationsByLastMessage);

      if (currentVersion !== conversationsRequestVersionRef.current) return;

      setConversations(sortedConversations);
      setNextConversationsUrl(sanitizeNextUrl(response.next));

      if (selectedConversationRef.current) {
        const updatedSelectedConversation = sortedConversations.find(
          (conv) => conv.external_chat_end_user_id === selectedConversationRef.current?.external_chat_end_user_id
        );
        if (updatedSelectedConversation) {
          setSelectedConversation(updatedSelectedConversation);
          selectedConversationRef.current = updatedSelectedConversation;
        }
      }
    } catch (err) {
      if (currentVersion === conversationsRequestVersionRef.current) {
        setError("Failed to load conversations");
      }
    } finally {
      if (currentVersion === conversationsRequestVersionRef.current) {
        if (!hasLoadedConversationsOnce.current) {
          hasLoadedConversationsOnce.current = true;
          setConversationsFirstLoading(false);
        }
      }
    }
  }, [selectedTenantId, debouncedSearchTerm, selectedFilter, auth, userId, teamFilterId, accountFilter, assignedUserFilterId, contact_id, platformFilter, chatTypeFilter, getInternalBotId]);

  const loadMoreConversations = async () => {
    if (!nextConversationsUrl || loadingMoreConversationsRef.current || !selectedTenantId) return;

    const requestVersion = conversationsRequestVersionRef.current;
    loadingMoreConversationsRef.current = true;
    setLoadingMoreConversations(true);

    try {
      const sanitizedUrl = sanitizeNextUrl(nextConversationsUrl);
      const response = await ChatService.getConversations(Number(selectedTenantId), userId, undefined, sanitizedUrl);
      if (requestVersion !== conversationsRequestVersionRef.current) return;

      const nextPage = response.results.sort(compareConversationsByLastMessage);
      setConversations((prev) => {
        const loadedIds = new Set(prev.map((c) => c.external_chat_end_user_id));
        return [...prev, ...nextPage.filter((c) => !loadedIds.has(c.external_chat_end_user_id))];
      });
      setNextConversationsUrl(sanitizeNextUrl(response.next));
      hasLoadedMoreConversationsRef.current = true;
    } catch (err) {
      console.error("Error fetching more conversations:", err);
    } finally {
      loadingMoreConversationsRef.current = false;
      setLoadingMoreConversations(false);
    }
  };

  useEffect(() => {
    hasLoadedConversationsOnce.current = false;
    setConversationsFirstLoading(true);
    setConversations([]);
    setNextConversationsUrl(null);
    setSelectedConversation(null);
    selectedConversationRef.current = null;
    setMessages([]);
    setAccountFilter("");
    setPlatformFilter("");
  }, [chatTypeFilter]);

  const filteredConversations = useMemo(() => {
    let filtered = conversations.filter(
      (conv) => conv.external_chat_tenant_bot?.is_authorized !== false
    );

    switch (filterTab) {
      case "unread":
        filtered = filtered.filter(
          (conv) => conv.unread_count && conv.unread_count > 0,
        );
        break;
      case "recents":
        filtered = filtered.slice().sort((a, b) => {
          const dateA = a.last_message?.created_datetime
            ? new Date(a.last_message.created_datetime).getTime()
            : 0;
          const dateB = b.last_message?.created_datetime
            ? new Date(b.last_message.created_datetime).getTime()
            : 0;
          return dateB - dateA;
        });
        break;
      case "starred":
      case "all":
      default:
        break;
    }

    return filtered;
  }, [conversations, filterTab]);

  const visibleMessages = useMemo(
    () => messages.filter((m) => !m.root_message_id && !isHiddenChatMessage(m)),
    [messages],
  );

  const fetchMessages = useCallback(
    async (conversation: ExternalChatEndUser, showLoading?: boolean, nextUrl?: string | null, preventScroll?: boolean) => {
      const currentVersion = ++messagesRequestVersionRef.current;
      if (showLoading) {
        setMessagesLoading(true);
      }
      if (nextUrl) {
        setLoadingOlderMessages(true);
        isLazyLoadingRef.current = true;
      }


      try {
        const botId = chatTypeFilter === "INTERNAL"
          ? (conversation.external_chat_tenant_bot?.external_chat_tenant_bot_id || internalBotIdRef.current || undefined)
          : undefined;
        const response = await ChatService.getMessages(
          conversation.external_chat_end_user_id,
          sanitizeNextUrl(nextUrl),
          botId
        );

        const newMessages = response.results as MessageWithUser[];
        const sorted = newMessages.sort(compareMessagesNewestFirst);

        if (currentVersion !== messagesRequestVersionRef.current) return;

        let scrollElement: HTMLElement | null = null;
        let oldScrollHeight = 0;
        let oldScrollTop = 0;

        if (nextUrl) {
          try {
            const osInstance = osRef.current?.osInstance();
            scrollElement = osInstance?.elements().scrollOffsetElement;
            oldScrollHeight = scrollElement?.scrollHeight || 0;
            oldScrollTop = scrollElement?.scrollTop || 0;
          } catch (e) { }
          setMessages((prev) => [...prev, ...sorted]);
        } else {
          setMessages(prev => {
            const usedLocalIds = new Set<string>();
            return sorted.map(newMsg => {
              const matchedOptimistic = prev.find(p => {
                if (!p._localId || usedLocalIds.has(p._localId)) return false;
                if (p.external_chat_message_id === newMsg.external_chat_message_id) return true;
                if (p.external_chat_message_id < 0 && p.external_chat_message_value === newMsg.external_chat_message_value) {
                  // For media messages, ensure attachments count matches
                  if (p.attachments?.length || newMsg.attachments?.length) {
                    return p.attachments?.length === newMsg.attachments?.length;
                  }
                  return true;
                }
                return false;
              });

              if (matchedOptimistic && matchedOptimistic._localId) {
                usedLocalIds.add(matchedOptimistic._localId);
                return { ...newMsg, _localId: matchedOptimistic._localId };
              }
              return newMsg;
            });
          });
        }
        setNextMessagesUrl(sanitizeNextUrl(response.next));

        if (nextUrl && scrollElement) {
          setTimeout(() => {
            if (scrollElement) {
              const newScrollHeight = scrollElement.scrollHeight;
              scrollElement.scrollTop = oldScrollTop + (newScrollHeight - oldScrollHeight);
            }
          }, 50);
        }

        const pendingThreadId = pendingThreadRootMessageIdRef.current;
        if (pendingThreadId && !nextUrl) {
          pendingThreadRootMessageIdRef.current = null;
          const parentMsg = sorted.find(
            (m) => m.external_chat_message_id === pendingThreadId
          );
          if (parentMsg) {
            setTimeout(() => {
              setThreadParentMessage(parentMsg);
              setThreadMessages([]);
              setThreadInput("");
              setRightPanelMode("THREAD");
              setIsDetailsOpen(true);
              setMobileActivePanel("right");
              if (fetchThreadMessagesRef.current) {
                fetchThreadMessagesRef.current(parentMsg.external_chat_message_id);
              }
            }, 100);
          }
        } else if (!nextUrl && !preventScroll) {
          setTimeout(() => {
            scrollToBottom();
          }, 150);
        }
      } catch (err) {
        console.error("Error fetching messages:", err);
      } finally {
        if (showLoading) {
          setMessagesLoading(false);
        }
        if (nextUrl) {
          setLoadingOlderMessages(false);
        }
      }
    },
    [],
  );

  fetchConversationsRef.current = fetchConversations;
  fetchMessagesRef.current = fetchMessages;

  const selectConversation = async (conversation: ExternalChatEndUser) => {
    const isNewConversation = selectedConversationRef.current?.external_chat_end_user_id !== conversation.external_chat_end_user_id;
    if (isNewConversation) {
      setNextMessagesUrl(null);
      setMessages([]);
      setThreadActivity({});
      countedThreadReplyIdsRef.current = new Set();
    }
    setSelectedConversation(conversation);
    selectedConversationRef.current = conversation;
    isInitialScrollRef.current = true;
    fetchMessages(conversation, isNewConversation);

    setMobileActivePanel("center");

    // Set initial right panel mode for Google Chat
    const platformKey = getConversationPlatformKey(conversation);
    const isGoogleChat = platformKey === "GOOGLE_CHAT";
    const chatType = conversation.external_chat_bot_channel_account?.chat_type;

    if (isGoogleChat) {
      if (chatType === "SPACE") {
        setRightPanelMode("SPACE");
        setGroupMembers([]);
        setGroupMembersSearch("");
        setGroupMembersPage(1);
        // We will fetch members when the panel actually opens or if it is already open
        if (isDetailsOpen) {
          if (conversation.external_chat_bot_channel_account?.external_chat_bot_channel_account_id) {
            fetchGroupMembers(conversation.external_chat_bot_channel_account.external_chat_bot_channel_account_id, debouncedGroupMembersSearch, 1, false);
          }
        }
      } else {
        setRightPanelMode("SENDER");
        setSelectedSender({
          name: conversation.username || `${conversation.first_name} ${conversation.last_name}`,
          email: conversation.end_user_email || "No Email",
          image: conversation.end_user_profile_image_url || dummy,
          external_chat_end_user_id: conversation.external_chat_end_user_id
        });
      }
    } else {
      setRightPanelMode("DEFAULT");
      setGroupMembers([]);
      setSelectedSender(null);
    }

    if (
      conversation.external_chat_end_user_status
        ?.external_chat_end_user_status_id
    ) {
      const currentStatus = statusOptions.find(
        (opt) =>
          opt.external_chat_end_user_status_id ===
          conversation.external_chat_end_user_status
            .external_chat_end_user_status_id,
      ) || (conversation.external_chat_end_user_status as any);
      setSelectedStatus(currentStatus || null);
    } else {
      setSelectedStatus(null);
    }

    try {
      if (selectedTenantId) {
        await ChatService.markMessagesAsRead(
          conversation.external_chat_end_user_id,
          Number(selectedTenantId),
        );

        fetchConversations();
        refetchUnreadCount();
      }
    } catch (err) { }
  };
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    chatInputRef.current?.focus();

    if (!selectedConversation || sending) return;

    const platformKey = getConversationPlatformKey(selectedConversation);
    const isGoogleChat = platformKey === "GOOGLE_CHAT";

    const hasText = messageInput.trim().length > 0;
    const hasFiles = isGoogleChat && gcPendingFiles.length > 0;

    if (!hasText && !hasFiles) return;

    const localId = `local-${Date.now()}-${Math.random()}`;
    const messageText = messageInput.trim();
    const googleChatFiles = isGoogleChat ? [...gcPendingFiles] : [];

    const optimisticAttachments = googleChatFiles.length > 0
      ? googleChatFiles.map((file, idx) => ({
        attachment_id: -1 - idx,
        title: file.name,
        size_in_kb: file.size / 1024,
        attachment_url: gcPendingFilePreviews[idx].url,
        extension: file.name.split(".").pop() || ""
      }))
      : undefined;

    const parent_message_id = (replyingToMessage as any)?.external_chat_message_id;
    const root_message_id = (replyingToMessage as any)?.root_message_id;

    const optimisticMessage: MessageWithUser = {
      external_chat_message_id: -Date.now(),
      external_chat_message_value: messageText,
      created_datetime: new Date().toISOString(),
      external_chat_message_datetime: new Date().toISOString(),
      is_read: null,
      external_chat_channel: (selectedConversation.external_chat_channel?.external_chat_channel_id || selectedConversation.external_chat_channel_id)
        ? {
          external_chat_channel_id: selectedConversation.external_chat_channel?.external_chat_channel_id || selectedConversation.external_chat_channel_id || 0,
          external_chat_channel_key: selectedConversation.external_chat_channel?.external_chat_channel_key || selectedConversation.external_chat_channel_key || "",
          external_chat_channel_display_name: selectedConversation.external_chat_channel?.external_chat_channel_display_name || ""
        }
        : {
          external_chat_channel_id: 0,
          external_chat_channel_key: selectedConversation.external_chat_channel?.external_chat_channel_key || selectedConversation.external_chat_channel_key || "",
          external_chat_channel_display_name: selectedConversation.external_chat_channel?.external_chat_channel_display_name || ""
        },
      external_chat_platform_packet_type: {
        external_chat_platform_packet_type_id: 2,
        external_chat_platform_packet_type_key: "AGENT_MESSAGE",
        external_chat_platform_packet_type_display_name: "Agent Message",
      },
      external_chat_packet_type: {
        external_chat_packet_type_id: 1,
        external_chat_packet_type_key: optimisticAttachments ? "GOOGLE_CHAT_FILE" : "TEXT",
        external_chat_packet_type_display_name: optimisticAttachments ? "File" : "Text",
      },
      tenant: selectedConversation.tenant,
      external_chat_end_user: selectedConversation,
      sender: userId
        ? {
          sender_id: Number(userId),
          first_name: auth?.first_name || auth?.full_name,
          last_name: auth?.last_name || null,
          avatar_url: auth?.avatar_url || null,
          user_role: groupMembers.find(m => m.user_id === Number(userId) || m.user?.user_id === Number(userId) || (m.member_email && auth?.email && m.member_email === auth?.email))?.user_role || auth?.tenants?.find((t: any) => String(t.tenant_id) === String(selectedTenantId))?.role_name || auth?.tenants?.find((t: any) => String(t.tenant_id) === String(selectedTenantId))?.role_key
        }
        : undefined,
      _status: "pending",
      _localId: localId,
      attachments: optimisticAttachments,
      parent_message_id,
      root_message_id
    };

    setMessages((prev) => [optimisticMessage, ...prev]);
    setMessageInput("");
    setReplyingToMessage(null);
    if (isGoogleChat) {
      setGcPendingFiles([]);
      setGcPendingFilePreviews([]);
    }
    setTimeout(() => {
      if (chatInputRef.current) {
        chatInputRef.current.style.height = "auto";
        chatInputRef.current.focus();
      }
    }, 0);

    setSending(true);

    try {
      const endUserId = selectedConversation.external_chat_end_user_id;
      const currentUserId = userId ? Number(userId) : null;

      if (isGoogleChat) {
        if (currentUserId) {
          if (googleChatFiles.length > 0) {
            await ChatService.sendGoogleChatMediaMessage(currentUserId, endUserId, googleChatFiles, messageText || undefined, parent_message_id, root_message_id);
          } else {
            await ChatService.sendGoogleChatTextMessage(currentUserId, endUserId, messageText, parent_message_id, root_message_id);
          }
        }
      } else {
        const assignedUserId = selectedConversation.assigned_user?.user_id;
        const autoAssignOnReply = selectedConversation.tenant?.auto_assign_chat_on_reply;

        if (!assignedUserId && currentUserId) {
          const response = await ChatService.sendNewLeadMessage(
            selectedConversation.channel_user_id,
            messageText,
            currentUserId,
            platformKey,
            endUserId,
            parent_message_id,
            root_message_id,
            getEngatiTenantBotParams(selectedConversation)
          );
          if (response.success && response.message === "Message sent successfully") {
            MySwal.fire({
              toast: true,
              position: "bottom-end",
              icon: "success",
              title: "Chat Assigned",
              showConfirmButton: false,
              timer: 3000,
              text: "You have been assigned to this conversation.",
              timerProgressBar: true,
            });
          }
        } else if (assignedUserId === currentUserId) {
          await ChatService.sendTextMessage(
            selectedConversation.channel_user_id,
            messageText,
            currentUserId ?? undefined,
            platformKey,
            endUserId,
            false,
            parent_message_id,
            root_message_id,
            messageVisibility,
            getEngatiTenantBotParams(selectedConversation)
          );
        } else if (assignedUserId !== currentUserId) {
          if (autoAssignOnReply && currentUserId) {
            const response = await ChatService.sendNewLeadMessage(
              selectedConversation.channel_user_id,
              messageText,
              currentUserId,
              platformKey,
              endUserId,
              parent_message_id,
              root_message_id,
              getEngatiTenantBotParams(selectedConversation)
            );
            if (response.success && response.message === "Message sent successfully") {
              MySwal.fire({
                toast: true,
                position: "bottom-end",
                icon: "success",
                title: "Chat Assigned",
                showConfirmButton: false,
                timer: 3000,
                text: "You have been assigned to this conversation.",
                timerProgressBar: true,
              });
            }
          } else {
            await ChatService.sendTextMessage(
              selectedConversation.channel_user_id,
              messageText,
              currentUserId ?? undefined,
              platformKey,
              endUserId,
              false,
              parent_message_id,
              root_message_id,
              messageVisibility,
              getEngatiTenantBotParams(selectedConversation)
            );
          }
        }
      }

      if (selectedConversation.unread_count && selectedConversation.unread_count > 0 && selectedTenantId && !isGoogleChat) {
        await ChatService.markMessagesAsRead(
          selectedConversation.external_chat_end_user_id,
          Number(selectedTenantId),
        );
      }

      setMessages((prev) =>
        prev.map((m) =>
          m._localId === localId ? { ...m, _status: "sent" as MessageStatus } : m
        )
      );

      await fetchMessages(selectedConversation);
      fetchConversations();
    } catch (err: any) {
      setMessages((prev) =>
        prev.map((m) =>
          m._localId === localId ? { ...m, _status: "error" as MessageStatus } : m
        )
      );
      const platformKey = getConversationPlatformKey(selectedConversation);
      const isAuthError = handleAuthError(err, platformKey, routerNavigate);
      if (!isAuthError) {
        MySwal.fire({
          icon: "error",
          title: "Failed to Send",
          text: "Could not send your message. Please try again.",
          timer: 3000,
        });
      }
    } finally {
      setSending(false);
    }
  };

  const closeNewChatModal = () => {
    setShowNewChatModal(false);
    setNewChatEmail("");
    setNewChatText("");
    setNewChatFile(null);
    setNewChatError("");
    setFilteredUsers([]);
    setShowUserDropdown(false);
    setIsNewChatMemberSelected(false);
  };

  const handleNewChatSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newChatEmail.trim() || newChatSending) return;

    setNewChatSending(true);
    try {
      let botId = internalBotIdRef.current;
      if (!botId) {
        botId = await getInternalBotId();
      }

      const response = await ChatService.sendViaEmail(
        newChatEmail.trim(),
        Number(userId),
        newChatText.trim() || undefined,
        newChatFile || undefined,
        selectedTenantId ? Number(selectedTenantId) : undefined,
        botId || undefined
      );

      if (response.success || response.message === "Message sent successfully") {
        MySwal.fire({
          icon: "success",
          title: "Message Sent",
          text: "Your message has been sent successfully via email.",
          timer: 3000,
        });
        closeNewChatModal();
        fetchConversations();
      } else if (response.error_code === "END_USER_NOT_FOUND_BY_EMAIL") {
        setNewChatError("User Not Found");
      } else {
        throw new Error(response.message || response.error || "Failed to send message");
      }
    } catch (err: any) {
      if (err.message !== "User Not Found") { // Already handled or generic
        MySwal.fire({
          icon: "error",
          title: "Failed to Send",
          text: err.message || "Could not send your message. Please try again.",
        });
      }
    } finally {
      setNewChatSending(false);
    }
  };

  const handleRetryMessage = async (failedMessage: MessageWithUser) => {
    if (!selectedConversation || !failedMessage._localId) return;

    const localId = failedMessage._localId;
    const messageText = failedMessage.external_chat_message_value;

    setMessages((prev) =>
      prev.map((m) =>
        m._localId === localId ? { ...m, _status: "pending" as MessageStatus } : m
      )
    );

    setSending(true);

    try {
      const platformKey = getConversationPlatformKey(selectedConversation);
      const endUserId = selectedConversation.external_chat_end_user_id;
      const currentUserId = userId ? Number(userId) : null;
      const isGoogleChat = platformKey === "GOOGLE_CHAT";

      if (isGoogleChat) {
        if (currentUserId) {
          if (failedMessage.attachments && failedMessage.attachments.length > 0) {
            MySwal.fire({
              icon: "info",
              title: "Media Retry",
              text: "To retry sending this media message, please re-attach the file. Text retries are only for text-only messages.",
              timer: 5000,
            });
            throw new Error("MEDIA_RETRY_NOT_SUPPORTED");
          }
          await ChatService.sendGoogleChatTextMessage(currentUserId, endUserId, messageText || "");
        }
      } else {
        const assignedUserId = selectedConversation.assigned_user?.user_id;
        const autoAssignOnReply = selectedConversation.tenant?.auto_assign_chat_on_reply;

        if (!assignedUserId && currentUserId) {
          const response = await ChatService.sendNewLeadMessage(
            selectedConversation.channel_user_id,
            messageText,
            currentUserId,
            platformKey,
            endUserId,
            undefined,
            undefined,
            getEngatiTenantBotParams(selectedConversation)
          );
          if (response.success && response.message === "Message sent successfully") {
            MySwal.fire({
              toast: true,
              position: "bottom-end",
              icon: "success",
              title: "Chat Assigned",
              showConfirmButton: false,
              timer: 3000,
              text: "You have been assigned to this conversation.",
              timerProgressBar: true,
            });
          }
        } else if (assignedUserId === currentUserId) {
          await ChatService.sendTextMessage(
            selectedConversation.channel_user_id,
            messageText,
            currentUserId ?? undefined,
            platformKey,
            endUserId,
            false,
            undefined,
            undefined,
            messageVisibility,
            getEngatiTenantBotParams(selectedConversation)
          );
        } else if (assignedUserId !== currentUserId) {
          if (autoAssignOnReply && currentUserId) {
            const response = await ChatService.sendNewLeadMessage(
              selectedConversation.channel_user_id,
              messageText,
              currentUserId,
              platformKey,
              endUserId,
              undefined,
              undefined,
              getEngatiTenantBotParams(selectedConversation)
            );
            if (response.success && response.message === "Message sent successfully") {
              MySwal.fire({
                toast: true,
                position: "bottom-end",
                icon: "success",
                title: "Chat Assigned",
                showConfirmButton: false,
                timer: 3000,
                text: "You have been assigned to this conversation.",
                timerProgressBar: true,
              });
            }
          } else {
            await ChatService.sendTextMessage(
              selectedConversation.channel_user_id,
              messageText,
              currentUserId ?? undefined,
              platformKey,
              endUserId,
              false,
              undefined,
              undefined,
              messageVisibility,
              getEngatiTenantBotParams(selectedConversation)
            );
          }
        }
      }

      setMessages((prev) =>
        prev.map((m) =>
          m._localId === localId ? { ...m, _status: "sent" as MessageStatus } : m
        )
      );

      await fetchMessages(selectedConversation);
      fetchConversations();
    } catch (err: any) {
      setMessages((prev) =>
        prev.map((m) =>
          m._localId === localId ? { ...m, _status: "error" as MessageStatus } : m
        )
      );
      const platformKey = getConversationPlatformKey(selectedConversation);
      const isAuthError = handleAuthError(err, platformKey, routerNavigate);
      if (!isAuthError) {
        MySwal.fire({
          icon: "error",
          title: "Failed to Send",
          text: "Could not send your message. Please try again.",
          timer: 3000,
        });
      }
    } finally {
      setSending(false);
    }
  };

  const handleFileAttachment = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []) as File[];
    if (files.length === 0 || !selectedConversation) return;

    const isLazada = getConversationPlatformKey(selectedConversation) === "LAZADA";
    if (isLazada) {
      const nonImage = files.find(f => !f.type.startsWith("image/"));
      if (nonImage) {
        MySwal.fire({
          icon: "error",
          title: "Invalid File Type",
          text: "Only image files are allowed for Lazada.",
          timer: 3000,
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
    }

    const isGoogleChat = getConversationPlatformKey(selectedConversation) === "GOOGLE_CHAT";

    if (isGoogleChat) {
      const newFiles: File[] = [];
      const newPreviews: { url: string; name: string; type: string; size: number }[] = [];

      files.forEach(file => {
        const url = URL.createObjectURL(file);
        gcPendingFilePreviewsRef.current.push(url);
        newFiles.push(file);
        newPreviews.push({ url, name: file.name, type: file.type, size: file.size });
      });

      setGcPendingFiles(prev => [...prev, ...newFiles]);
      setGcPendingFilePreviews(prev => [...prev, ...newPreviews]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // For other platforms, take only the first file
    const file = files[0];
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    const url = URL.createObjectURL(file);
    setPreviewFile(file);
    setPreviewUrl(url);
    previewUrlRef.current = url;
    setShowMediaPreview(true);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSendMedia = async () => {
    if (!previewFile || !selectedConversation) return;

    const localId = `local-media-${Date.now()}-${Math.random()}`;
    const objectUrl = previewUrl;
    const platformKey = getConversationPlatformKey(selectedConversation);
    const isGoogleChat = platformKey === "GOOGLE_CHAT";
    const captionText = messageInput.trim();

    let messageType: "IMAGE" | "VIDEO" | "AUDIO" | "PDF";
    if (previewFile.type.startsWith("image/")) {
      messageType = "IMAGE";
    } else if (previewFile.type.startsWith("video/")) {
      messageType = "VIDEO";
    } else if (previewFile.type.startsWith("audio/")) {
      messageType = "AUDIO";
    } else {
      messageType = "PDF";
    }

    const parent_message_id = (replyingToMessage as any)?.external_chat_message_id;
    const root_message_id = (replyingToMessage as any)?.root_message_id;

    const optimisticMedia: MessageWithUser = {
      external_chat_message_id: -Date.now(),
      external_chat_message_value: captionText || objectUrl,
      created_datetime: new Date().toISOString(),
      external_chat_message_datetime: new Date().toISOString(),
      is_read: null,
      external_chat_channel: (selectedConversation.external_chat_channel?.external_chat_channel_id || selectedConversation.external_chat_channel_id)
        ? {
          external_chat_channel_id: selectedConversation.external_chat_channel?.external_chat_channel_id || selectedConversation.external_chat_channel_id || 0,
          external_chat_channel_key: selectedConversation.external_chat_channel?.external_chat_channel_key || selectedConversation.external_chat_channel_key || "",
          external_chat_channel_display_name: selectedConversation.external_chat_channel?.external_chat_channel_display_name || ""
        }
        : {
          external_chat_channel_id: 0,
          external_chat_channel_key: selectedConversation.external_chat_channel?.external_chat_channel_key || selectedConversation.external_chat_channel_key || "",
          external_chat_channel_display_name: selectedConversation.external_chat_channel?.external_chat_channel_display_name || ""
        },
      external_chat_platform_packet_type: {
        external_chat_platform_packet_type_id: 2,
        external_chat_platform_packet_type_key: "AGENT_MESSAGE",
        external_chat_platform_packet_type_display_name: "Agent Message",
      },
      external_chat_packet_type: {
        external_chat_packet_type_id: 1,
        external_chat_packet_type_key: isGoogleChat ? "GOOGLE_CHAT_FILE" : messageType,
        external_chat_packet_type_display_name: isGoogleChat ? "File" : messageType,
      },
      tenant: selectedConversation.tenant,
      external_chat_end_user: selectedConversation,
      sender: userId
        ? {
          sender_id: Number(userId),
          first_name: auth?.first_name || auth?.full_name,
          last_name: auth?.last_name || null,
          avatar_url: auth?.avatar_url || null,
          user_role: groupMembers.find(m => m.user_id === Number(userId) || m.user?.user_id === Number(userId) || (m.member_email && auth?.email && m.member_email === auth?.email))?.user_role || auth?.tenants?.find((t: any) => String(t.tenant_id) === String(selectedTenantId))?.role_name || auth?.tenants?.find((t: any) => String(t.tenant_id) === String(selectedTenantId))?.role_key
        }
        : undefined,
      _status: "pending",
      _localId: localId,
      _fileName: previewFile.name,
      attachments: isGoogleChat
        ? [{ attachment_id: -2, title: previewFile.name, size_in_kb: previewFile.size / 1024, attachment_url: objectUrl, extension: previewFile.name.split(".").pop() || "" }]
        : undefined,
      parent_message_id,
      root_message_id
    };

    setMessages((prev) => [optimisticMedia, ...prev]);
    setShowMediaPreview(false);
    setPreviewFile(null);
    setPreviewUrl("");
    setReplyingToMessage(null);
    setSending(true);

    try {
      const endUserId = selectedConversation.external_chat_end_user_id;
      const currentUserId = userId ? Number(userId) : null;

      if (isGoogleChat) {
        if (currentUserId) {
          await ChatService.sendGoogleChatMediaMessage(
            currentUserId,
            endUserId,
            [previewFile as File],
            captionText || undefined,
            parent_message_id,
            root_message_id
          );
        }
      } else {
        await ChatService.sendMediaMessage(
          selectedConversation.channel_user_id,
          previewFile,
          messageType,
          currentUserId ?? undefined,
          platformKey,
          endUserId,
          false,
          undefined,
          parent_message_id,
          root_message_id,
          getEngatiTenantBotParams(selectedConversation)
        );
      }

      setMessages((prev) =>
        prev.map((m) =>
          m._localId === localId ? { ...m, _status: "sent" as MessageStatus } : m
        )
      );

      await fetchMessages(selectedConversation);
    } catch (err: any) {
      setMessages((prev) =>
        prev.map((m) =>
          m._localId === localId ? { ...m, _status: "error" as MessageStatus } : m
        )
      );
      const resolvedPlatformKey = getConversationPlatformKey(selectedConversation);
      const isAuthError = handleAuthError(err, resolvedPlatformKey, routerNavigate);
      if (!isAuthError) {
        MySwal.fire({
          icon: "error",
          title: "Failed to Send Media",
          text: "Could not send your file. Please try again.",
          timer: 3000,
        });
      }
    } finally {
      setSending(false);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    }
  };

  const handleCancelPreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewFile(null);
    setPreviewUrl("");
    setShowMediaPreview(false);
  };

  const scrollThreadToBottom = (behavior: ScrollBehavior = "auto") => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          const viewport = threadOsRef.current?.osInstance()?.elements().viewport;
          if (viewport) {
            viewport.scrollTo({ top: viewport.scrollHeight - viewport.clientHeight, behavior });
          }
        }, 0);
      });
    });
  };

  const scrollToBottom = () => {
    const run = () => {
      const instance = osRef.current?.osInstance();
      if (instance) {
        const { viewport } = instance.elements();
        if (viewport) {
          const top = viewport.scrollHeight - viewport.clientHeight;
          viewport.scrollTo({ top, behavior: "smooth" });
        }
      } else {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      }
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(run, 0);
      });
    });
  };

  const getChannelIcon = (channelKey: string) => {
    switch ((channelKey || "").trim().toUpperCase()) {
      case "SMS":
        return "fa-solid fa-message";
      case "WHATSAPP":
        return "fa-brands fa-whatsapp";
      case "FACEBOOK":
        return "fa-brands fa-facebook";
      case "INSTAGRAM":
        return "fa-brands fa-instagram";
      case "LINE":
        return "fa-brands fa-line";
      case "SLACK":
        return "fa-brands fa-slack";
      case "TELEGRAM":
        return "fa-brands fa-telegram";
      case "GOOGLE_CHAT":
        return "fa-solid fa-comments";
      case "LAZADA":
        return "fa-solid fa-shopping-cart";
      case "TIKTOK":
        return "fa-brands fa-tiktok";
      case "WEB":
        return "bi bi-display";
      default:
        return "fa-solid fa-message";
    }
  };

  const getChannelColor = (channelKey: string) => {
    switch ((channelKey || "").trim().toUpperCase()) {
      case "SMS":
        return "#4caf50";
      case "WHATSAPP":
        return "#25D366";
      case "FACEBOOK":
        return "#1877F2";
      case "INSTAGRAM":
        return "#E4405F";
      case "LINE":
        return "#00B900";
      case "SLACK":
        return "#4A154B";
      case "TELEGRAM":
        return "#2AABEE";
      case "GOOGLE_CHAT":
        return "#34a853";
      case "LAZADA":
        return "#f36f21";
      case "TIKTOK":
        return "#000000";
      case "WEB":
        return "#AE1536";
      default:
        return "#666666";
    }
  };

  const getChannelBgColor = (channelKey: string) => {
    switch ((channelKey || "").trim().toUpperCase()) {
      case "SMS":
        return "rgba(76, 175, 80, 0.1)";
      case "WHATSAPP":
        return "rgba(37, 211, 102, 0.1)";
      case "FACEBOOK":
        return "rgba(24, 119, 242, 0.1)";
      case "INSTAGRAM":
        return "rgba(228, 64, 95, 0.1)";
      case "LINE":
        return "rgba(0, 185, 0, 0.1)";
      case "SLACK":
        return "rgba(74, 21, 75, 0.1)";
      case "WEB":
        return "rgba(174, 21, 54, 0.1)";
      case "GLOBAL":
        return "rgba(99, 102, 241, 0.1)";
      default:
        return "rgba(102, 102, 102, 0.1)";
    }
  };

  const formatMessageTime = (datetime: string) => {
    if (!datetime) return "";
    let dateStr = datetime;
    if (
      !dateStr.endsWith("Z") &&
      !dateStr.includes("+") &&
      !dateStr.includes("-", 10)
    ) {
      dateStr = dateStr + "Z";
    }

    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return "";
    }
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatConversationDate = (datetime: string) => {
    if (!datetime) return "";
    let dateStr = datetime;
    if (
      !dateStr.endsWith("Z") &&
      !dateStr.includes("+") &&
      !dateStr.includes("-", 10)
    ) {
      dateStr = dateStr + "Z";
    }

    const messageDate = new Date(dateStr);
    if (isNaN(messageDate.getTime())) {
      return "";
    }

    const now = new Date();

    const todayMidnight = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    const messageMidnight = new Date(
      messageDate.getFullYear(),
      messageDate.getMonth(),
      messageDate.getDate(),
    );

    const diffTime = todayMidnight.getTime() - messageMidnight.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return messageDate.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } else if (diffDays === 1) {
      return "Yesterday";
    } else {
      return messageDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
  };

  const handleReaction = async (message: MessageWithUser | ThreadMessage, emoji: string) => {
    if (!selectedConversation || userId === undefined || userId === null || chatTypeFilter !== "INTERNAL") return;

    const isThreadMsg = 'root_message_id' in message && !('external_chat_end_user' in message);
    const currentReactions = message.reactions || [];
    const existingReaction = currentReactions.find(r =>
      (r.reaction_type === emoji || r.reaction_type === `unicode:${emoji}`) && r.is_me
    );

    if (existingReaction) {
      // REMOVE REACTION
      const updateState = (prev: any[]) => prev.map((m) => {
        if (m.external_chat_message_id === message.external_chat_message_id) {
          return {
            ...m,
            reactions: (m.reactions || []).filter((r: any) => r.external_chat_message_reaction_id !== existingReaction.external_chat_message_reaction_id)
          };
        }
        return m;
      });

      if (isThreadMsg) {
        setThreadMessages(updateState as any);
      } else {
        setMessages(updateState);
      }

      if (threadParentMessage?.external_chat_message_id === message.external_chat_message_id) {
        setThreadParentMessage(prev => prev ? {
          ...prev,
          reactions: (prev.reactions || []).filter((r: any) => r.external_chat_message_reaction_id !== existingReaction.external_chat_message_reaction_id)
        } : null);
      }

      try {
        await ChatService.removeReaction({
          sender_id: Number(userId),
          external_chat_message_id: message.external_chat_message_id,
          external_chat_message_reaction_id: existingReaction.external_chat_message_reaction_id
        });
        fetchMessages(selectedConversation, false, null, true);
      } catch (err) {
        console.error("Failed to remove reaction:", err);
      }
    } else {
      // ADD REACTION
      const optimisticId = Date.now();
      const newReaction: any = {
        external_chat_message_reaction_id: optimisticId,
        reaction_type: emoji,
        member_id: userId.toString(),
        member_name: auth?.full_name || "You",
        member_email: auth?.email || "",
        member_profile_picture: auth?.avatar_url || "",
        is_me: true
      };

      const updateState = (prev: any[]) => prev.map((m) => {
        if (m.external_chat_message_id === message.external_chat_message_id) {
          return {
            ...m,
            reactions: [...(m.reactions || []), newReaction]
          };
        }
        return m;
      });

      if (isThreadMsg) {
        setThreadMessages(updateState as any);
      } else {
        setMessages(updateState);
      }

      if (threadParentMessage?.external_chat_message_id === message.external_chat_message_id) {
        setThreadParentMessage(prev => prev ? {
          ...prev,
          reactions: [...(prev.reactions || []), newReaction]
        } : null);
      }

      setReactionPickerMessageId(null);

      try {
        const response = await ChatService.addReaction({
          sender_id: Number(userId),
          end_user_id: selectedConversation.external_chat_end_user_id,
          message_id: message.external_chat_message_id,
          reaction: emoji,
        });

        const realId = response?.external_chat_message_reaction_id || response?.data?.external_chat_message_reaction_id || response?.reaction?.external_chat_message_reaction_id;

        if (realId) {
          const updateRealId = (prev: any[]) => prev.map((m) => {
            if (m.external_chat_message_id === message.external_chat_message_id) {
              return {
                ...m,
                reactions: (m.reactions || []).map((r: any) =>
                  r.external_chat_message_reaction_id === optimisticId ? { ...r, external_chat_message_reaction_id: realId } : r
                )
              };
            }
            return m;
          });

          if (isThreadMsg) {
            setThreadMessages(updateRealId as any);
          } else {
            setMessages(updateRealId);
          }

          if (threadParentMessage?.external_chat_message_id === message.external_chat_message_id) {
            setThreadParentMessage(prev => prev ? {
              ...prev,
              reactions: (prev.reactions || []).map((r: any) =>
                r.external_chat_message_reaction_id === optimisticId ? { ...r, external_chat_message_reaction_id: realId } : r
              )
            } : null);
          }
        }

        // Refresh messages from server to ensure full sync
        fetchMessages(selectedConversation, false, null, true);
      } catch (err) {
        console.error("Failed to add reaction:", err);
        const rollbackAdd = (prev: any[]) => prev.map((m) => {
          if (m.external_chat_message_id === message.external_chat_message_id) {
            return {
              ...m,
              reactions: (m.reactions || []).filter((r: any) => r.external_chat_message_reaction_id !== optimisticId)
            };
          }
          return m;
        });

        if (isThreadMsg) {
          setThreadMessages(rollbackAdd as any);
        } else {
          setMessages(rollbackAdd);
        }

        if (threadParentMessage?.external_chat_message_id === message.external_chat_message_id) {
          setThreadParentMessage(prev => prev ? {
            ...prev,
            reactions: (prev.reactions || []).filter((r: any) => r.external_chat_message_reaction_id !== optimisticId)
          } : null);
        }
      }
    }
  };

  const groupReactions = (reactions: any[]) => {
    return reactions.reduce((acc: any, curr: any) => {
      if (!curr.reaction_type || (!curr.reaction_type.startsWith("unicode:") && curr.reaction_type.length > 20)) {
        return acc;
      }
      const type = curr.reaction_type.replace("unicode:", "");
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(curr);
      return acc;
    }, {});
  };

  const fetchThreadMessages = useCallback(async (parentId: number, showLoading: boolean = false) => {
    if (!selectedTenantId) return;
    if (showLoading) setThreadLoading(true);
    try {
      const res = await ChatService.getThreadMessages(Number(selectedTenantId), parentId);
      if (res.success && res.data) {
        const sorted = (res.data as ThreadMessage[]).sort((a, b) => {
          const dateA = new Date(a.created_datetime).getTime();
          const dateB = new Date(b.created_datetime).getTime();
          return dateB - dateA;
        });
        setThreadMessages(sorted);
      }
    } catch (err) {
      console.error("Failed to load thread messages:", err);
    } finally {
      if (showLoading) setThreadLoading(false);
    }
  }, [selectedTenantId]);

  useEffect(() => {
    fetchThreadMessagesRef.current = (parentId: number) => fetchThreadMessages(parentId, false);
  }, [fetchThreadMessages]);

  useEffect(() => {
    threadParentMessageRef.current = threadParentMessage;
  }, [threadParentMessage]);

  useEffect(() => {
    isThreadPanelVisibleRef.current = isDetailsOpen && rightPanelMode === "THREAD" && !!threadParentMessage;
  }, [isDetailsOpen, rightPanelMode, threadParentMessage]);

  useEffect(() => {
    if (rightPanelMode !== "THREAD" || threadLoading) return;
    const previousCount = previousThreadMessageCountRef.current;
    previousThreadMessageCountRef.current = threadMessages.length;
    if (threadMessages.length > previousCount && threadStickToBottomRef.current) {
      scrollThreadToBottom(previousCount === 0 ? "auto" : "smooth");
    }
  }, [threadMessages.length, threadLoading, rightPanelMode]);

  useEffect(() => {
    const threadBody = threadBodyRef.current;
    if (!threadBody) return;
    const handleMediaLoad = () => {
      if (threadStickToBottomRef.current) scrollThreadToBottom("auto");
    };
    threadBody.addEventListener("load", handleMediaLoad, true);
    threadBody.addEventListener("loadedmetadata", handleMediaLoad, true);
    return () => {
      threadBody.removeEventListener("load", handleMediaLoad, true);
      threadBody.removeEventListener("loadedmetadata", handleMediaLoad, true);
    };
  }, [rightPanelMode, isDetailsOpen, threadParentMessage]);

  const openThread = async (message: MessageWithUser) => {
    if (!selectedTenantId) return;
    threadStickToBottomRef.current = true;
    previousThreadMessageCountRef.current = 0;
    setThreadParentMessage(message);
    setThreadMessages([]);
    setThreadInput("");
    setThreadPendingFiles([]);
    setThreadActivity((prev) =>
      prev[message.external_chat_message_id]
        ? { ...prev, [message.external_chat_message_id]: { ...prev[message.external_chat_message_id], unread: 0 } }
        : prev,
    );
    setRightPanelMode("THREAD");
    setIsDetailsOpen(true);
    setMobileActivePanel("right");
    await fetchThreadMessages(message.external_chat_message_id, true);
  };

  const handleThreadFileAttachment = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []) as File[];
    if (files.length > 0) {
      const newPending = files.map((file) => {
        const url = URL.createObjectURL(file);
        gcPendingFilePreviewsRef.current.push(url);
        return { file, url };
      });
      setThreadPendingFiles((prev) => [...prev, ...newPending]);
    }
    if (threadFileInputRef.current) threadFileInputRef.current.value = "";
  };

  const removeThreadPendingFile = (index: number) => {
    setThreadPendingFiles((prev) => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed.url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSendThreadMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    threadInputRef.current?.focus();
    const hasThreadText = threadInput.trim().length > 0;
    const hasThreadFiles = threadPendingFiles.length > 0;
    if (!threadParentMessage || !selectedConversation || (!hasThreadText && !hasThreadFiles) || threadSending) return;
    if (!userId) return;

    const textToSend = threadInput.trim();
    const pendingToSend = [...threadPendingFiles];
    const optimisticThreadAttachments = pendingToSend.length > 0
      ? pendingToSend.map(({ file, url }, idx) => ({
        attachment_id: -1 - idx,
        title: file.name,
        size_in_kb: file.size / 1024,
        attachment_url: url,
        extension: file.name.split(".").pop() || "",
      }))
      : undefined;
    setThreadInput("");
    setThreadPendingFiles([]);
    setThreadSending(true);
    setTimeout(() => {
      threadInputRef.current?.focus();
    }, 0);

    const parent_message_id = (threadReplyingToMessage as any)?.external_chat_message_id || threadParentMessage.external_chat_message_id;
    const root_message_id = threadParentMessage.external_chat_message_id;

    const optimisticThread: ThreadMessage = {
      external_chat_message_id: -Date.now(),
      parent_message_id: parent_message_id,
      root_message_id: root_message_id,
      external_chat_message_value: textToSend,
      external_chat_message_datetime: new Date().toISOString(),
      created_datetime: new Date().toISOString(),
      is_read: false,
      external_chat_platform_packet_type_id: 2,
      external_chat_packet_type_id: 1,
      thread_messages: [],
      external_chat_platform_packet_type: {
        external_chat_platform_packet_type_id: 2,
        external_chat_platform_packet_type_key: "AGENT_MESSAGE",
        external_chat_platform_packet_type_display_name: "Agent Message",
      },
      external_chat_packet_type: {
        external_chat_packet_type_id: 1,
        external_chat_packet_type_key: optimisticThreadAttachments ? "GOOGLE_CHAT_FILE" : "TEXT",
        external_chat_packet_type_display_name: optimisticThreadAttachments ? "File" : "Text",
      },
      external_sender_name: [auth?.first_name, auth?.last_name].filter(Boolean).join(" ") || auth?.full_name,
      external_sender_info: null,
      attachments: optimisticThreadAttachments,
    };

    threadStickToBottomRef.current = true;
    setThreadMessages((prev) => [optimisticThread, ...prev]);
    setThreadReplyingToMessage(null);

    try {
      await ChatService.sendThreadMessage({
        sender_id: Number(userId),
        end_user_id: selectedConversation.external_chat_end_user_id,
        parent_message_id: parent_message_id,
        root_message_id: root_message_id,
        text: textToSend,
        files: pendingToSend.map(({ file }) => file),
      });

      if (selectedTenantId) {
        await fetchThreadMessages(threadParentMessage.external_chat_message_id, false);
      }
      fetchMessages(selectedConversation);
      fetchConversations();
    } catch (err: any) {
      const platformKey = getConversationPlatformKey(selectedConversation);
      const isAuthError = handleAuthError(err, platformKey, routerNavigate);
      if (!isAuthError) {
        MySwal.fire({
          icon: "error",
          title: "Failed to Send",
          text: "Could not send your thread reply. Please try again.",
          timer: 3000,
        });
      }
    } finally {
      setThreadSending(false);
    }
  };

  const scrollToMessage = (messageId: number | null | undefined) => {
    if (!messageId) return;
    const element = document.getElementById(`msg-${messageId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      element.classList.add("highlight-message");
      setTimeout(() => {
        element.classList.remove("highlight-message");
      }, 2000);
    }
  };

  const findParentMessage = (parentId: number | null | undefined) => {
    if (!parentId) return null;
    let found: any = messages.find(m => m.external_chat_message_id === parentId);
    if (found) return found;
    found = threadMessages.find(m => m.external_chat_message_id === parentId);
    if (found) return found;
    if (threadParentMessage?.external_chat_message_id === parentId) return threadParentMessage;
    return null;
  };

  const isUrl = (val: string) => /^https?:\/\//i.test(val);

  const renderMessageContent = (message: MessageWithUser) => {
    const packetType = message.external_chat_packet_type?.external_chat_packet_type_key;
    const isAgent = message.external_chat_platform_packet_type?.external_chat_platform_packet_type_key === "AGENT_MESSAGE";

    const isQuoted = (message.parent_message_id != null && message.root_message_id == null) ||
      (message.parent_message_id != null && message.root_message_id != null && message.parent_message_id !== message.root_message_id);

    const renderQuote = () => {
      if (!isQuoted) return null;
      const parent = findParentMessage(message.parent_message_id);
      if (!parent) return null;

      const isParentAgent = parent.external_chat_platform_packet_type?.external_chat_platform_packet_type_key === "AGENT_MESSAGE" || parent.sender_type === "AGENT";
      const senderName = isParentAgent
        ? getAgentOrBotName(parent, "You")
        : (parent.external_sender_name || parent.external_chat_end_user?.username || "User");

      const senderAvatar = isParentAgent
        ? (parent.sender?.avatar_url || dummy)
        : (parent.external_sender_info?.member_profile_url || parent.external_chat_end_user?.end_user_profile_image_url || dummy);

      return (
        <div
          className="quoted-message-container"
          onClick={(e) => {
            e.stopPropagation();
            scrollToMessage(message.parent_message_id);
          }}
        >
          <div className="quoted-message-header">
            <span className="quoted-icon">“</span>
            <img src={senderAvatar} alt="" className="quoted-sender-avatar" onError={(e) => (e.currentTarget.src = dummy)} />
            <span className="quoted-sender-name">{senderName}</span>
          </div>
          <div className="quoted-message-body">
            {getMessagePreviewText(parent)}
          </div>
        </div>
      );
    };

    const getMessageFile = () => {
      const value = message.external_chat_message_value || "";
      let media: { value?: unknown; mimeType?: unknown } | undefined;
      try {
        media = message.raw_message ? JSON.parse(message.raw_message)?.media : undefined;
      } catch (e) {
        // ignore
      }
      const mimeType = typeof media?.mimeType === "string" ? media.mimeType : undefined;
      if (isUrl(value) || value.startsWith("blob:")) return { url: value, mimeType };
      if (typeof media?.value === "string" && isUrl(media.value)) return { url: media.value, mimeType };
      return { url: value, mimeType };
    };

    if (message.attachments && message.attachments.length > 0) {
      const gcText = message.external_chat_message_value;
      const IMAGE_EXTS = ["jpeg", "jpg", "png", "gif", "webp", "bmp", "svg"];
      const VIDEO_EXTS = ["mp4", "mov", "avi", "webm", "mkv"];
      const AUDIO_EXTS = ["mp3", "wav", "ogg", "m4a", "aac"];
      const renderAtt = (att: { attachment_id: number; title: string; attachment_url: string; extension: string }) => {
        const ext = (att.extension || "").toLowerCase();
        const isImage = IMAGE_EXTS.includes(ext) || packetType === "IMAGE";
        const isVideo = VIDEO_EXTS.includes(ext) || packetType === "VIDEO";
        const isAudio = AUDIO_EXTS.includes(ext) || packetType === "AUDIO";
        if (isImage) return (
          <div key={att.attachment_id} className="gc-attachment-item">
            <ImageWithLoader
              src={att.attachment_url}
              alt={att.title}
              className="message-media-image"
              onClick={() => { setViewerImageUrl(att.attachment_url); setImageZoom(1); setShowImageViewer(true); }}
            />
          </div>
        );
        if (isVideo) return (
          <div key={att.attachment_id} className="gc-attachment-item">
            <video controls src={att.attachment_url} className="message-media-video" />
          </div>
        );
        if (isAudio) return (
          <div key={att.attachment_id} className="gc-attachment-item">
            <audio controls src={att.attachment_url} className="message-media-audio" />
          </div>
        );
        return (
          <div key={att.attachment_id} className="gc-attachment-item">
            <FileViewer url={att.attachment_url} label={att.title} />
          </div>
        );
      };
      return (
        <div className="gc-message-bubble">
          {renderQuote()}
          <div className="gc-attachments-list">
            {message.attachments.map((att) => renderAtt(att))}
          </div>
          {gcText && gcText.trim() && !gcText.startsWith("blob:") && !message.attachments.some((att) => {
            if (!att.attachment_url) return false;
            const c1 = gcText.trim().replace(/^<|>$/g, '');
            const c2 = att.attachment_url.trim().replace(/^<|>$/g, '');
            if (c1 === c2 || c1.includes(c2) || c2.includes(c1)) return true;
            // If the message is just a URL (no spaces) and we have an attachment, the attachment is likely a preview of this URL.
            if (/^https?:\/\/[^\s]+$/i.test(c1)) return true;
            return false;
          }) && (
              <p style={{ color: isAgent ? "white" : "inherit", marginTop: "6px" }}>{gcText}</p>
            )}
        </div>
      );
    }

    if (!packetType) {
      return (
        <>
          {renderQuote()}
          <p style={{ color: isAgent ? "white" : "inherit" }}>{message.external_chat_message_value}</p>
        </>
      );
    }

    const effectivePacketType = (packetType === "TEXT" && message.google_chat_meeting_link)
      ? "GOOGLE_CHAT_MEETING_LINK"
      : (packetType === "TEXT" && message.google_chat_card)
        ? "GOOGLE_CHAT_CARD"
        : packetType;

    switch (effectivePacketType) {
      case "STICKER":
        return (
          <ImageWithLoader
            src={message.external_chat_message_value}
            alt="Sticker"
            style={{ width: "150px", height: "150px" }}
          />
        );
      case "TEXT":
        return (
          <>
            {renderQuote()}
            <p style={{ color: isAgent ? "white" : "inherit" }}>
              {message.external_chat_message_value}
            </p>
          </>
        );
      case "IMAGE":
        return (
          <>
            {renderQuote()}
            <ImageWithLoader
              src={message.external_chat_message_value}
              alt="Shared image"
              className="message-media-image"
              onClick={() => {
                setViewerImageUrl(message.external_chat_message_value);
                setImageZoom(1);
                setShowImageViewer(true);
              }}
            />
          </>
        );
      case "VIDEO":
        return (
          <>
            {renderQuote()}
            <video
              controls
              src={message.external_chat_message_value}
              className="message-media-video"
            />
          </>
        );
      case "AUDIO":
        return (
          <>
            {renderQuote()}
            <audio
              controls
              src={message.external_chat_message_value}
              className="message-media-audio"
            />
          </>
        );
      case "PDF":
      case "FILE":
      case "FILE_RECEIVED":
      case "APPLICATION":
      case "DOCUMENT": {
        const file = getMessageFile();
        return (
          <>
            {renderQuote()}
            <FileViewer url={file.url} label={message._fileName} mimeType={file.mimeType} />
          </>
        );
      }
      case "GOOGLE_CHAT_FILE": {
        const gcAttachments = message.attachments as any[] as (GoogleChatAttachment & { download_uri?: string; content_name?: string })[];
        const gcText = message.external_chat_message_value;
        const renderGCAttachment = (att: any) => {
          const uri = att.attachment_url || att.download_uri || "";
          const title = att.title || att.content_name || "Attachment";
          const ext = (att.extension || title.split(".").pop() || "").toLowerCase();
          const isImage = ["jpg", "jpeg", "png", "gif", "webp", "image/jpeg", "image/png", "image/gif"].some(e => ext.includes(e) || (att.content_type && att.content_type.includes("image/")));
          const isVideo = ["mp4", "mov", "webm", "video/mp4"].some(e => ext.includes(e) || (att.content_type && att.content_type.includes("video/")));
          const isAudio = ["mp3", "wav", "ogg", "audio/mpeg"].some(e => ext.includes(e) || (att.content_type && att.content_type.includes("audio/")));

          if (isImage) {
            return (
              <div key={title} className="gc-attachment-item">
                {uri ? (
                  <ImageWithLoader
                    src={uri}
                    alt={title}
                    className="message-media-image"
                    onClick={() => { setViewerImageUrl(uri); setImageZoom(1); setShowImageViewer(true); }}
                  />
                ) : (
                  <div className="msg-file-icon-box"><i className="ti ti-photo" /></div>
                )}
              </div>
            );
          }
          if (isVideo) {
            return (
              <div key={title} className="gc-attachment-item">
                {uri ? <video controls src={uri} className="message-media-video" /> : <div className="msg-file-icon-box"><i className="ti ti-video" /></div>}
              </div>
            );
          }
          if (isAudio) {
            return (
              <div key={title} className="gc-attachment-item">
                {uri ? <audio controls src={uri} className="message-media-audio" /> : <div className="msg-file-icon-box"><i className="ti ti-music" /></div>}
              </div>
            );
          }
          return (
            <div key={title} className="gc-attachment-item">
              <FileViewer url={uri || ""} label={title} mimeType={att.content_type} />
            </div>
          );
        };
        return (
          <div className="gc-message-bubble">
            {renderQuote()}
            {gcAttachments && gcAttachments.length > 0 && (
              <div className="gc-attachments-list">
                {gcAttachments.map((att, idx) => (
                  <React.Fragment key={att.attachment_id || idx}>
                    {renderGCAttachment(att)}
                  </React.Fragment>
                ))}
              </div>
            )}
            {gcText && gcText.trim() && !gcText.startsWith("blob:") && (
              <p style={{ color: isAgent ? "white" : "inherit", marginTop: gcAttachments && gcAttachments.length > 0 ? "6px" : 0 }}>{gcText}</p>
            )}
          </div>
        );
      }
      case "OPTIONS":
      case "POSTBACK": {
        const raw = message.external_chat_message_value || "";
        const cleaned = raw.replace(/^\[|\]$/g, "").trim();
        const options = cleaned.split(",").map((o) => o.trim()).filter(Boolean);
        return (
          <div className="msg-options-container">
            {renderQuote()}
            {options.map((opt, idx) => (
              <span key={idx} className="msg-option-chip">
                {opt}
              </span>
            ))}
          </div>
        );
      }
      case "IDENTITY":
        return (
          <>
            {renderQuote()}
            <p style={{ color: isAgent ? "white" : "inherit" }}>
              {message.external_chat_message_value}
            </p>
          </>
        );
      case "CAROUSEL": {
        const carousel = message.carousel;
        if (!carousel) {
          return (
            <p style={{ color: isAgent ? "white" : "inherit" }}>
              {message.external_chat_message_value}
            </p>
          );
        }
        return (
          <div className="msg-carousel-card">
            {carousel.image_url ? (
              <ImageWithLoader
                src={carousel.image_url}
                alt={carousel.title}
                className="msg-carousel-image"
                onClick={() => {
                  if (carousel.image_url) {
                    setViewerImageUrl(carousel.image_url);
                    setImageZoom(1);
                    setShowImageViewer(true);
                  }
                }}
              />
            ) : (
              <div className="msg-carousel-image-placeholder">
                <i className="ti ti-photo" />
              </div>
            )}
            <div className="msg-carousel-body">
              {carousel.title && <div className="msg-carousel-title">{carousel.title}</div>}
              {carousel.description && <div className="msg-carousel-desc">{carousel.description}</div>}
            </div>
          </div>
        );
      }
      case "LAZADA_PRODUCT": {
        const product = message.lazada_product;
        if (!product) {
          return (
            <p style={{ color: isAgent ? "white" : "inherit" }}>
              {message.external_chat_message_value}
            </p>
          );
        }
        return (
          <div className="msg-carousel-card lazada-product-card" style={{ maxWidth: '300px', backgroundColor: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            {product.image ? (
              <ImageWithLoader
                src={product.image}
                alt={product.title}
                className="msg-carousel-image"
                style={{ width: '100%', height: 'auto', display: 'block' }}
                onClick={() => {
                  setViewerImageUrl(product.image);
                  setImageZoom(1);
                  setShowImageViewer(true);
                }}
              />
            ) : (
              <div className="msg-carousel-image-placeholder" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '150px', backgroundColor: '#f5f5f5' }}>
                <i className="ti ti-photo" style={{ fontSize: '24px', color: '#999' }} />
              </div>
            )}
            <div className="msg-carousel-body" style={{ padding: '12px' }}>
              <div className="msg-carousel-title" style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {product.title}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ color: '#f57224', fontWeight: 'bold', fontSize: '16px' }}>{product.price}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Qty: {product.quantity}</div>
              </div>
              {product.action_url && (
                <a
                  href={product.action_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    backgroundColor: '#1a1a1a',
                    color: 'white',
                    padding: '8px',
                    borderRadius: '4px',
                    textDecoration: 'none',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  View details
                </a>
              )}
            </div>
          </div>
        );
      }
      case "GOOGLE_CHAT_MEETING_LINK": {
        const meeting = message.google_chat_meeting_link;
        if (!meeting) {
          return (
            <p style={{ color: isAgent ? "white" : "inherit" }}>
              {message.external_chat_message_value}
            </p>
          );
        }
        return (
          <div style={{ maxWidth: '280px', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e5e7eb', backgroundColor: '#fff' }}>
            <div style={{ backgroundColor: '#824EEB', padding: '20px 16px 24px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', minHeight: '100px' }}>
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: '18px', lineHeight: '1.3', marginBottom: '4px' }}>{meeting.title || "Video meeting"}</div>
                <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>Google Meet</div>
              </div>
              <div style={{ width: '48px', height: '48px', borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginLeft: '12px' }}>
                <i className="ti ti-video" style={{ fontSize: '24px', color: 'rgba(255,255,255,0.75)' }} />
              </div>
            </div>
            <a
              href={meeting.uri}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', textDecoration: 'none', borderTop: '1px solid #f0f0f0' }}
            >
              <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <path fill="#4CAF50" d="M29 29H19v-6l-9 6v-10l9 6v-6h10v10z" />
                <path fill="#1565C0" d="M39 29l-10-5v-6l10-5v16z" />
                <rect fill="#E53935" x="19" y="9" width="10" height="10" rx="2" />
                <rect fill="#FDD835" x="9" y="29" width="10" height="10" rx="2" />
                <rect fill="#1565C0" x="19" y="29" width="10" height="10" rx="2" />
              </svg>
              <span style={{ color: '#824EEB', fontSize: '14px', fontWeight: 500 }}>Join video meeting</span>
            </a>
          </div>
        );
      }
      case "GOOGLE_CHAT_CARD": {
        const card = message.google_chat_card;
        if (!card) {
          return (
            <p style={{ color: isAgent ? "white" : "inherit" }}>
              {message.external_chat_message_value}
            </p>
          );
        }
        return (
          <div style={{ maxWidth: '320px', backgroundColor: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 6px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 12px 10px' }}>
              {card.image_url && (
                <img
                  src={card.image_url}
                  alt=""
                  style={{ width: '36px', height: '36px', objectFit: 'contain', flexShrink: 0, borderRadius: '4px' }}
                />
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '14px', lineHeight: '1.3', wordBreak: 'break-word' }}>{card.title}</div>
                {card.subtitle && (
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '2px', wordBreak: 'break-word' }}>{card.subtitle}</div>
                )}
              </div>
            </div>
            <div style={{ borderTop: '1px solid #e5e7eb' }}>
              {card.fields.map((field, i) => (
                <div key={i} style={{ padding: '8px 12px', borderBottom: i < card.fields.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                  {field.value && (
                    <div style={{ fontSize: '13px', wordBreak: 'break-word', marginBottom: field.label ? '4px' : '0' }}>
                      {isUrl(field.value) ? (
                        <a href={field.value} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                          {field.value}
                        </a>
                      ) : (
                        <span dangerouslySetInnerHTML={{ __html: field.value }} />
                      )}
                    </div>
                  )}
                  {field.label && (
                    <div
                      style={{ fontSize: '11px', color: '#9ca3af', wordBreak: 'break-word' }}
                      dangerouslySetInnerHTML={{ __html: field.label }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      }
      default:
        return <p style={{ color: isAgent ? "white" : "inherit" }}>{message.external_chat_message_value}</p>;
    }
  };

  useEffect(() => {
    const checkChatbotIntegration = async () => {
      setInitialLoading(true);
      if (!selectedTenantId) {
        setIsChatbotIntegrated(false);
        setInitialLoading(false);
        return;
      }
      try {
        const response = await ChatService.getExternalChatTenantBot(Number(selectedTenantId));
        const results: any[] = response?.results || [];
        const hasAuthorizedExternal = results.some((bot) => bot.is_authorized === true && bot.chat_type?.chat_type_key === "EXTERNAL");
        const internalBot = results.find((bot: any) => bot.is_authorized === true && bot.chat_type?.chat_type_key === "INTERNAL");
        const hasAuthorizedInternal = !!internalBot;
        const isIntegrated = hasAuthorizedExternal || hasAuthorizedInternal;
        localStorage.setItem("chatIntegration", String(isIntegrated));
        setIsChatbotIntegrated(isIntegrated);
        setHasAuthorizedExternalBot(hasAuthorizedExternal);
        setHasAuthorizedInternalBot(hasAuthorizedInternal);
        const resolvedInternalBotId = internalBot?.external_chat_tenant_bot_id ?? null;
        setInternalBotId(resolvedInternalBotId);
        internalBotIdRef.current = resolvedInternalBotId;
      } catch (error) {
        setIsChatbotIntegrated(false);
        setHasAuthorizedExternalBot(false);
        setHasAuthorizedInternalBot(false);
        setInternalBotId(null);
        internalBotIdRef.current = null;
      } finally {
        setTimeout(() => {
          setInitialLoading(false);
        }, 1200);
      }
    };

    setSelectedConversation(null);
    selectedConversationRef.current = null;
    setActiveConversationId(null);
    setMessages([]);
    setMobileActivePanel("left");
    setInternalBotId(null);
    internalBotIdRef.current = null;
    setStatusOptions([]);
    setAccountOptions([]);
    setPlatformOptions([]);
    setTenantUsers([]);
    setTeams([]);
    setChatTemplates([]);

    hasLoadedConversationsOnce.current = false;

    checkChatbotIntegration();
  }, [selectedTenantId, setActiveConversationId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && showImageViewer) {
        setShowImageViewer(false);
        setImageZoom(1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showImageViewer]);

  useEffect(() => {
    return () => {
      gcPendingFilePreviewsRef.current.forEach(url => URL.revokeObjectURL(url));
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      setActiveConversationId(null);
    };
  }, [setActiveConversationId]);

  useEffect(() => {
    const unregister = registerMessageListener(async (wsMessage: any) => {
      fetchConversationsRef.current?.();

      if (!selectedConversationRef.current) {
        return;
      }

      const incomingEndUserId =
        wsMessage.external_chat_end_user_id ??
        wsMessage.message?.external_chat_end_user_id;

      const openId = selectedConversationRef.current.external_chat_end_user_id;

      const isForOpenChat =
        incomingEndUserId &&
        Number(incomingEndUserId) === Number(openId);

      if (!isForOpenChat) {
        return;
      }

      const incomingMsg = wsMessage.message;

      if (incomingMsg && incomingMsg.external_chat_message_id) {
        const currentStatusKey = selectedConversationRef.current.external_chat_end_user_status?.external_chat_end_user_status_key;
        const incomingStatusKey = incomingMsg.external_chat_end_user_status?.external_chat_end_user_status_key;
        const threadRootId = Number(incomingMsg.root_message_id) || null;
        const isThreadReply = threadRootId !== null && threadRootId !== Number(incomingMsg.external_chat_message_id);

        if (incomingStatusKey && currentStatusKey && incomingStatusKey !== currentStatusKey && !isThreadReply) {
          fetchMessagesRef.current?.(selectedConversationRef.current, false, null, true);

          if (incomingMsg.external_chat_end_user_status) {
            setSelectedStatus(incomingMsg.external_chat_end_user_status);
            setSelectedConversation(prev => prev ? {
              ...prev,
              external_chat_end_user_status: incomingMsg.external_chat_end_user_status
            } : null);
            selectedConversationRef.current = {
              ...selectedConversationRef.current,
              external_chat_end_user_status: incomingMsg.external_chat_end_user_status
            };
          }

          if (threadParentMessageRef.current) {
            const isForOpenThread = (incomingMsg.parent_message_id === threadParentMessageRef.current.external_chat_message_id) ||
              (incomingMsg.root_message_id === threadParentMessageRef.current.external_chat_message_id);

            if (isForOpenThread) {
              fetchThreadMessagesRef.current?.(threadParentMessageRef.current.external_chat_message_id);
            }
          }
          return;
        }

        if (isThreadReply && threadRootId) {
          if (incomingStatusKey && currentStatusKey && incomingStatusKey !== currentStatusKey && incomingMsg.external_chat_end_user_status) {
            setSelectedStatus(incomingMsg.external_chat_end_user_status);
            setSelectedConversation(prev => prev ? {
              ...prev,
              external_chat_end_user_status: incomingMsg.external_chat_end_user_status
            } : null);
            selectedConversationRef.current = {
              ...selectedConversationRef.current,
              external_chat_end_user_status: incomingMsg.external_chat_end_user_status
            };
          }

          const replyAt = normalizeMessageDatetime(incomingMsg.created_datetime || incomingMsg.external_chat_message_datetime) ?? new Date().toISOString();
          const openThreadRootId = threadParentMessageRef.current?.external_chat_message_id;
          const isThreadOpen = isThreadPanelVisibleRef.current && openThreadRootId === threadRootId;
          const isOwnReply =
            (incomingMsg.sender?.sender_id != null && Number(incomingMsg.sender.sender_id) === Number(userId)) ||
            (!!auth?.email && incomingMsg.external_sender_info?.sender_email === auth.email);
          const isNewReply = !countedThreadReplyIdsRef.current.has(incomingMsg.external_chat_message_id);
          countedThreadReplyIdsRef.current.add(incomingMsg.external_chat_message_id);

          if (isNewReply && !isOwnReply) {
            setMessages((prev) =>
              prev.map((m) =>
                m.external_chat_message_id === threadRootId
                  ? { ...m, thread_message_count: (m.thread_message_count || 0) + 1 }
                  : m,
              ),
            );
          }

          const senderName =
            incomingMsg.external_sender_info?.sender_name ||
            incomingMsg.external_sender_name ||
            [incomingMsg.sender?.first_name, incomingMsg.sender?.last_name].filter(Boolean).join(" ") ||
            "User";
          const senderAvatar = incomingMsg.external_sender_info?.member_profile_url || incomingMsg.sender?.avatar_url || null;

          setThreadActivity((prev) => {
            const currentUnread = prev[threadRootId]?.unread || 0;
            return {
              ...prev,
              [threadRootId]: {
                unread: isThreadOpen ? 0 : currentUnread + (isNewReply && !isOwnReply ? 1 : 0),
                lastReplyAt: replyAt,
                senderName,
                senderAvatar,
              },
            };
          });

          if (isThreadOpen) {
            const threadReply = {
              ...incomingMsg,
              created_datetime: replyAt,
              external_chat_message_datetime: normalizeMessageDatetime(incomingMsg.external_chat_message_datetime) ?? replyAt,
            } as ThreadMessage;

            setThreadMessages((prev) => {
              if (prev.some((tm) => tm.external_chat_message_id === threadReply.external_chat_message_id)) return prev;
              const optimisticIndex = prev.findIndex(
                (tm) =>
                  tm.external_chat_message_id < 0 &&
                  (tm.external_chat_message_value || "") === (threadReply.external_chat_message_value || "") &&
                  (tm.attachments?.length || 0) === (threadReply.attachments?.length || 0),
              );
              const remaining = optimisticIndex !== -1 ? prev.filter((_, index) => index !== optimisticIndex) : prev;
              return [threadReply, ...remaining].sort(
                (a, b) => getMessageTime(b.created_datetime) - getMessageTime(a.created_datetime),
              );
            });
            fetchThreadMessagesRef.current?.(threadRootId);
          }
        } else {
          let isAtBottom = true;
          try {
            const instance = osRef.current?.osInstance();
            if (instance) {
              const { viewport } = instance.elements();
              if (viewport) {
                const { scrollTop, scrollHeight, clientHeight } = viewport;
                isAtBottom = scrollHeight - scrollTop <= clientHeight + 250;
              }
            }
          } catch (e) {
            // ignore
          }

          const receivedAt = new Date().toISOString();
          const normalizedMsg = {
            ...incomingMsg,
            created_datetime: normalizeMessageDatetime(incomingMsg.created_datetime || incomingMsg.external_chat_message_datetime) ?? receivedAt,
            external_chat_message_datetime: normalizeMessageDatetime(incomingMsg.external_chat_message_datetime || incomingMsg.created_datetime) ?? receivedAt,
          } as MessageWithUser;

          setMessages((prev) => {
            const matchedOptimisticIndex = prev.findIndex(p => {
              if (p.external_chat_message_id === normalizedMsg.external_chat_message_id) return true;
              if (p.external_chat_message_id < 0 && p.external_chat_message_value === normalizedMsg.external_chat_message_value) {
                if (p.attachments?.length || normalizedMsg.attachments?.length) {
                  return p.attachments?.length === normalizedMsg.attachments?.length;
                }
                return true;
              }
              return false;
            });

            if (matchedOptimisticIndex !== -1) {
              const existing = prev[matchedOptimisticIndex];
              // If it already has a real ID, it means we already received it.
              if (existing.external_chat_message_id > 0) return prev;
              // Otherwise, it's our optimistic message! Upgrade it with the incoming data and KEEP _localId.
              const newPrev = [...prev];
              newPrev[matchedOptimisticIndex] = { ...normalizedMsg, _localId: existing._localId };
              return newPrev.sort(compareMessagesNewestFirst);
            }

            return [normalizedMsg, ...prev].sort(compareMessagesNewestFirst);
          });

          if (isAtBottom) {
            setTimeout(() => {
              scrollToBottom();
            }, 150);
          }

          // Background reload thread if it's for the open thread
          if (threadParentMessageRef.current) {
            const isForOpenThread = (incomingMsg.parent_message_id === threadParentMessageRef.current.external_chat_message_id) ||
              (incomingMsg.root_message_id === threadParentMessageRef.current.external_chat_message_id);

            if (isForOpenThread) {
              fetchThreadMessagesRef.current?.(threadParentMessageRef.current.external_chat_message_id);
            }
          }
        }
      } else {
        fetchMessagesRef.current?.(selectedConversationRef.current, false, null, true);
        if (threadParentMessageRef.current) {
          fetchThreadMessagesRef.current?.(threadParentMessageRef.current.external_chat_message_id);
        }
      }

      if (selectedTenantId) {
        try {
          await ChatService.markMessagesAsRead(
            selectedConversationRef.current.external_chat_end_user_id,
            Number(selectedTenantId)
          );
          refetchUnreadCount();
        } catch (_) { }
      }
    });
    return unregister;
  }, [registerMessageListener, selectedTenantId, refetchUnreadCount]);


  useEffect(() => {
    // Deep-link target can arrive via router state (in-app navigation, e.g.
    // the chat toast) or via URL query params (a push notification opening a
    // fresh tab/window has no router state to work with).
    const searchParams = new URLSearchParams(location.search);
    const queryId = searchParams.get("openConversationId");
    const queryChatType = searchParams.get("chatTypeKey") || undefined;
    const queryThreadRootMessageId = searchParams.get("threadRootMessageId");

    const stateId: number | undefined = (location.state as any)?.openConversationId;
    const stateChatType: string | undefined = (location.state as any)?.chatTypeKey;
    const stateThreadRootMessageId: number | undefined = (location.state as any)?.threadRootMessageId;
    const localIdStr: string | null = localStorage.getItem("openConversationId");

    const chatType = stateChatType || queryChatType;
    const threadRootMessageId =
      stateThreadRootMessageId || (queryThreadRootMessageId ? Number(queryThreadRootMessageId) : undefined);

    let targetId: number | undefined = stateId || (queryId ? Number(queryId) : undefined);
    if (!targetId && localIdStr) {
      targetId = Number(localIdStr);
    }

    if (chatType && chatType !== chatTypeFilter) {
      setChatTypeFilter(chatType);
      return;
    }

    if (!targetId || conversations.length === 0) return;

    if (localIdStr) {
      localStorage.removeItem("openConversationId");
    }

    const match = conversations.find(
      (c) => c.external_chat_end_user_id === Number(targetId)
    );
    if (match) {
      if (threadRootMessageId) {
        pendingThreadRootMessageIdRef.current = threadRootMessageId;
      }
      selectConversation(match);

      if (stateId || stateChatType || stateThreadRootMessageId || queryId || queryChatType || queryThreadRootMessageId) {
        routerNavigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, location.search, conversations, routerNavigate, chatTypeFilter]);


  useEffect(() => {
    if ((!filterDropdownOpen && !selectedConversation) || !selectedTenantId || statusOptions.length > 0) return;
    const fetchStatusOptions = async () => {
      try {
        const response = await ChatService.getEndUserStatusOptions(
          Number(selectedTenantId),
        );
        setStatusOptions(response.results || []);
      } catch (err) { }
    };

    fetchStatusOptions();
  }, [filterDropdownOpen, selectedConversation, selectedTenantId, statusOptions.length]);

  useEffect(() => {
    if (selectedConversation?.external_chat_end_user_status?.external_chat_end_user_status_id && statusOptions.length > 0) {
      const match = statusOptions.find(
        (opt) =>
          opt.external_chat_end_user_status_id ===
          selectedConversation.external_chat_end_user_status
            ?.external_chat_end_user_status_id
      );
      if (match) setSelectedStatus(match);
    }
  }, [statusOptions, selectedConversation?.external_chat_end_user_status?.external_chat_end_user_status_id]);

  useEffect(() => {
    if (!filterDropdownOpen || !selectedTenantId) return;
    const fetchDropdownOptions = async () => {
      try {
        const [accRes, platRes] = await Promise.all([
          ChatService.getBotChannelAccounts(Number(selectedTenantId), chatTypeFilter),
          ChatService.getPlatforms(Number(selectedTenantId), chatTypeFilter)
        ]);
        setAccountOptions(accRes?.results || []);
        setPlatformOptions(platRes?.results || []);
      } catch (err) { }
    };

    fetchDropdownOptions();
  }, [filterDropdownOpen, selectedTenantId, chatTypeFilter]);

  useEffect(() => {
    const isContactDetailsOpen = isDetailsOpen && !!selectedConversation;
    const needsUsersOrTeams =
      filterDropdownOpen ||
      showCreateGroupModal ||
      showConvertToContactModal ||
      showAssignedTeamModal ||
      showAssignedUserModal ||
      isContactDetailsOpen;

    if (!needsUsersOrTeams || !selectedTenantId) return;

    const fetchUsersAndTeams = async () => {
      try {
        const promises: Promise<any>[] = [];
        if (tenantUsers.length === 0) {
          promises.push(ChatService.getTenantUsers(Number(selectedTenantId)));
        }
        if (teams.length === 0) {
          promises.push(ChatService.getTeams(Number(selectedTenantId)));
        }
        if (promises.length === 0) return;
        const results = await Promise.all(promises);
        let idx = 0;
        if (tenantUsers.length === 0) {
          setTenantUsers(results[idx]?.results || []);
          idx++;
        }
        if (teams.length === 0) {
          setTeams(results[idx]?.results || []);
        }
      } catch (err) { }
    };
    fetchUsersAndTeams();
  }, [
    filterDropdownOpen,
    showCreateGroupModal,
    showConvertToContactModal,
    showAssignedTeamModal,
    showAssignedUserModal,
    isDetailsOpen,
    selectedConversation,
    selectedTenantId,
    tenantUsers.length,
    teams.length,
  ]);

  useEffect(() => {
    if (!selectedConversation || !selectedTenantId || chatTemplates.length > 0) return;
    const fetchChatTemplates = async () => {
      try {
        const response = await ChatService.getChatTemplateMessages(Number(selectedTenantId));
        setChatTemplates(response.results || []);
      } catch (err) { }
    };
    fetchChatTemplates();
  }, [selectedConversation, selectedTenantId, chatTemplates.length]);

  const selectedConversationTeams = getAssignedTeams(selectedConversation);

  const loadAssignableUsers = async () => {
    const requestId = ++assignableUsersRequestRef.current;
    const teamIds = Array.from(new Set(selectedConversationTeams.map((team) => team.assigned_team_id)));

    if (teamIds.length === 0) {
      setDisplayedUsers(tenantUsers);
      return;
    }

    setDisplayedUsers([]);
    setLoadingAssignableUsers(true);
    try {
      const teamUsersResponses = await Promise.all(teamIds.map((teamId) => ChatService.getTeamUsers(teamId)));
      if (requestId !== assignableUsersRequestRef.current) return;

      const uniqueUsers = new Map<number, any>();
      teamUsersResponses
        .flatMap((res: any) => res?.results || [])
        .forEach((tu: any) => {
          if (tu?.user_id == null || uniqueUsers.has(tu.user_id)) return;
          uniqueUsers.set(tu.user_id, {
            user_id: tu.user_id,
            user_first_name: tu.user_full_name?.split(' ')[0] || '',
            user_last_name: tu.user_full_name?.split(' ').slice(1).join(' ') || '',
            email: tu.email || '',
            full_name: tu.user_full_name || ''
          });
        });
      setDisplayedUsers(Array.from(uniqueUsers.values()));
    } catch (error) {
      if (requestId === assignableUsersRequestRef.current) setDisplayedUsers(tenantUsers);
    } finally {
      if (requestId === assignableUsersRequestRef.current) setLoadingAssignableUsers(false);
    }
  };

  const openAssignUserModal = () => {
    setSelectedAssignedUserId(selectedConversation?.assigned_user?.user_id || null);
    setShowAssignedUserModal(true);
    loadAssignableUsers();
  };

  const fetchContacts = useCallback(async () => {
    if (!selectedTenantId) return;

    setContactsLoading(true);
    try {
      const filters: any = {
        page: contactCurrentPage,
        page_size: contactPageSize,
      };

      if (contactNameFilter.trim()) {
        filters.contact_full_name = contactNameFilter.trim();
      }
      if (contactEmailFilter.trim()) {
        filters.primary_email = contactEmailFilter.trim();
      }
      if (contactCompanyFilter.trim()) {
        filters.company_name = contactCompanyFilter.trim();
      }

      const contactsRes = await ChatService.getContacts(
        Number(selectedTenantId),
        isSalesUser ? Number(userId) : null,
        filters
      );
      setContacts(contactsRes.results || []);
      setTotalContactsCount(contactsRes.count || 0);
    } catch (error) {
      setContacts([]);
      setTotalContactsCount(0);
    } finally {
      setContactsLoading(false);
    }
  }, [selectedTenantId, contactNameFilter, contactEmailFilter, contactCompanyFilter, contactCurrentPage, contactPageSize, userId]);

  useEffect(() => {
    if (!showContactModal) return;

    const debounceTimer = setTimeout(() => {
      fetchContacts();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [showContactModal, contactNameFilter, contactEmailFilter, contactCompanyFilter, contactCurrentPage, fetchContacts]);

  const handleAssignContact = async (contactId: number | null) => {
    if (!selectedConversation || !selectedTenantId) return;

    try {
      await ChatService.updateEndUserAssignments(
        selectedConversation.external_chat_end_user_id,
        Number(selectedTenantId),
        contactId,
        undefined,
      );
      setShowContactModal(false);

      await fetchConversations();

      const updatedConversations = await ChatService.getConversations(
        Number(selectedTenantId),
        userId,
      );
      const updated = updatedConversations.results.find(
        (c) =>
          c.external_chat_end_user_id ===
          selectedConversation.external_chat_end_user_id,
      );
      if (updated) {
        setSelectedConversation(updated);
        selectedConversationRef.current = updated;
      }
      MySwal.fire({
        icon: "success",
        title: "Contact Updated",
        timer: 2000,
      });
    } catch (error) {
      MySwal.fire({
        icon: "error",
        title: "Failed to Update Contact",
        timer: 2000,
      });
    }
  };

  const handleConvertToContactClick = () => {
    if (!selectedConversation) return;
    setConvertContactName((selectedConversation.username || `${selectedConversation.first_name || ""} ${selectedConversation.last_name || ""}`).trim());
    setConvertContactEmail(selectedConversation.end_user_email || "");
    setConvertContactPhone(selectedConversation.end_user_phone_number || "");
    setConvertAssignedUserId(selectedConversation.assigned_user?.user_id || null);
    setConvertAssignedTeamId(getAssignedTeams(selectedConversation)[0]?.assigned_team_id ?? null);
    setConvertEmailError("");
    setConvertPhoneError("");
    setShowConvertToContactModal(true);
    loadAssignableUsers();
  };

  const handleConvertToContact = async () => {
    if (!selectedConversation || !selectedTenantId || !userId) return;

    let hasErrors = false;
    if (convertContactEmail.trim() && !isValidEmail(convertContactEmail)) {
      setConvertEmailError("Please enter a valid email address");
      hasErrors = true;
    } else {
      setConvertEmailError("");
    }

    if (convertContactPhone.trim() && !isValidPhone(convertContactPhone)) {
      setConvertPhoneError("Please enter a valid phone number (7-15 digits)");
      hasErrors = true;
    } else {
      setConvertPhoneError("");
    }

    if (hasErrors) return;

    setConvertingContact(true);
    try {
      const nameParts = convertContactName.trim().split(/\s+/);
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";
      const payload: any = {
        first_name: firstName,
        last_name: lastName,
        tenant_id: Number(selectedTenantId),
        created_by: Number(userId),
        updated_by: Number(userId),
        is_active: true,
      };

      if (convertContactEmail.trim()) {
        payload.primary_email = convertContactEmail.trim();
      }
      if (convertContactPhone.trim()) {
        payload.primary_phone = convertContactPhone.trim();
      }
      if (convertAssignedUserId) {
        payload.assigned_user_id = convertAssignedUserId;
      }
      if (convertAssignedTeamId) {
        payload.assigned_team_id = convertAssignedTeamId;
      }

      const newContact = await ChatService.createContact(payload);

      await ChatService.updateEndUserAssignments(
        selectedConversation.external_chat_end_user_id,
        Number(selectedTenantId),
        newContact.contact_id,
        undefined,
      );

      setShowConvertToContactModal(false);
      setShowContactModal(false);
      setConvertContactEmail("");
      setConvertContactPhone("");
      setConvertAssignedUserId(null);
      setConvertAssignedTeamId(null);

      await fetchConversations();

      const updatedConversations = await ChatService.getConversations(
        Number(selectedTenantId),
        userId,
      );
      const updated = updatedConversations.results.find(
        (c) =>
          c.external_chat_end_user_id ===
          selectedConversation.external_chat_end_user_id,
      );
      if (updated) {
        setSelectedConversation(updated);
        selectedConversationRef.current = updated;
      }

      MySwal.fire({
        icon: "success",
        title: "Contact Created",
        text: "Chat has been converted to a contact successfully.",
        timer: 2000,
      });
    } catch (error) {
      MySwal.fire({
        icon: "error",
        title: "Failed to Create Contact",
        text: "Could not convert chat to contact. Please try again.",
        timer: 2000,
      });
    } finally {
      setConvertingContact(false);
    }
  };

  const handleConvertToContactAndCreateLead = async () => {
    if (!selectedConversation || !selectedTenantId || !userId) return;

    let hasErrors = false;
    if (convertContactEmail.trim() && !isValidEmail(convertContactEmail)) {
      setConvertEmailError("Please enter a valid email address");
      hasErrors = true;
    } else {
      setConvertEmailError("");
    }

    if (convertContactPhone.trim() && !isValidPhone(convertContactPhone)) {
      setConvertPhoneError("Please enter a valid phone number (7-15 digits)");
      hasErrors = true;
    } else {
      setConvertPhoneError("");
    }

    if (hasErrors) return;

    setCreatingContactAndLead(true);
    try {
      const nameParts = convertContactName.trim().split(/\s+/);
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";
      const contactPayload: any = {
        first_name: firstName,
        last_name: lastName,
        tenant_id: Number(selectedTenantId),
        created_by: Number(userId),
        updated_by: Number(userId),
        is_active: true,
      };

      if (convertContactEmail.trim()) contactPayload.primary_email = convertContactEmail.trim();
      if (convertContactPhone.trim()) contactPayload.primary_phone = convertContactPhone.trim();
      if (convertAssignedUserId) contactPayload.assigned_user_id = convertAssignedUserId;
      if (convertAssignedTeamId) contactPayload.assigned_team_id = convertAssignedTeamId;

      const newContact = await ChatService.createContact(contactPayload);

      await ChatService.updateEndUserAssignments(
        selectedConversation.external_chat_end_user_id,
        Number(selectedTenantId),
        newContact.contact_id,
        undefined,
      );

      const firstStageId = await ChatService.getFirstLeadStageId(Number(selectedTenantId));

      const leadTitle = convertContactName.trim() || selectedConversation.username || "";
      const leadPayload: any = {
        title: leadTitle,
        tenant_id: Number(selectedTenantId),
        created_by: Number(userId),
        updated_by: Number(userId),
        primary_contact: newContact.contact_id,
        lifecycle_type_stage_id: firstStageId,
        priority: "Low",
      };
      if (convertAssignedUserId) leadPayload.assigned_user_id = convertAssignedUserId;
      if (convertAssignedTeamId) leadPayload.assigned_team_id = convertAssignedTeamId;
      await ChatService.createLead(leadPayload);

      setShowConvertToContactModal(false);
      setShowContactModal(false);
      setConvertContactName("");
      setConvertContactEmail("");
      setConvertContactPhone("");
      setConvertAssignedUserId(null);
      setConvertAssignedTeamId(null);
      setContactNameFilter("");
      setContactEmailFilter("");
      setContactCompanyFilter("");
      setContactCurrentPage(1);

      await fetchConversations();

      const updatedConversations = await ChatService.getConversations(Number(selectedTenantId), userId);
      const updated = updatedConversations.results.find(
        (c) => c.external_chat_end_user_id === selectedConversation.external_chat_end_user_id,
      );
      if (updated) {
        setSelectedConversation(updated);
        selectedConversationRef.current = updated;
      }

      MySwal.fire({
        icon: "success",
        title: "Contact & Lead Created",
        text: "Contact has been created and a lead has been assigned successfully.",
        timer: 2000,
      });
    } catch (error) {
      MySwal.fire({
        icon: "error",
        title: "Failed to Create Contact & Lead",
        text: "An error occurred. Please try again.",
        timer: 2000,
      });
    } finally {
      setCreatingContactAndLead(false);
    }
  };

  const handleAssignContactAndCreateLead = async (contactId: number | null) => {
    if (!selectedConversation || !selectedTenantId || !userId || !contactId) return;

    setAssigningContactAndLead(true);
    try {
      await ChatService.updateEndUserAssignments(
        selectedConversation.external_chat_end_user_id,
        Number(selectedTenantId),
        contactId,
        undefined,
      );

      const firstStageId = await ChatService.getFirstLeadStageId(Number(selectedTenantId));

      const endUserName = (selectedConversation.username || `${selectedConversation.first_name || ""} ${selectedConversation.last_name || ""}`).trim();
      await ChatService.createLead({
        title: endUserName,
        tenant_id: Number(selectedTenantId),
        created_by: Number(userId),
        updated_by: Number(userId),
        primary_contact: contactId,
        lifecycle_type_stage_id: firstStageId,
        priority: "Low",
      });

      setShowContactModal(false);
      setContactNameFilter("");
      setContactEmailFilter("");
      setContactCompanyFilter("");
      setContactCurrentPage(1);

      await fetchConversations();

      const updatedConversations = await ChatService.getConversations(Number(selectedTenantId), userId);
      const updated = updatedConversations.results.find(
        (c) => c.external_chat_end_user_id === selectedConversation.external_chat_end_user_id,
      );
      if (updated) {
        setSelectedConversation(updated);
        selectedConversationRef.current = updated;
      }

      MySwal.fire({
        icon: "success",
        title: "Contact Assigned & Lead Created",
        timer: 2000,
      });
    } catch (error) {
      MySwal.fire({
        icon: "error",
        title: "Failed to Assign Contact & Create Lead",
        timer: 2000,
      });
    } finally {
      setAssigningContactAndLead(false);
    }
  };

  const handleAssignUser = async (assignUserId: number | null) => {
    if (!selectedConversation || !selectedTenantId || !canAssignTeamAndUser) return;

    try {
      await ChatService.updateEndUserAssignments(
        selectedConversation.external_chat_end_user_id,
        Number(selectedTenantId),
        undefined,
        assignUserId,
      );
      setShowAssignedUserModal(false);

      await fetchConversations();

      const updatedConversations = await ChatService.getConversations(
        Number(selectedTenantId),
        userId,
      );
      const updated = updatedConversations.results.find(
        (c) =>
          c.external_chat_end_user_id ===
          selectedConversation.external_chat_end_user_id,
      );
      if (updated) {
        setSelectedConversation(updated);
        selectedConversationRef.current = updated;
      }
      MySwal.fire({
        icon: "success",
        title: "Assigned User Updated",
        timer: 2000,
      });
    } catch (error) {
      MySwal.fire({
        icon: "error",
        title: "Failed to Update Assigned User",
        timer: 2000,
      });
    }
  };

  const handleAssignTeams = async (assignTeamIds: number[]) => {
    if (!selectedConversation || !selectedTenantId || !canAssignTeamAndUser || updatingTeams) return;

    const teamIds = Array.from(new Set(assignTeamIds));
    setUpdatingTeams(true);

    try {
      await ChatService.updateEndUserAssignments(
        selectedConversation.external_chat_end_user_id,
        Number(selectedTenantId),
        undefined,
        undefined,
        teamIds,
      );
      setShowAssignedTeamModal(false);
      setSelectedAssignedTeamIds([]);
      setRemovedTeamIds([]);

      const updatedTeams: AssignedTeam[] = teamIds.map((teamId) => ({
        assigned_team_id: teamId,
        name:
          selectedConversationTeams.find((team) => team.assigned_team_id === teamId)?.name ??
          teams.find((team: any) => team.team_id === teamId)?.name ??
          null,
      }));
      const locallyUpdated = { ...selectedConversation, assigned_teams: updatedTeams };
      setSelectedConversation(locallyUpdated);
      selectedConversationRef.current = locallyUpdated;

      await fetchConversations();

      const updatedConversations = await ChatService.getConversations(
        Number(selectedTenantId),
        userId,
      );
      const updated = updatedConversations.results.find(
        (c) =>
          c.external_chat_end_user_id ===
          selectedConversation.external_chat_end_user_id,
      );
      if (updated) {
        setSelectedConversation(updated);
        selectedConversationRef.current = updated;
      }
      MySwal.fire({
        icon: "success",
        title: "Assigned Teams Updated",
        timer: 2000,
      });
    } catch (error) {
      MySwal.fire({
        icon: "error",
        title: "Failed to Update Assigned Teams",
        timer: 2000,
      });
    } finally {
      setUpdatingTeams(false);
    }
  };

  const openManageTeamsModal = () => {
    setSelectedAssignedTeamIds([]);
    setRemovedTeamIds([]);
    setShowAssignedTeamModal(true);
  };

  const toggleTeamRemoval = (teamId: number) => {
    setRemovedTeamIds((prev) =>
      prev.includes(teamId) ? prev.filter((id) => id !== teamId) : [...prev, teamId],
    );
  };

  const handleSaveTeams = () => {
    handleAssignTeams([
      ...selectedConversationTeams
        .map((team) => team.assigned_team_id)
        .filter((teamId) => !removedTeamIds.includes(teamId)),
      ...selectedAssignedTeamIds,
    ]);
  };

  const handleRemoveTeam = async (team: AssignedTeam) => {
    const result = await MySwal.fire({
      title: "Remove Team",
      text: `Remove ${team.name || "this team"} from this conversation?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Remove",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    handleAssignTeams(
      selectedConversationTeams
        .map((assignedTeam) => assignedTeam.assigned_team_id)
        .filter((teamId) => teamId !== team.assigned_team_id),
    );
  };



  useEffect(() => {
    fetchConversations(true);
  }, [fetchConversations]);

  useEffect(() => {
    const prevLength = previousMessagesLengthRef.current;
    previousMessagesLengthRef.current = messages.length;

    if (isLazyLoadingRef.current) {
      isLazyLoadingRef.current = false;
      return;
    }

    if (messages.length > prevLength) {
      const t = setTimeout(scrollToBottom, 100);
      return () => clearTimeout(t);
    }
  }, [messages]);


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target as Node)
      ) {
        setFilterDropdownOpen(false);
      }

      if (
        optionsMenuRef.current &&
        !optionsMenuRef.current.contains(event.target as Node)
      ) {
        setOptionsMenuOpen(false);
      }

      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target as Node)
      ) {
        setStatusDropdownOpen(false);
      }

      if (messageMenuOpen !== null) {
        const messageMenuElement = messageMenuRefs.current.get(messageMenuOpen);
        if (
          messageMenuElement &&
          !messageMenuElement.contains(event.target as Node)
        ) {
          setMessageMenuOpen(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    filterDropdownOpen,
    optionsMenuOpen,
    statusDropdownOpen,
    messageMenuOpen,
  ]);

  if (initialLoading) {
    return (
      <>
        <div className="page-wrapper">
          <div className="content">
            {!isContactPage && (
              <div className="page-header">
                <div className="row">
                  <div className="col-8">
                    <h3 className="page-title">Chat</h3>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!isChatbotIntegrated) {
    return (
      <>
        <div className="page-wrapper">
          <div className="content">
            {!isContactPage && (
              <div className="page-header">
                <div className="row">
                  <div className="col-8">
                    <h3 className="page-title">Chat</h3>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px", backgroundColor: "#ffffffff", borderRadius: "50px" }}>
              <div className="text-center" style={{ backgroundColor: "#ffffffff" }}>
                <i className="fa fa-unlink" style={{
                  fontSize: "64px",
                  color: "#000000ff",
                  animation: "floatUpDown 2s ease-in-out infinite"
                }}></i>
                <h4 className="mt-3">Chatbot is <span style={{ color: "#fd590dff" }}>not</span> integrated</h4>
                <p className="text-muted">Please configure chatbot settings in Integration Settings to enable chat functionality.</p>
                <div className="d-flex gap-3 justify-content-center mt-3">
                  <button
                    className="btn btn-primary px-4"
                    onClick={() => routerNavigate(all_routes.chatSettings)}
                  >
                    <i className="ti ti-plug-connected me-2"></i>
                    Integrate External Chat
                  </button>
                  <button
                    className="btn btn-outline-primary px-4"
                    onClick={() => routerNavigate(all_routes.chatSettings)}
                  >
                    <i className="ti ti-plug-connected me-2"></i>
                    Integrate Internal Chat
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const handleMarkAllAsRead = async () => {
    if (chatTypeFilter !== "INTERNAL" || !selectedTenantId) return;
    const currentConvs = chatConversationsRef.current || [];
    const unreadConvs = currentConvs.filter((c: any) => c.unread_count && c.unread_count > 0);
    if (unreadConvs.length === 0) return;

    try {
      const response = await ChatService.getExternalChatTenantBot(Number(selectedTenantId));
      const results: any[] = response?.results || [];
      const internalBot = results.find(
        (bot: any) => bot.is_authorized === true && bot.chat_type?.chat_type_key === "INTERNAL"
      );
      const botId = internalBot?.external_chat_tenant_bot_id;

      if (!botId) return;

      const markResponse = await ChatService.markAllBotMessagesAsRead(Number(selectedTenantId), botId);

      if (markResponse && markResponse.success) {
        MySwal.fire({
          toast: true,
          position: "bottom-end",
          icon: "success",
          title: "All messages are marked as read",
          showConfirmButton: false,
          timer: 3000,
        });
      }

      if (fetchConversationsRef.current) {
        await fetchConversationsRef.current();
      }
      refetchUnreadCount();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      {/* Start Page Content */}
      <div className={!isContactPage ? "page-wrapper" : ""}>
        {/* Start Content */}
        <div className={!isContactPage ? "content" : ""}>
          {!isContactPage && (
            <div className="page-header">
              <div className="row">
                <div className="col-8">
                  <h3 className="page-title">Chat</h3>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="chat-wrapper chat-wrapper">
            {/* --- Left Panel Start --- */}
            <div
              className="chat-sidebar-left"
              data-mobile-panel={mobileActivePanel}
            >
              <div className="chat-sidebar-header">
                <div className="chat-filter-tabs">
                  <button
                    className={`filter-tab ${filterTab === "unread" ? "active" : ""
                      }`}
                    onClick={() => setFilterTab("unread")}
                  >
                    Unread
                  </button>
                  <button
                    className={`filter-tab ${filterTab === "all" ? "active" : ""
                      }`}
                    onClick={() => setFilterTab("all")}
                  >
                    All
                  </button>
                </div>
                <div className="chat-search-wrap">
                  <form onSubmit={(e) => e.preventDefault()}>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fa fa-search" />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {chatTypeFilter === "INTERNAL" && (
                        <div
                          className="position-relative"
                          title={!conversations.some((c: any) => c.unread_count && c.unread_count > 0) ? "You dont have any unread message" : "Mark all messages read"}
                        >
                          <button
                            type="button"
                            className="btn btn-icon position-relative d-inline-flex align-items-center justify-content-center"
                            onClick={handleMarkAllAsRead}
                            style={{
                              marginLeft: "8px",
                              border: "none",
                              ...(!conversations.some((c: any) => c.unread_count && c.unread_count > 0) ? { pointerEvents: "none" } : {})
                            }}
                            disabled={!conversations.some((c: any) => c.unread_count && c.unread_count > 0)}
                          >
                            <i className="bi bi-check-all" style={{ fontSize: "1.5rem", color: conversations.some((c: any) => c.unread_count && c.unread_count > 0) ? "#212529" : "#64748b" }} />
                          </button>
                        </div>
                      )}
                      {/* Filter Button with Dropdown */}
                      <div
                        className="position-relative"
                        ref={filterDropdownRef}
                      >
                        <button
                          type="button"
                          className="btn btn-icon position-relative"
                          onClick={() =>
                            setFilterDropdownOpen(!filterDropdownOpen)
                          }
                        >
                          <i className="fa fa-filter" />
                          {(selectedFilter.length > 0 || teamFilterId || assignedUserFilterId || accountFilter || platformFilter) && (
                            <span
                              className="position-absolute translate-middle badge rounded-pill bg-danger"
                              style={{ top: "8px", right: "-4px", fontSize: "10px", padding: "2px 5px" }}
                            >
                              {[selectedFilter.length > 0, teamFilterId, assignedUserFilterId, accountFilter, platformFilter].filter(Boolean).length}
                            </span>
                          )}
                        </button>
                        {filterDropdownOpen && (
                          <div
                            className="dropdown-menu show"
                            style={{
                              position: "absolute",
                              top: "100%",
                              right: 0,
                              marginTop: "4px",
                              minWidth: "280px",
                              maxHeight: "400px",
                              overflowY: "auto",
                              padding: "8px 0",
                            }}
                          >
                            <div className="px-3 pb-2 mb-2 border-bottom d-flex justify-content-between align-items-center">
                              <span className="fw-semibold">Filters</span>
                              <button
                                type="button"
                                className="btn btn-sm btn-link p-0 text-muted"
                                onClick={() => {
                                  setSelectedFilter([]);
                                  setTeamFilterId("");
                                  setAssignedUserFilterId("");
                                  setAccountFilter("");
                                  setPlatformFilter("");
                                }}
                              >
                                Clear All
                              </button>
                            </div>
                            <div className="filter-section">
                              <div
                                className="filter-section-header px-3 py-2 d-flex justify-content-between align-items-center"
                                style={{ cursor: "pointer", backgroundColor: expandedFilterSections.status ? "#f8f9fa" : "transparent" }}
                                onClick={() => setExpandedFilterSections(prev => ({ ...prev, status: !prev.status }))}
                              >
                                <span className="d-flex align-items-center">
                                  <i className={`ti ti-chevron-${expandedFilterSections.status ? "down" : "right"} me-2`} />
                                  Status
                                </span>
                                {selectedFilter.length > 0 && (
                                  <span className="badge bg-primary">{selectedFilter.length}</span>
                                )}
                              </div>
                              {expandedFilterSections.status && (
                                <div className="filter-section-content px-3 py-2">
                                  <label
                                    className={`dropdown-item rounded mb-1 d-flex align-items-center gap-2 ${selectedFilter.length === 0 ? "active" : ""}`}
                                    style={{ cursor: "pointer" }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedFilter.length === 0}
                                      onChange={() => setSelectedFilter([])}
                                    />
                                    All
                                  </label>
                                  {statusOptions.map((status) => (
                                    <label
                                      key={status.external_chat_end_user_status_id}
                                      className={`dropdown-item rounded mb-1 d-flex align-items-center gap-2 ${selectedFilter.includes(status.external_chat_end_user_status_display_name) ? "active" : ""}`}
                                      style={{ cursor: "pointer" }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selectedFilter.includes(status.external_chat_end_user_status_display_name)}
                                        onChange={() => {
                                          setSelectedFilter(prev =>
                                            prev.includes(status.external_chat_end_user_status_display_name)
                                              ? prev.filter(f => f !== status.external_chat_end_user_status_display_name)
                                              : [...prev, status.external_chat_end_user_status_display_name]
                                          );
                                        }}
                                      />
                                      {status.external_chat_end_user_status_display_name}
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                            {chatTypeFilter !== "INTERNAL" && (
                              <>
                                <div className="filter-section">
                                  <div
                                    className="filter-section-header px-3 py-2 d-flex justify-content-between align-items-center"
                                    style={{ cursor: "pointer", backgroundColor: expandedFilterSections.team ? "#f8f9fa" : "transparent" }}
                                    onClick={() => setExpandedFilterSections(prev => ({ ...prev, team: !prev.team }))}
                                  >
                                    <span className="d-flex align-items-center">
                                      <i className={`ti ti-chevron-${expandedFilterSections.team ? "down" : "right"} me-2`} />
                                      Team
                                    </span>
                                    {teamFilterId && (
                                      <span className="badge bg-primary text-truncate" style={{ maxWidth: "100px" }}>
                                        {teams.find(t => t.team_id === Number(teamFilterId))?.name || "Selected"}
                                      </span>
                                    )}
                                  </div>
                                  {expandedFilterSections.team && (
                                    <div className="filter-section-content px-3 py-2">
                                      <select
                                        className="form-select form-select-sm"
                                        value={teamFilterId}
                                        onChange={(e) => setTeamFilterId(e.target.value ? Number(e.target.value) : "")}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <option value="">All Teams</option>
                                        {teams.map((team) => (
                                          <option key={team.team_id} value={team.team_id}>
                                            {team.name}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}
                                </div>
                                <div className="filter-section">
                                  <div
                                    className="filter-section-header px-3 py-2 d-flex justify-content-between align-items-center"
                                    style={{ cursor: "pointer", backgroundColor: expandedFilterSections.assignedUser ? "#f8f9fa" : "transparent" }}
                                    onClick={() => setExpandedFilterSections(prev => ({ ...prev, assignedUser: !prev.assignedUser }))}
                                  >
                                    <span className="d-flex align-items-center">
                                      <i className={`ti ti-chevron-${expandedFilterSections.assignedUser ? "down" : "right"} me-2`} />
                                      Assigned User
                                    </span>
                                    {assignedUserFilterId && (
                                      <span className="badge bg-primary text-truncate" style={{ maxWidth: "100px" }}>
                                        {tenantUsers.find(u => u.user_id === Number(assignedUserFilterId))?.full_name ||
                                          tenantUsers.find(u => u.user_id === Number(assignedUserFilterId))?.user_first_name || "Selected"}
                                      </span>
                                    )}
                                  </div>
                                  {expandedFilterSections.assignedUser && (
                                    <div className="filter-section-content px-3 py-2">
                                      <select
                                        className="form-select form-select-sm"
                                        value={assignedUserFilterId}
                                        onChange={(e) => setAssignedUserFilterId(e.target.value ? Number(e.target.value) : "")}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <option value="">All Users</option>
                                        {tenantUsers.map((user) => (
                                          <option key={user.user_id} value={user.user_id}>
                                            {user.full_name || `${user.user_first_name} ${user.user_last_name}`}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                            <div className="filter-section">
                              <div
                                className="filter-section-header px-3 py-2 d-flex justify-content-between align-items-center"
                                style={{ cursor: "pointer", backgroundColor: expandedFilterSections.account ? "#f8f9fa" : "transparent" }}
                                onClick={() => setExpandedFilterSections(prev => ({ ...prev, account: !prev.account }))}
                              >
                                <span className="d-flex align-items-center">
                                  <i className={`ti ti-chevron-${expandedFilterSections.account ? "down" : "right"} me-2`} />
                                  Account
                                </span>
                                {accountFilter && (
                                  <span
                                    className="badge bg-primary"
                                    title={accountOptions.find(a => String(a.external_chat_bot_channel_account_id) === accountFilter)?.account_name || accountFilter}
                                  >
                                    {(accountOptions.find(a => String(a.external_chat_bot_channel_account_id) === accountFilter)?.account_name || accountFilter).length > 25
                                      ? (accountOptions.find(a => String(a.external_chat_bot_channel_account_id) === accountFilter)?.account_name || accountFilter).substring(0, 25) + '...'
                                      : (accountOptions.find(a => String(a.external_chat_bot_channel_account_id) === accountFilter)?.account_name || accountFilter)
                                    }
                                  </span>
                                )}
                              </div>
                              {expandedFilterSections.account && (
                                <div className="filter-section-content px-3 py-2">
                                  <select
                                    className="form-select form-select-sm"
                                    value={accountFilter}
                                    onChange={(e) => setAccountFilter(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <option value="">All Accounts</option>
                                    {accountOptions.map((account) => (
                                      <option
                                        key={account.external_chat_bot_channel_account_id}
                                        value={account.external_chat_bot_channel_account_id}
                                        title={account.account_name}
                                      >
                                        {(account.account_name || "").length > 30 ? (account.account_name || "").substring(0, 30) + '...' : account.account_name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                            <div className="filter-section">
                              <div
                                className="filter-section-header px-3 py-2 d-flex justify-content-between align-items-center"
                                style={{ cursor: "pointer", backgroundColor: expandedFilterSections.platform ? "#f8f9fa" : "transparent" }}
                                onClick={() => setExpandedFilterSections(prev => ({ ...prev, platform: !prev.platform }))}
                              >
                                <span className="d-flex align-items-center">
                                  <i className={`ti ti-chevron-${expandedFilterSections.platform ? "down" : "right"} me-2`} />
                                  Platform
                                </span>
                                {platformFilter && (
                                  <span
                                    className="badge bg-primary"
                                    title={platformOptions.find(p => String(p.external_chat_platform_id) === platformFilter)?.external_chat_platform_display_name || platformFilter}
                                  >
                                    {(platformOptions.find(p => String(p.external_chat_platform_id) === platformFilter)?.external_chat_platform_display_name || platformFilter).length > 25
                                      ? (platformOptions.find(p => String(p.external_chat_platform_id) === platformFilter)?.external_chat_platform_display_name || platformFilter).substring(0, 25) + '...'
                                      : (platformOptions.find(p => String(p.external_chat_platform_id) === platformFilter)?.external_chat_platform_display_name || platformFilter)
                                    }
                                  </span>
                                )}
                              </div>
                              {expandedFilterSections.platform && (
                                <div className="filter-section-content px-3 py-2">
                                  <select
                                    className="form-select form-select-sm"
                                    value={platformFilter}
                                    onChange={(e) => setPlatformFilter(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <option value="">All Platforms</option>
                                    {platformOptions.map((platform) => (
                                      <option
                                        key={platform.external_chat_platform_id}
                                        value={platform.external_chat_platform_id}
                                        title={platform.external_chat_platform_display_name}
                                      >
                                        {(platform.external_chat_platform_display_name || "").length > 30 ? (platform.external_chat_platform_display_name || "").substring(0, 30) + '...' : platform.external_chat_platform_display_name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="results-info mt-2">
                    <i className="ti ti-check-square" />{" "}
                    {filteredConversations.length} RESULTS
                  </div>
                  {!isContactPage && (
                    <div className="d-flex align-items-center mt-2">
                      <div
                        className="d-flex align-items-center position-relative"
                        style={{
                          backgroundColor: "#f1f5f9",
                          borderRadius: "50px",
                          padding: "2px",
                          boxShadow: "inset 0 1px 3px rgba(0,0,0,0.06)",
                          display: "inline-flex",
                          width: "140px",
                          height: "28px"
                        }}
                      >
                        <div
                          className="position-absolute"
                          style={{
                            top: "2px",
                            bottom: "2px",
                            left: chatTypeFilter === "INTERNAL" ? "2px" : "50%",
                            width: "calc(50% - 2px)",
                            backgroundColor: "#fff",
                            borderRadius: "50px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                            transition: "all 0.3s ease",
                            zIndex: 1
                          }}
                        />
                        <div
                          style={{
                            flex: 1,
                            textAlign: "center",
                            fontWeight: chatTypeFilter === "INTERNAL" ? 600 : 500,
                            color: chatTypeFilter === "INTERNAL" ? "#0f172a" : "#64748b",
                            cursor: "pointer",
                            fontSize: "0.65rem",
                            position: "relative",
                            zIndex: 2,
                            transition: "color 0.3s ease"
                          }}
                          onClick={() => setChatTypeFilter("INTERNAL")}
                        >
                          Internal
                        </div>
                        <div
                          style={{
                            flex: 1,
                            textAlign: "center",
                            fontWeight: chatTypeFilter === "EXTERNAL" ? 600 : 500,
                            color: chatTypeFilter === "EXTERNAL" ? "#0f172a" : "#64748b",
                            cursor: "pointer",
                            fontSize: "0.65rem",
                            position: "relative",
                            zIndex: 2,
                            transition: "color 0.3s ease"
                          }}
                          onClick={() => setChatTypeFilter("EXTERNAL")}
                        >
                          External
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <OverlayScrollbarsComponent
                style={{ height: "calc(100vh - 250px)", width: "100%" }}
                events={{
                  scroll: (instance) => {
                    const { scrollTop, scrollHeight, clientHeight } = instance.elements().scrollOffsetElement;
                    if (scrollHeight - scrollTop - clientHeight <= 100) {
                      loadMoreConversations();
                    }
                  }
                }}
              >
                <div className="chat-conversations-list">
                  {error && (
                    <div className="text-center p-3 text-danger">{error}</div>
                  )}
                  {!error && conversationsFirstLoading && (
                    <div className="d-flex justify-content-center align-items-center" style={{ padding: "40px 0" }}>
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                  {!error && !conversationsFirstLoading && chatTypeFilter === "EXTERNAL" && !hasAuthorizedExternalBot && (
                    <div className="text-center p-4">
                      <i className="ti ti-plug-connected-x" style={{ fontSize: "48px", color: "#ccc" }}></i>
                      <p className="text-muted mt-2 mb-3">No authorized external chat bot found.</p>
                      <button
                        className="btn btn-primary btn-sm px-4"
                        onClick={() => routerNavigate(all_routes.chatSettings)}
                      >
                        <i className="ti ti-plug-connected me-1"></i>
                        Integrate External Chat
                      </button>
                    </div>
                  )}
                  {!error && !conversationsFirstLoading && chatTypeFilter === "INTERNAL" && !hasAuthorizedInternalBot && (
                    <div className="text-center p-4">
                      <i className="ti ti-plug-connected-x" style={{ fontSize: "48px", color: "#ccc" }}></i>
                      <p className="text-muted mt-2 mb-3">No authorized internal chat bot found.</p>
                      <button
                        className="btn btn-primary btn-sm px-4"
                        onClick={() => routerNavigate(all_routes.chatSettings)}
                      >
                        <i className="ti ti-plug-connected me-1"></i>
                        Integrate Internal Chat
                      </button>
                    </div>
                  )}
                  {!error && !conversationsFirstLoading && filteredConversations.length === 0 && ((chatTypeFilter === "EXTERNAL" && hasAuthorizedExternalBot) || (chatTypeFilter === "INTERNAL" && hasAuthorizedInternalBot)) && (
                    <div className="text-center p-3">
                      No conversations found
                    </div>
                  )}
                  {!error && !conversationsFirstLoading &&
                    ((chatTypeFilter === "EXTERNAL" && hasAuthorizedExternalBot) || (chatTypeFilter === "INTERNAL" && hasAuthorizedInternalBot)) &&
                    filteredConversations.map((conversation) => (
                      <div
                        key={conversation.external_chat_end_user_id}
                        className={`conversation-item ${selectedConversation?.external_chat_end_user_id ===
                          conversation.external_chat_end_user_id
                          ? "active"
                          : ""
                          }`}
                        onClick={() => selectConversation(conversation)}
                      >
                        <div className="conversation-avatar-wrapper">
                          <div className="avatar avatar-md">
                            {(() => {
                              const platformKey = getConversationPlatformKey(conversation);
                              const isGoogleChat = platformKey === "GOOGLE_CHAT";
                              const chatType = conversation.external_chat_bot_channel_account?.chat_type;

                              if (isGoogleChat && chatType === "SPACE") {
                                const spaceName = conversation.external_chat_bot_channel_account?.account_name || "Space";
                                const initial = spaceName.charAt(0).toUpperCase();
                                const bgColor = getAvatarColor(spaceName);
                                return (
                                  <div
                                    className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                                    style={{ width: '100%', height: '100%', backgroundColor: bgColor, fontSize: '18px' }}
                                  >
                                    {initial}
                                  </div>
                                );
                              }

                              if (conversation.end_user_profile_image_url) {
                                return (
                                  <img
                                    src={conversation.end_user_profile_image_url || dummy}
                                    className="rounded-circle"
                                    alt="image"
                                    onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                                  />
                                );
                              }

                              if (conversation.contact?.avatar_url) {
                                return (
                                  <img
                                    src={conversation.contact.avatar_url || dummy}
                                    className="rounded-circle"
                                    alt="image"
                                    onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                                  />
                                );
                              }

                              return (
                                <img
                                  src={dummy}
                                  className="rounded-circle"
                                  alt="image"
                                  onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                                />
                              );
                            })()}
                            {/* Source Icon Badge */}
                            {(() => {
                              const platformKey = conversation.external_chat_tenant_bot?.external_chat_platform?.external_chat_platform_key;
                              const isEngati = !platformKey || platformKey === 'ENGATI';
                              if (isEngati) {
                                const channelKey = getConversationChannelKey(conversation);
                                return (
                                  <span
                                    className={`source-badge source-${channelKey.toLowerCase()}`}
                                  >
                                    <i
                                      className={getChannelIcon(channelKey)}
                                      style={{
                                        color: getChannelColor(channelKey),
                                      }}
                                    />
                                  </span>
                                );
                              }
                              const platformIconMap: Record<string, string> = {
                                LAZADA: 'Lazada',
                                TIKTOK: 'TikTok',
                                GOOGLE_CHAT: 'Google-Chat',
                              };
                              const iconFileName = platformIconMap[platformKey] || (platformKey.charAt(0).toUpperCase() + platformKey.slice(1).toLowerCase());
                              return (
                                <span
                                  className="source-badge"
                                  style={{ background: 'rgba(255,255,255,0.9)', padding: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                  <img
                                    src={`/assets/img/platform-icons/${iconFileName}.png`}
                                    alt={platformKey}
                                    style={{ width: '30px', height: '30px', objectFit: 'contain' }}
                                    onError={(e) => {
                                      const target = e.currentTarget;
                                      target.style.display = 'none';
                                      const parent = target.parentElement;
                                      if (parent) {
                                        const fallback = document.createElement('i');
                                        fallback.className = 'fa-solid fa-globe';
                                        fallback.style.color = '#666666';
                                        parent.appendChild(fallback);
                                      }
                                    }}
                                  />
                                </span>
                              );
                            })()}


                          </div>
                        </div>
                        <div className="conversation-info">
                          <div className="conversation-header">
                            <h6 className="conversation-name">
                              {conversation.username ||
                                `${conversation.first_name} ${conversation.last_name}`}
                            </h6>
                            <span className="conversation-time">
                              {conversation.last_message
                                ? formatConversationDate(
                                  conversation.last_message?.created_datetime,
                                )
                                : ""}
                            </span>
                          </div>
                          <div className="conversation-preview d-flex justify-content-between align-items-center w-100">
                            <p className="mb-0 text-truncate" style={{ flex: 1, paddingRight: '8px' }}>
                              {(() => {
                                if (!conversation.last_message) return "No messages yet";
                                if (isHiddenChatMessage(conversation.last_message)) return "";
                                const pkt = conversation.last_message.external_chat_packet_type?.external_chat_packet_type_key;
                                const val = conversation.last_message.external_chat_message_value || "";
                                switch (pkt) {
                                  case "AUDIO": return "Audio";
                                  case "VIDEO": return "Video";
                                  case "IMAGE": return "Image";
                                  case "PDF": return "PDF File";
                                  case "FILE":
                                  case "FILE_RECEIVED":
                                  case "APPLICATION":
                                  case "DOCUMENT": return "File";
                                  case "OPTIONS": return "Options";
                                  case "CAROUSEL": return "Carousel";
                                  case "TEXT":
                                  case "POSTBACK":
                                  case "IDENTITY":
                                    return val.substring(0, 50);
                                  default:
                                    return `${conversation.last_message.external_chat_packet_type?.external_chat_packet_type_display_name ?? ''}`;
                                }
                              })()}
                            </p>
                            {conversation.is_group && (
                              <i className="ti ti-users text-primary flex-shrink-0" style={{ fontSize: '14px' }} title="Group Chat" />
                            )}
                            {(conversation.unread_count ?? 0) > 0 && (
                              <span className="unread-badge">
                                {conversation.unread_count}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  {loadingMoreConversations && (
                    <div className="d-flex justify-content-center py-3">
                      <span className="spinner-border spinner-border-sm text-primary" role="status" />
                    </div>
                  )}
                </div>
              </OverlayScrollbarsComponent>
              {chatTypeFilter === "INTERNAL" && hasAuthorizedInternalBot && (
                <div className="new-chat-button-container">
                  <button
                    className="btn btn-primary new-chat-btn"
                    onClick={() => {
                      setNewChatError("");
                      setShowNewChatModal(true);
                    }}
                  >
                    <i className="fa fa-plus me-1" />
                    New Chat
                  </button>
                </div>
              )}
            </div>
            {/* --- Left Panel End --- */}
            {/* End Left Panel */}

            {/* --- Center Panel (Chat Window) Start --- */}
            <div
              className="chat-main-content"
              data-mobile-panel={mobileActivePanel}
            >
              {selectedConversation ? (
                <>
                  <div className="chat-header">
                    <div className="chat-header-left">
                      <div className="mobile-back-btn">
                        <button
                          className="text-muted chat-close me-2"
                          onClick={handleMobileBackToConversations}
                          type="button"
                        >
                          <i className="ti ti-circle-arrow-left" />
                        </button>
                      </div>
                      <div className="avatar flex-shrink-0 me-2" style={{ position: 'relative' }}>
                        {(() => {
                          const platformKey = getConversationPlatformKey(selectedConversation);
                          const isGoogleChat = platformKey === "GOOGLE_CHAT";
                          const chatType = selectedConversation.external_chat_bot_channel_account?.chat_type;

                          if (isGoogleChat && chatType === "SPACE") {
                            const spaceName = selectedConversation.external_chat_bot_channel_account?.account_name || "Space";
                            const initial = spaceName.charAt(0).toUpperCase();
                            const bgColor = getAvatarColor(spaceName);
                            return (
                              <div
                                className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                                style={{ width: '100%', height: '100%', backgroundColor: bgColor, fontSize: '18px', cursor: 'pointer' }}
                                onClick={handleHeaderClick}
                              >
                                {initial}
                              </div>
                            );
                          }

                          if (selectedConversation.end_user_profile_image_url) {
                            return (
                              <img
                                src={selectedConversation.end_user_profile_image_url || dummy}
                                className="rounded-circle"
                                alt="image"
                                onClick={handleHeaderClick}
                                style={{ cursor: 'pointer' }}
                                onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                              />
                            );
                          }

                          if (selectedConversation.contact?.avatar_url) {
                            return (
                              <img
                                src={selectedConversation.contact.avatar_url || dummy}
                                className="rounded-circle"
                                alt="image"
                                onClick={handleHeaderClick}
                                style={{ cursor: 'pointer' }}
                                onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                              />
                            );
                          }

                          return (
                            <img
                              src={dummy}
                              className="rounded-circle"
                              alt="image"
                              onClick={handleHeaderClick}
                              style={{ cursor: 'pointer' }}
                              onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                            />
                          );
                        })()}
                      </div>

                      <div className="chat-user-info">
                        <h6
                          className={`mb-0 ${!isContactPage ? "chat-user-name" : ""}`}
                          onClick={(e) => {
                            const platformKey = getConversationPlatformKey(selectedConversation);
                            const isDM = selectedConversation?.external_chat_bot_channel_account?.chat_type === "DIRECT_MESSAGE";
                            if (chatTypeFilter === "INTERNAL" && platformKey === "GOOGLE_CHAT" && isDM) {
                              e.stopPropagation();
                              setEditingNameType("header");
                              setTempNameValue(selectedConversation.username || `${selectedConversation.first_name} ${selectedConversation.last_name}`);
                            } else {
                              handleHeaderClick();
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          {editingNameType === "header" ? (
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={tempNameValue}
                              onChange={(e) => setTempNameValue(e.target.value)}
                              onBlur={() => handleNameSave("header", undefined, tempNameValue)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleNameSave("header", undefined, tempNameValue);
                                if (e.key === "Escape") setEditingNameType(null);
                              }}
                              autoFocus
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <>
                              {selectedConversation.username ||
                                `${selectedConversation.first_name} ${selectedConversation.last_name}`}
                              {selectedConversation.is_group && (
                                <i className="ti ti-users ms-2 text-primary" title="Group Chat" />
                              )}
                              {!isContactPage && (
                                <i
                                  className={`ti ${isDetailsOpen && rightPanelMode === "DEFAULT" ? "ti-eye-off" : "ti-info-circle"
                                    } ms-2`}
                                  style={{ fontSize: "16px", cursor: "pointer" }}
                                  title={
                                    isDetailsOpen && rightPanelMode === "DEFAULT" ? "Hide details" : "Show details"
                                  }
                                />
                              )}
                            </>
                          )}
                        </h6>
                      </div>
                    </div>
                    <div className="chat-header-right">
                      <ul className="list-unstyled d-flex align-items-center mb-0">
                        <li className="position-relative d-md-none ms-2" ref={optionsMenuRef}>
                          <button
                            className="btn btn-sm"
                            onClick={() => setOptionsMenuOpen(!optionsMenuOpen)}
                            style={{
                              borderRadius: "2px",
                              padding: "2px 4px",
                            }}
                          >
                            <i className="bi bi-three-dots-vertical text-primary fs-3"></i>
                          </button>
                          {optionsMenuOpen && (
                            <div
                              className="dropdown-menu show dropdown-menu-end"
                              style={{
                                position: "absolute",
                                top: "100%",
                                right: 0,
                                marginTop: "4px",
                                minWidth: "200px",
                                zIndex: 1000,
                              }}
                            >
                              {selectedConversation && chatTypeFilter !== "INTERNAL" && (
                                <>
                                  <button
                                    className="dropdown-item d-flex align-items-center gap-2 text-primary fw-semibold mb-2"
                                    onClick={() => {
                                      setOptionsMenuOpen(false);
                                      setShowCreateGroupModal(true);
                                    }}
                                  >
                                    <i className={selectedConversation.is_group ? "ti ti-user-plus" : "ti ti-users"} />
                                    {selectedConversation.is_group ? "Manage Member" : "Create Group"}
                                  </button>
                                  <div className="dropdown-divider" />
                                </>
                              )}
                              {(() => {
                                const platformKey = getConversationPlatformKey(selectedConversation);
                                if (platformKey === "GOOGLE_CHAT") return null;
                                return (
                                  <div className="mobile-status-dropdown">
                                    <div className="dropdown-item-label px-3 py-2 text-muted fw-bold" style={{ fontSize: '11px', textTransform: 'uppercase' }}>
                                      Change Status
                                    </div>
                                    {statusOptions.map((statusOption) => (
                                      <button
                                        key={
                                          statusOption.external_chat_end_user_status_id
                                        }
                                        className={`dropdown-item ${selectedStatus?.external_chat_end_user_status_id ===
                                          statusOption.external_chat_end_user_status_id
                                          ? "active"
                                          : ""
                                          }`}
                                        onClick={async () => {
                                          if (selectedConversation) {
                                            setIsStatusUpdating(statusOption.external_chat_end_user_status_id);
                                            try {
                                              await ChatService.updateEndUserStatus(
                                                selectedConversation.external_chat_end_user_id,
                                                statusOption.external_chat_end_user_status_id,
                                                Number(selectedTenantId),
                                                Number(userId),
                                              );
                                              setSelectedStatus(statusOption);
                                              setStatusDropdownOpen(false);
                                              setOptionsMenuOpen(false);
                                              MySwal.fire({
                                                icon: "success",
                                                title: "Status Updated",
                                                timer: 2000,
                                              });
                                              fetchConversations();
                                              fetchMessages(selectedConversation);
                                            } catch (err) {
                                              MySwal.fire({
                                                icon: "error",
                                                title: "Failed",
                                                text: "Could not update status.",
                                                timer: 2000,
                                              });
                                            } finally {
                                              setIsStatusUpdating(null);
                                            }
                                          }
                                        }}
                                        disabled={isStatusUpdating !== null}
                                      >
                                        {
                                          statusOption.external_chat_end_user_status_display_name
                                        }
                                        {isStatusUpdating === statusOption.external_chat_end_user_status_id ? (
                                          <div className="spinner-border spinner-border-sm text-primary ms-2" role="status" />
                                        ) : selectedStatus?.external_chat_end_user_status_id ===
                                          statusOption.external_chat_end_user_status_id ? (
                                          <i className="ti ti-check ms-2" />
                                        ) : null}
                                      </button>
                                    ))}
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </li>
                        {(getConversationPlatformKey(selectedConversation) === "ENGATI" || getConversationChannelKey(selectedConversation) === "ENGATI") && selectedConversation?.is_chat_transferred === false && selectedConversation?.external_chat_end_user_status?.external_chat_end_user_status_key !== "CLOSE" && (
                          <li className="position-relative me-2">
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={async () => {
                                setIsTransferringChat(true);
                                try {
                                  await ChatService.transferChatToAgent(
                                    Number(selectedTenantId),
                                    selectedConversation.external_chat_end_user_id,
                                    selectedConversation.external_chat_tenant_bot?.external_chat_tenant_bot_id || 0
                                  );
                                  MySwal.fire({
                                    icon: "success",
                                    title: "Transferred",
                                    text: "Chat has been transferred to agent successfully.",
                                    timer: 2000,
                                  });
                                  await fetchConversations();
                                  await fetchMessages(selectedConversation);
                                } catch (err: any) {
                                  let errorMessage = "Could not transfer chat.";
                                  if (err?.message) {
                                    if (err.message.includes("Failed to transfer chat: ")) {
                                      const jsonStr = err.message.replace("Failed to transfer chat: ", "");
                                      try {
                                        const parsed = JSON.parse(jsonStr);
                                        errorMessage = parsed.error || err.message;
                                      } catch (e) {
                                        errorMessage = err.message;
                                      }
                                    } else {
                                      errorMessage = err.message;
                                    }
                                  }
                                  MySwal.fire({
                                    icon: "error",
                                    title: "Failed",
                                    text: errorMessage,
                                  });
                                } finally {
                                  setIsTransferringChat(false);
                                }
                              }}
                              disabled={isTransferringChat}
                              style={{
                                borderRadius: "6px",
                                padding: "6px 12px",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                fontSize: "14px",
                              }}
                            >
                              {isTransferringChat ? (
                                <div className="spinner-border spinner-border-sm text-white" role="status" />
                              ) : (
                                <i className="ti ti-headset" />
                              )}
                              Transfer to Agent
                            </button>
                          </li>
                        )}
                        {(() => {
                          const platformKey = getConversationPlatformKey(selectedConversation);
                          if (platformKey === "GOOGLE_CHAT") return null;
                          return (
                            <li
                              className="position-relative desktop-status-dropdown"
                              ref={statusDropdownRef}
                            >
                              <button
                                className="btn btn-sm"
                                onClick={() =>
                                  setStatusDropdownOpen(!statusDropdownOpen)
                                }
                                style={{
                                  backgroundColor: "#f5f5f5",
                                  border: "1px solid #e0e0e0",
                                  borderRadius: "6px",
                                  padding: "6px 12px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  color: "#333",
                                  fontSize: "14px",
                                }}
                              >
                                {selectedStatus?.external_chat_end_user_status_display_name ||
                                  "Select Status"}
                                <i
                                  className="fa fa-chevron-down"
                                  style={{ fontSize: "12px" }}
                                />
                              </button>
                              {statusDropdownOpen && (
                                <div
                                  className="dropdown-menu show"
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    right: 0,
                                    marginTop: "4px",
                                    minWidth: "200px",
                                    zIndex: 1000,
                                  }}
                                >
                                  <div className="dropdown-item-label px-3 py-2 text-muted fw-bold" style={{ fontSize: '11px', textTransform: 'uppercase' }}>
                                    Change Status
                                  </div>
                                  {statusOptions.map((statusOption) => (
                                    <button
                                      key={
                                        statusOption.external_chat_end_user_status_id
                                      }
                                      className={`dropdown-item d-flex align-items-center justify-content-between ${selectedStatus?.external_chat_end_user_status_id ===
                                        statusOption.external_chat_end_user_status_id
                                        ? "active"
                                        : ""
                                        }`}
                                      onClick={async () => {
                                        if (selectedConversation) {
                                          setIsStatusUpdating(statusOption.external_chat_end_user_status_id);
                                          try {
                                            await ChatService.updateEndUserStatus(
                                              selectedConversation.external_chat_end_user_id,
                                              statusOption.external_chat_end_user_status_id,
                                              Number(selectedTenantId),
                                              Number(userId),
                                            );
                                            setSelectedStatus(statusOption);
                                            setStatusDropdownOpen(false);
                                            MySwal.fire({
                                              icon: "success",
                                              title: "Status Updated",
                                              timer: 2000,
                                            });
                                            fetchConversations();
                                            fetchMessages(selectedConversation);
                                          } catch (err) {
                                            MySwal.fire({
                                              icon: "error",
                                              title: "Failed",
                                              text: "Could not update status.",
                                              timer: 2000,
                                            });
                                          } finally {
                                            setIsStatusUpdating(null);
                                          }
                                        }
                                      }}
                                      disabled={isStatusUpdating !== null}
                                    >
                                      {
                                        statusOption.external_chat_end_user_status_display_name
                                      }
                                      {isStatusUpdating === statusOption.external_chat_end_user_status_id ? (
                                        <div className="spinner-border spinner-border-sm text-primary ms-2" role="status" />
                                      ) : selectedStatus?.external_chat_end_user_status_id ===
                                        statusOption.external_chat_end_user_status_id ? (
                                        <i className="ti ti-check" />
                                      ) : null}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </li>
                          );
                        })()}
                        {selectedConversation && chatTypeFilter !== "INTERNAL" && (
                          <li className="position-relative d-none d-md-flex align-items-center">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => setShowCreateGroupModal(true)}
                              style={{
                                borderRadius: "6px",
                                padding: "6px 12px",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                fontSize: "14px",
                                marginLeft: "8px"
                              }}
                            >
                              <i className={selectedConversation.is_group ? "ti ti-user-plus" : "ti ti-users"} />
                              {selectedConversation.is_group ? "Manage Member" : "Create Group"}
                            </button>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>

                  {/* Message Channel Tabs */}
                  <div className="message-channel-tabs">
                    <button className="channel-tab active">
                      {(() => {
                        const platformKey = selectedConversation.external_chat_tenant_bot?.external_chat_platform?.external_chat_platform_key;
                        const isEngati = !platformKey || platformKey === 'ENGATI';
                        if (isEngati) {
                          const channelKey = getConversationChannelKey(selectedConversation);
                          return (
                            <i
                              className={`${getChannelIcon(channelKey)} me-1`}
                              style={{ color: getChannelColor(channelKey) }}
                            />
                          );
                        }
                        const platformIconMap: Record<string, string> = {
                          LAZADA: 'Lazada',
                          TIKTOK: 'TikTok',
                          GOOGLE_CHAT: 'Google-Chat',
                        };
                        const iconFileName = platformIconMap[platformKey] || (platformKey.charAt(0).toUpperCase() + platformKey.slice(1).toLowerCase());
                        return (
                          <img
                            src={`/assets/img/platform-icons/${iconFileName}.png`}
                            alt={platformKey}
                            style={{ width: '18px', height: '18px', objectFit: 'contain', marginRight: '6px' }}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        );
                      })()}
                      {selectedConversation.external_chat_bot_channel_account?.account_name ||
                        selectedConversation.external_chat_channel?.external_chat_channel_display_name ||
                        selectedConversation.last_message?.external_chat_channel
                          ?.external_chat_channel_display_name ||
                        selectedConversation.external_chat_channel?.external_chat_channel_key ||
                        selectedConversation.external_chat_channel_key}
                    </button>
                  </div>

                  {/* Chat Body */}
                  <div className="chat-body">
                    <OverlayScrollbarsComponent
                      ref={osRef}
                      style={{ height: "100%", width: "100%" }}
                      events={{
                        scroll: (instance) => {
                          const { scrollOffsetElement } = instance.elements();
                          const { scrollTop } = scrollOffsetElement;

                          if (scrollTop > 100) {
                            isInitialScrollRef.current = false;
                          }

                          if (scrollTop <= 50 && nextMessagesUrl && !loadingOlderMessages && selectedConversation && !isInitialScrollRef.current) {
                            fetchMessages(selectedConversation, false, nextMessagesUrl);
                          }
                        }
                      }}

                    >
                      <div className="messages-container" style={{ paddingTop: '60px' }}>
                        <div ref={messagesEndRef} />
                        {messagesLoading && (
                          <div className="d-flex justify-content-center align-items-center" style={{ padding: "60px 0" }}>
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </div>
                        )}
                        {!messagesLoading && visibleMessages.length === 0 && (
                          <div className="text-center p-3">No messages yet</div>
                        )}
                        {!messagesLoading && visibleMessages.map((message) => {
                          const packetType = message.external_chat_packet_type?.external_chat_packet_type_key;
                          const isChatStatus = (packetType as any) === "CHAT_STATUS";
                          const isAgentClosed = message.external_chat_message_value === "Agent closed the Chat";
                          const isAgentOpened = message.external_chat_message_value === "Agent opened the Chat";
                          const isUserJoined = message.external_chat_message_value === "User joined the chat" || message.external_chat_message_value === "User re-joined the chat";

                          if (isChatStatus && (isAgentClosed || isAgentOpened || isUserJoined)) {
                            const statusText = isAgentClosed ? "Closed" : (isAgentOpened ? "Opened" : "Joined");
                            const senderName = isUserJoined
                              ? (message.external_chat_end_user?.username || "User")
                              : getAgentOrBotName(message, "Agent");
                            const dateTime = new Date(message.created_datetime).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            });

                            return (
                              <div key={message._localId ?? message.external_chat_message_id} className="chat-date-divider my-3">
                                <span>Chat {statusText} by {senderName} at {dateTime}</span>
                              </div>
                            );
                          }

                          if (packetType === "USER_ADDED" || packetType === "USER_REMOVED") {
                            const dateTime = new Date(message.created_datetime).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            });

                            return (
                              <div key={message._localId ?? message.external_chat_message_id} className="chat-date-divider my-3">
                                <span>{message.external_chat_message_value} at {dateTime}</span>
                              </div>
                            );
                          }

                          return (() => {
                            const isAgentPacket = message.external_chat_platform_packet_type?.external_chat_platform_packet_type_key === "AGENT_MESSAGE";
                            const isUserPacket = message.external_chat_platform_packet_type?.external_chat_platform_packet_type_key === "USER_MESSAGE";
                            const isOtherAgentInGroup = selectedConversation?.is_group && isAgentPacket && message.sender?.sender_id !== Number(userId);
                            const isAlignLeft = isUserPacket || isOtherAgentInGroup;

                            const resolvedRole = selectedConversation?.is_group
                              ? (groupMembers.find(m => m.user_id === message.sender?.sender_id || m.user?.user_id === message.sender?.sender_id)?.user_role || message.sender?.user_role)
                              : message.sender?.user_role;

                            return (
                              <div
                                key={message._localId ?? message.external_chat_message_id}
                                className={`message-item ${!isAlignLeft ? "message-sent" : "message-received"}`}
                                style={{ position: 'relative', zIndex: hoveredMessageId === message.external_chat_message_id ? 1000 : 1 }}
                              >
                                {isAlignLeft && (
                                  <div className="message-avatar">
                                    {(() => {
                                      if (isOtherAgentInGroup && message.sender) {
                                        return (
                                          <img
                                            src={message.sender.avatar_url || dummy}
                                            className="rounded-circle"
                                            alt="image"
                                            onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                                          />
                                        );
                                      }
                                      const platformKey = getConversationPlatformKey(selectedConversation);
                                      const isGoogleChat = platformKey === "GOOGLE_CHAT";
                                      const chatType = selectedConversation.external_chat_bot_channel_account?.chat_type;

                                      if (isGoogleChat && chatType === "SPACE") {
                                        return (
                                          <img
                                            src={message.external_sender_info?.member_profile_url || dummy}
                                            className="rounded-circle"
                                            alt="image"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => handleSenderClick({
                                              name: message.external_sender_info?.sender_name || "Unknown",
                                              email: message.external_sender_info?.sender_email || "No Email",
                                              image: message.external_sender_info?.member_profile_url || dummy,
                                              external_chat_group_member_id: message.external_chat_group_member_id || undefined
                                            })}
                                            onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                                          />
                                        );
                                      }

                                      if (selectedConversation.end_user_profile_image_url) {
                                        return (
                                          <img
                                            src={selectedConversation.end_user_profile_image_url || dummy}
                                            className="rounded-circle"
                                            alt="image"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => {
                                              if (isGoogleChat) {
                                                handleHeaderClick();
                                              }
                                            }}
                                            onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                                          />
                                        );
                                      }

                                      if (selectedConversation.contact?.avatar_url) {
                                        return (
                                          <img
                                            src={selectedConversation.contact.avatar_url || dummy}
                                            className="rounded-circle"
                                            alt="image"
                                            onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                                          />
                                        );
                                      }

                                      return (
                                        <img
                                          src={dummy}
                                          className="rounded-circle"
                                          alt="image"
                                          style={{ cursor: isGoogleChat ? 'pointer' : 'default' }}
                                          onClick={() => {
                                            if (isGoogleChat) {
                                              handleHeaderClick();
                                            }
                                          }}
                                          onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                                        />
                                      );
                                    })()}
                                  </div>
                                )}
                                <div
                                  className="message-content-wrapper"
                                  style={{ position: "relative" }}
                                >

                                  {message.external_sender_name && isUserPacket && (
                                    <div
                                      className="external-sender-name"
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => {
                                        const platformKey = getConversationPlatformKey(selectedConversation);
                                        if (platformKey === "GOOGLE_CHAT") {
                                          if (message.external_sender_info) {
                                            handleSenderClick({
                                              name: message.external_sender_info.sender_name,
                                              email: message.external_sender_info.sender_email,
                                              image: message.external_sender_info.member_profile_url,
                                              external_chat_group_member_id: message.external_chat_group_member_id || undefined
                                            });
                                          } else {
                                            handleHeaderClick();
                                          }
                                        }
                                      }}
                                    >
                                      {message.external_sender_name}
                                    </div>
                                  )}
                                  {selectionMode && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        left:
                                          message.external_chat_platform_packet_type
                                            .external_chat_platform_packet_type_key ===
                                            "AGENT_MESSAGE"
                                            ? "-30px"
                                            : "auto",
                                        right:
                                          message.external_chat_platform_packet_type
                                            .external_chat_platform_packet_type_key ===
                                            "USER_MESSAGE"
                                            ? "-30px"
                                            : "auto",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selectedMessageIds.includes(
                                          message.external_chat_message_id,
                                        )}
                                        onChange={() => {
                                          if (
                                            selectedMessageIds.includes(
                                              message.external_chat_message_id,
                                            )
                                          ) {
                                            setSelectedMessageIds(
                                              selectedMessageIds.filter(
                                                (id) =>
                                                  id !==
                                                  message.external_chat_message_id,
                                              ),
                                            );
                                          } else {
                                            setSelectedMessageIds([
                                              ...selectedMessageIds,
                                              message.external_chat_message_id,
                                            ]);
                                          }
                                        }}
                                        style={{
                                          width: "18px",
                                          height: "18px",
                                          cursor: "pointer",
                                        }}
                                      />
                                    </div>
                                  )}
                                  <div
                                    id={`msg-${message.external_chat_message_id}`}
                                    className={(message.external_chat_packet_type?.external_chat_packet_type_key === "STICKER" || message.google_chat_meeting_link) ? "" : "message-bubble"}
                                    style={{
                                      position: "relative",
                                      ...(isAlignLeft ? { color: "#333" } : {})
                                    }}
                                    onMouseEnter={() =>
                                      setHoveredMessageId(
                                        message.external_chat_message_id,
                                      )
                                    }
                                    onMouseLeave={() => {
                                      if (
                                        messageMenuOpen !==
                                        message.external_chat_message_id
                                      ) {
                                        setHoveredMessageId(null);
                                      }
                                    }}
                                  >
                                    {selectedConversation?.is_group && (
                                      <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        marginBottom: "4px",
                                        paddingBottom: "4px",
                                        borderBottom: isAlignLeft ? "1px solid rgba(0, 0, 0, 0.06)" : "1px solid rgba(255, 255, 255, 0.15)",
                                      }}>
                                        <span style={{
                                          fontSize: "0.75rem",
                                          fontWeight: 600,
                                          color: isAlignLeft ? "#1a73e8" : "rgba(255, 255, 255, 0.95)",
                                          lineHeight: 1,
                                        }}>
                                          {isAgentPacket
                                            ? getAgentOrBotName(message, "Agent")
                                            : (message.external_sender_info?.sender_name || message.external_sender_name || selectedConversation.username || selectedConversation.first_name || "User")}
                                        </span>
                                        {(isUserPacket || (isAgentPacket && resolvedRole)) && (
                                          <span style={{
                                            fontSize: "0.6rem",
                                            fontWeight: 600,
                                            padding: "1px 6px",
                                            borderRadius: "8px",
                                            lineHeight: "1.4",
                                            letterSpacing: "0.3px",
                                            textTransform: "uppercase" as const,
                                            background: isAlignLeft
                                              ? "linear-gradient(135deg, #e8f0fe, #d2e3fc)"
                                              : "rgba(255, 255, 255, 0.2)",
                                            color: isAlignLeft ? "#1967d2" : "rgba(255, 255, 255, 0.9)",
                                          }}>
                                            {isUserPacket ? "Client" : resolvedRole}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                    {renderMessageContent(message)}
                                    {(hoveredMessageId === message.external_chat_message_id ||
                                      messageMenuOpen === message.external_chat_message_id ||
                                      reactionPickerMessageId === message.external_chat_message_id) &&
                                      !selectionMode && chatTypeFilter === "INTERNAL" && (
                                        <div
                                          className="message-hover-actions-wrapper"
                                          style={{
                                            position: "absolute",
                                            top: "-42px",
                                            right: message.external_chat_platform_packet_type?.external_chat_platform_packet_type_key === "AGENT_MESSAGE" ? "0" : "auto",
                                            left: message.external_chat_platform_packet_type?.external_chat_platform_packet_type_key === "USER_MESSAGE" ? "0" : "auto",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            zIndex: 10000
                                          }}
                                        >
                                          <div
                                            className="quick-reactions-pill"
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              background: "#ffffff",
                                              border: "1px solid #e5e7eb",
                                              borderRadius: "20px",
                                              padding: "2px 8px",
                                              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                                              gap: "2px"
                                            }}
                                          >
                                            {["❤️", "👍", "🙏"].map((emoji) => (
                                              <button
                                                key={emoji}
                                                className="reaction-btn-mini"
                                                onClick={() => handleReaction(message, emoji)}
                                                style={{
                                                  background: "none",
                                                  border: "none",
                                                  padding: "4px",
                                                  fontSize: "18px",
                                                  cursor: "pointer",
                                                  borderRadius: "50%",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                  transition: "all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)"
                                                }}
                                              >
                                                {emoji}
                                              </button>
                                            ))}
                                          </div>

                                          <OverlayTrigger
                                            trigger="click"
                                            rootClose
                                            placement={message.external_chat_platform_packet_type?.external_chat_platform_packet_type_key === "AGENT_MESSAGE" ? "top-end" : "top-start"}
                                            show={reactionPickerMessageId === message.external_chat_message_id}
                                            onToggle={(nextShow) => setReactionPickerMessageId(nextShow ? message.external_chat_message_id : null)}
                                            overlay={
                                              <Popover
                                                id={`reaction-picker-${message.external_chat_message_id}`}
                                                className="reaction-picker-popover"
                                                style={{ border: "none", background: "transparent", boxShadow: "none", zIndex: 999999 }}
                                              >
                                                <EmojiPicker
                                                  theme={Theme.LIGHT}
                                                  onEmojiClick={(emojiData) => handleReaction(message, emojiData.emoji)}
                                                  autoFocusSearch={false}
                                                  width={320}
                                                  height={350}
                                                  previewConfig={{ showPreview: false }}
                                                  skinTonesDisabled
                                                  searchPlaceHolder="Search emojis"
                                                  lazyLoadEmojis={true}
                                                />
                                              </Popover>
                                            }
                                          >
                                            <button
                                              className="more-reactions-btn"
                                              style={{
                                                background: "#ffffff",
                                                border: "1px solid #e5e7eb",
                                                width: "34px",
                                                height: "34px",
                                                cursor: "pointer",
                                                borderRadius: "50%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "#6b7280",
                                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                                                transition: "all 0.2s ease"
                                              }}
                                            >
                                              <i className={`ti ti-mood-${reactionPickerMessageId === message.external_chat_message_id ? 'smile-beam' : 'plus'} fs-18`} />
                                            </button>
                                          </OverlayTrigger>
                                          <button
                                            className="reply-message-btn"
                                            onClick={() => setReplyingToMessage(message)}
                                            title="Reply"
                                            style={{
                                              background: "#ffffff",
                                              border: "1px solid #e5e7eb",
                                              width: "34px",
                                              height: "34px",
                                              cursor: "pointer",
                                              borderRadius: "50%",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              color: "#6b7280",
                                              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                                              transition: "all 0.2s ease",
                                              marginLeft: "4px"
                                            }}
                                          >
                                            <i className="ti ti-arrow-back-up fs-18" />
                                          </button>
                                          <button
                                            className="reply-in-thread-btn"
                                            onClick={() => openThread(message)}
                                            title="Reply in thread"
                                            style={{
                                              background: "#ffffff",
                                              border: "1px solid #e5e7eb",
                                              width: "34px",
                                              height: "34px",
                                              cursor: "pointer",
                                              borderRadius: "50%",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              color: "#6b7280",
                                              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                                              transition: "all 0.2s ease",
                                              marginLeft: "4px"
                                            }}
                                          >
                                            <i className="ti ti-message-2 fs-18" />
                                          </button>
                                        </div>
                                      )}
                                  </div>
                                  {chatTypeFilter === "INTERNAL" && message.reactions && message.reactions.length > 0 && (
                                    <div className="message-reactions-container">
                                      {Object.entries(groupReactions(message.reactions)).map(([type, reactionList]: [string, any]) => {
                                        const hasMe = reactionList.some((r: any) => r.is_me);
                                        return (
                                          <OverlayTrigger
                                            key={type}
                                            placement="top"
                                            overlay={
                                              <Tooltip id={`reaction-tooltip-${message.external_chat_message_id}-${type}`}>
                                                <div className="reaction-tooltip-content">
                                                  {reactionList.map((r: any) => (
                                                    <div key={r.external_chat_message_reaction_id} className="reaction-user-item">
                                                      <img
                                                        src={r.member_profile_picture || dummy}
                                                        alt={r.member_name}
                                                        style={{ width: "20px", height: "20px", borderRadius: "50%", objectFit: "cover" }}
                                                        onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                                                      />
                                                      <span className="user-name" style={{ fontSize: "11px", color: "#fff" }}>{r.member_name}</span>
                                                    </div>
                                                  ))}
                                                </div>
                                              </Tooltip>
                                            }
                                          >
                                            <div className={`reaction-bubble ${hasMe ? 'is-me' : ''}`} onClick={() => handleReaction(message, type)}>
                                              <span className="reaction-emoji">{type}</span>
                                              {reactionList.length > 0 && (
                                                <span className="reaction-count">{reactionList.length}</span>
                                              )}
                                            </div>
                                          </OverlayTrigger>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {chatTypeFilter === "INTERNAL" && (message.is_thread_message || (message.thread_message_count != null && message.thread_message_count > 0) || !!threadActivity[message.external_chat_message_id]) && (() => {
                                    const activity = threadActivity[message.external_chat_message_id];
                                    const replyCount = message.thread_message_count || 0;
                                    return (
                                      <div
                                        className={`thread-indicator${activity ? " has-activity" : ""}`}
                                        onClick={() => openThread(message)}
                                        style={{
                                          display: "inline-flex",
                                          visibility: "visible",
                                          opacity: 1,
                                          zIndex: 100
                                        }}
                                      >
                                        {activity ? (
                                          activity.senderAvatar ? (
                                            <img
                                              src={activity.senderAvatar}
                                              alt={activity.senderName}
                                              title={activity.senderName}
                                              className="thread-indicator-avatar"
                                              onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                                            />
                                          ) : (
                                            <span
                                              className="thread-indicator-avatar thread-indicator-initial"
                                              title={activity.senderName}
                                              style={{ backgroundColor: getAvatarColor(activity.senderName) }}
                                            >
                                              {activity.senderName.charAt(0).toUpperCase()}
                                            </span>
                                          )
                                        ) : (
                                          <i className="ti ti-message-2" />
                                        )}
                                        {activity && activity.unread > 0 && (
                                          <span className="thread-indicator-unread">
                                            <i className="ti ti-bell" />
                                            {activity.unread} unread
                                          </span>
                                        )}
                                        <span className="thread-indicator-count">
                                          {replyCount > 0 ? `${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}` : 'Reply'}
                                        </span>
                                        {activity && <RelativeReplyTime datetime={activity.lastReplyAt} />}
                                      </div>
                                    );
                                  })()}
                                  <div className="message-time">
                                    {formatMessageTime(message.created_datetime)}
                                    {isAlignLeft && !selectedConversation?.is_group && (
                                      <span className="message-sender">
                                        {"   "}
                                        {message.external_sender_info?.sender_name || selectedConversation.username || selectedConversation.first_name || "User"}
                                      </span>
                                    )}

                                    {!isAlignLeft && (
                                      <span style={{ marginLeft: "4px", display: "inline-flex", alignItems: "center" }}>
                                        {message._status === "pending" && (
                                          <i className="fa fa-clock-o" style={{ fontSize: "12px", opacity: 0.7 }} />
                                        )}
                                        {(message._status === "sent" || (message.external_chat_message_id && message._status !== "pending")) && (
                                          <i className={`ti ti-checks ${message.is_read ? 'text-primary' : 'text-muted'}`} style={{ fontSize: "14px" }} />
                                        )}
                                        {message._status === "error" && (
                                          <button
                                            onClick={() => handleRetryMessage(message)}
                                            title="Retry sending"
                                            style={{
                                              background: "none",
                                              border: "none",
                                              padding: "0 2px",
                                              cursor: "pointer",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              lineHeight: 1,
                                            }}
                                          >
                                            <i className="ti ti-refresh" style={{ fontSize: "13px", color: "#ff4d4f" }} />
                                          </button>
                                        )}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })();
                        })}
                        {loadingOlderMessages && (
                          <div className="d-flex justify-content-center align-items-center p-3">
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                              <span className="visually-hidden">Loading older messages...</span>
                            </div>
                          </div>
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                    </OverlayScrollbarsComponent>
                  </div>

                  {/* Chat Footer - Input Area */}
                  {selectedConversation && (() => {
                    const isChatClosed = selectedConversation?.external_chat_end_user_status?.external_chat_end_user_status_key === "CLOSE";
                    const platformKey = getConversationPlatformKey(selectedConversation);
                    const isNotTransferred = platformKey === "ENGATI" && selectedConversation?.is_chat_transferred === false;
                    const isInputDisabled = isChatClosed || isNotTransferred;
                    let footerTooltip = "";
                    if (isChatClosed) footerTooltip = "Chat has been closed";
                    else if (isNotTransferred) footerTooltip = "Chat is not transferred to agent";

                    return (
                      <div className={`chat-footer ${isInputDisabled ? "disabled-footer" : ""}`}
                        title={footerTooltip}
                      >
                        {/* Selection Mode Actions */}
                        {selectionMode && selectedMessageIds.length > 0 && (
                          <div
                            style={{
                              padding: "12px 20px",
                              background: "#f8f9fa",
                              borderTop: "1px solid #e5e7eb",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <div style={{ fontWeight: "600", fontSize: "14px" }}>
                              {selectedMessageIds.length} message
                              {selectedMessageIds.length > 1 ? "s" : ""} selected
                            </div>
                            <div style={{ display: "flex", gap: "8px" }}>
                              <button
                                className="btn btn-light btn-sm"
                                onClick={() => {
                                  setSelectionMode(false);
                                  setSelectedMessageIds([]);
                                }}
                              >
                                <i className="fa-solid fa-times me-1" />
                                Cancel
                              </button>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={async () => {
                                  const confirmed = await MySwal.fire({
                                    icon: "warning",
                                    title: `Delete ${selectedMessageIds.length
                                      } Message${selectedMessageIds.length > 1 ? "s" : ""
                                      }?`,
                                    text: "This will permanently delete the selected messages.",
                                    showCancelButton: true,
                                    confirmButtonText: "Yes, delete",
                                    cancelButtonText: "Cancel",
                                    confirmButtonColor: "#dc3545",
                                  });
                                  if (
                                    confirmed.isConfirmed &&
                                    selectedConversation
                                  ) {
                                    try {
                                      await ChatService.deleteMessages(
                                        selectedMessageIds,
                                      );
                                      setSelectionMode(false);
                                      setSelectedMessageIds([]);
                                      fetchMessages(selectedConversation);
                                      fetchConversations();
                                      MySwal.fire({
                                        icon: "success",
                                        title: "Deleted!",
                                        text: "Selected messages have been deleted.",
                                        timer: 2000,
                                      });
                                    } catch (err) {
                                      MySwal.fire({
                                        icon: "error",
                                        title: "Failed",
                                        text: "Could not delete messages.",
                                        timer: 2000,
                                      });
                                    }
                                  }
                                }}
                              >
                                <i className="fa-solid fa-trash me-1" />
                                Delete ({selectedMessageIds.length})
                              </button>
                            </div>
                          </div>
                        )}
                        {/* Google Chat inline file preview */}
                        {gcPendingFiles.length > 0 && (
                          <div style={{ padding: "8px 12px", borderTop: "1px solid #e5e7eb", background: "#f9fafb", display: "flex", flexDirection: "column", gap: "8px" }}>
                            {gcPendingFilePreviews.map((preview, index) => (
                              <div key={index} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                {preview.type.startsWith("image/") ? (
                                  <img src={preview.url} alt={preview.name} style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6, flexShrink: 0, border: "1px solid #e5e7eb" }} />
                                ) : (
                                  <div style={{ width: 56, height: 56, borderRadius: 6, background: "#e0e7ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    <i className="ti ti-file" style={{ fontSize: 24, color: "#6366f1" }} />
                                  </div>
                                )}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{preview.name}</div>
                                  <div style={{ fontSize: 11, color: "#9ca3af" }}>{(preview.size / 1024).toFixed(1)} KB</div>
                                </div>
                                <button
                                  type="button"
                                  style={{ background: "transparent", border: "none", color: "#6b7280", fontSize: 18, cursor: "pointer", padding: "4px", lineHeight: 1, flexShrink: 0 }}
                                  onClick={() => {
                                    const newFiles = [...gcPendingFiles];
                                    const newPreviews = [...gcPendingFilePreviews];
                                    URL.revokeObjectURL(preview.url);
                                    newFiles.splice(index, 1);
                                    newPreviews.splice(index, 1);
                                    setGcPendingFiles(newFiles);
                                    setGcPendingFilePreviews(newPreviews);
                                  }}
                                  title="Remove file"
                                  disabled={sending}
                                >
                                  <i className="ti ti-x" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Regular Chat Input */}
                        <form
                          className="chat-input-form"
                          onSubmit={handleSendMessage}
                        >
                          <div className="chat-input-wrapper">
                            <div className="input-actions-left">
                              <button
                                type="button"
                                className="action-btn"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={sending || isInputDisabled}
                              >
                                <i className="ti ti-paperclip" />
                              </button>
                              <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept={
                                  getConversationPlatformKey(selectedConversation) === "LAZADA"
                                    ? "image/*"
                                    : "image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
                                }
                                style={{ display: "none" }}
                                onChange={handleFileAttachment}
                              />
                              <button
                                type="button"
                                className="action-btn"
                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                disabled={sending || isInputDisabled}
                              >
                                <i className="ti ti-mood-smile" />
                              </button>
                              {/* Emoji Picker */}
                              {showEmojiPicker && (
                                <div
                                  style={{
                                    position: "absolute",
                                    bottom: "50px",
                                    left: "10px",
                                    background: "#fff",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "8px",
                                    padding: "12px",
                                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                                    zIndex: 1000,
                                    maxWidth: "300px",
                                  }}
                                >
                                  <div
                                    style={{
                                      marginBottom: "8px",
                                      fontWeight: "600",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Select Emoji
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: "4px",
                                    }}
                                  >
                                    {[
                                      "😀",
                                      "😃",
                                      "😄",
                                      "😁",
                                      "😅",
                                      "😂",
                                      "🤣",
                                      "😊",
                                      "😇",
                                      "🙂",
                                      "🙃",
                                      "😉",
                                      "😌",
                                      "😍",
                                      "🥰",
                                      "😘",
                                      "😗",
                                      "😙",
                                      "😚",
                                      "😋",
                                      "😛",
                                      "😝",
                                      "😜",
                                      "🤪",
                                      "🤨",
                                      "🧐",
                                      "🤓",
                                      "😎",
                                      "🤩",
                                      "🥳",
                                      "👍",
                                      "👎",
                                      "👏",
                                      "🙌",
                                      "👌",
                                      "🤝",
                                      "❤️",
                                      "💕",
                                      "💖",
                                      "💗",
                                      "💙",
                                      "💚",
                                      "💛",
                                      "🔥",
                                      "⭐",
                                      "✨",
                                    ].map((emoji) => (
                                      <button
                                        key={emoji}
                                        type="button"
                                        onClick={() => {
                                          setMessageInput(messageInput + emoji);
                                          setShowEmojiPicker(false);
                                        }}
                                        style={{
                                          fontSize: "20px",
                                          padding: "4px 8px",
                                          border: "none",
                                          background: "transparent",
                                          cursor: "pointer",
                                          borderRadius: "4px",
                                        }}
                                        onMouseEnter={(e) =>
                                        (e.currentTarget.style.background =
                                          "#f3f4f6")
                                        }
                                        onMouseLeave={(e) =>
                                        (e.currentTarget.style.background =
                                          "transparent")
                                        }
                                      >
                                        {emoji}
                                      </button>
                                    ))}
                                  </div>
                                  <div
                                    style={{ marginTop: "8px", textAlign: "right" }}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => setShowEmojiPicker(false)}
                                      style={{
                                        fontSize: "11px",
                                        padding: "4px 12px",
                                        border: "1px solid #e5e7eb",
                                        background: "#fff",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                      }}
                                    >
                                      Close
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="input-field-wrapper" style={{ position: "relative" }}>
                              {replyingToMessage && (
                                <div className="reply-preview-container">
                                  <div className="reply-preview-content">
                                    <div className="reply-preview-sender">
                                      Replying to {(replyingToMessage as any).external_sender_name || (replyingToMessage as any).sender?.first_name || "User"}
                                    </div>
                                    <div className="reply-preview-text">
                                      {getMessagePreviewText(replyingToMessage)}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    className="reply-preview-close"
                                    onClick={() => setReplyingToMessage(null)}
                                  >
                                    <i className="ti ti-x fs-16" />
                                  </button>
                                </div>
                              )}
                              {showTemplateDropdown && filteredTemplates.length > 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    bottom: "100%",
                                    left: 0,
                                    right: 0,
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                    background: "#fff",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "8px",
                                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                                    zIndex: 1000,
                                    marginBottom: "8px",
                                  }}
                                >
                                  {filteredTemplates.map((template) => (
                                    <div
                                      key={template.chat_template_message_id}
                                      style={{
                                        padding: "10px 14px",
                                        cursor: "pointer",
                                        borderBottom: "1px solid #f3f4f6",
                                      }}
                                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
                                      onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        setMessageInput(template.message);
                                        setShowTemplateDropdown(false);
                                        setTimeout(() => {
                                          const textarea = document.querySelector(".chat-input") as HTMLTextAreaElement;
                                          if (textarea) {
                                            textarea.focus();
                                            textarea.style.height = "auto";
                                            textarea.style.height = textarea.scrollHeight + "px";
                                          }
                                        }, 0);
                                      }}
                                    >
                                      <div className="d-flex align-items-center">
                                        <span className="badge bg-primary me-2">{template.short_code}</span>
                                        <strong>{template.title}</strong>
                                      </div>
                                      <small className="text-muted" style={{ display: "block", marginTop: "4px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{(template.message || "").substring(0, 60)}{(template.message || "").length > 60 ? "..." : ""}</small>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <textarea
                                ref={chatInputRef}
                                className="form-control chat-input"
                                placeholder="Type a message (use / for templates)"
                                rows={1}
                                value={messageInput}
                                style={{ minHeight: "38px", maxHeight: "150px", resize: "none", overflow: "auto" }}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setMessageInput(value);
                                  if (value.startsWith("/")) {
                                    const searchTerm = value.toLowerCase();
                                    const filtered = chatTemplates.filter(
                                      (t) => (t.short_code || "").toLowerCase().includes(searchTerm) || (t.title || "").toLowerCase().includes(searchTerm.slice(1))
                                    );
                                    setFilteredTemplates(filtered);
                                    setShowTemplateDropdown(true);
                                  } else {
                                    setShowTemplateDropdown(false);
                                  }
                                }}
                                onBlur={() => {
                                  setTimeout(() => setShowTemplateDropdown(false), 150);
                                }}
                                onInput={(e) => {
                                  const target = e.target as HTMLTextAreaElement;
                                  target.style.height = "auto";
                                  target.style.height = Math.min(target.scrollHeight, 150) + "px";
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Escape" && showTemplateDropdown) {
                                    setShowTemplateDropdown(false);
                                    return;
                                  }
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    if (showTemplateDropdown && filteredTemplates.length > 0) {
                                      setMessageInput(filteredTemplates[0].message);
                                      setShowTemplateDropdown(false);
                                    } else {
                                      handleSendMessage(e);
                                    }
                                  }
                                }}
                                disabled={isInputDisabled}
                                maxLength={2000}
                              />
                            </div>
                            <div className="input-actions-right d-flex align-items-center gap-2">
                              {selectedConversation?.is_group && chatTypeFilter !== "INTERNAL" && (
                                <div className="form-check me-2 mb-0 d-flex align-items-center" style={{ minHeight: 'auto' }}>
                                  <input
                                    className="form-check-input mt-0 me-2"
                                    type="checkbox"
                                    id="messageVisibilityToggle"
                                    checked={messageVisibility === "ALL"}
                                    onChange={(e) => {
                                      const newVal = e.target.checked ? "ALL" : "INTERNAL";
                                      setMessageVisibility(newVal);
                                      localStorage.setItem("chat_messageVisibility", newVal);
                                    }}
                                    disabled={sending || isInputDisabled}
                                  />
                                  <label className="form-check-label mb-0" htmlFor="messageVisibilityToggle" style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                    Send to all user
                                  </label>
                                </div>
                              )}
                              <button
                                className="btn btn-primary btn-sm py-2"
                                type="submit"
                                disabled={sending || (!messageInput.trim() && gcPendingFiles.length === 0) || isInputDisabled}
                              >
                                <i className="ti ti-send me-1" />
                                {sending ? "Sending..." : "Send"}
                              </button>
                            </div>
                          </div>
                        </form>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <div className="text-center">
                    <i
                      className="ti ti-message-circle"
                      style={{ fontSize: "64px", color: "#ccc" }}
                    />
                    <p className="mt-3 text-muted">
                      Select a conversation to start chatting
                    </p>
                  </div>
                </div>
              )}
            </div>
            {/* End Center Panel */}

            {/* Right Panel - Contact Details */}
            {/* --- Right Panel (Contact Details) Start --- */}
            {isDetailsOpen && selectedConversation && (
              <div
                className="chat-sidebar-right"
                data-mobile-panel={mobileActivePanel}
                style={{ position: 'relative' }}
              >
                {rightPanelMode !== "THREAD" && (
                  <button
                    className="btn-icon right-panel-close-btn"
                    onClick={closeRightPanel}
                    title="Close panel"
                  >
                    <i className="ti ti-x" />
                  </button>
                )}
                <div className="contact-details-panel" style={{ height: "100%" }}>
                  {rightPanelMode === "THREAD" ? (
                    <div className="thread-panel" style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 150px)" }}>
                      <div className="thread-header">
                        <button
                          className="thread-back-btn"
                          onClick={() => {
                            setRightPanelMode("DEFAULT");
                            setThreadParentMessage(null);
                            setThreadMessages([]);
                          }}
                        >
                          <i className="ti ti-arrow-left" />
                        </button>
                        <div className="thread-header-title">
                          <i className="ti ti-message-2" />
                          <span>Thread</span>
                        </div>
                        <button
                          className="thread-close-btn"
                          onClick={() => {
                            closeRightPanel();
                            setThreadParentMessage(null);
                            setThreadMessages([]);
                          }}
                        >
                          <i className="ti ti-x" />
                        </button>
                      </div>

                      <OverlayScrollbarsComponent
                        ref={threadOsRef}
                        style={{ flex: 1, height: "100%" }}
                        options={{ scrollbars: { autoHide: "leave" } }}
                        events={{
                          scroll: (instance) => {
                            const { scrollTop, scrollHeight, clientHeight } = instance.elements().viewport;
                            threadStickToBottomRef.current = scrollHeight - scrollTop - clientHeight <= 80;
                          }
                        }}
                      >
                        <div ref={threadBodyRef} className="thread-body" style={{ display: "flex", flexDirection: "column" }}>
                          {threadParentMessage && (
                            <div
                              className={`message-item thread-parent-message ${threadParentMessage.external_chat_platform_packet_type?.external_chat_platform_packet_type_key === "AGENT_MESSAGE" ? "message-sent" : "message-received"}`}
                              style={{ padding: "16px 24px", margin: 0, borderBottom: "1px solid #e5e7eb", position: 'relative' }}
                            >
                              {threadParentMessage.external_chat_platform_packet_type?.external_chat_platform_packet_type_key === "USER_MESSAGE" && (
                                <div className="message-avatar">
                                  <img
                                    src={threadParentMessage.external_sender_info?.member_profile_url || selectedConversation?.end_user_profile_image_url || dummy}
                                    className="rounded-circle"
                                    alt="image"
                                    onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                                  />
                                </div>
                              )}
                              <div className="message-content-wrapper" style={{ maxWidth: "100%", position: "relative" }}>
                                {threadParentMessage.external_chat_platform_packet_type?.external_chat_platform_packet_type_key === "USER_MESSAGE" && (
                                  <div className="external-sender-name">
                                    {threadParentMessage.external_sender_name || selectedConversation?.username || "User"}
                                  </div>
                                )}
                                <div
                                  className={((threadParentMessage as any).external_chat_packet_type?.external_chat_packet_type_key === "STICKER" || (threadParentMessage as any).google_chat_meeting_link) ? "" : "message-bubble"}
                                  style={{ position: "relative" }}
                                  onMouseEnter={() => setHoveredMessageId(threadParentMessage.external_chat_message_id)}
                                  onMouseLeave={() => setHoveredMessageId(null)}
                                >
                                  {renderMessageContent(threadParentMessage as any)}
                                  {hoveredMessageId === threadParentMessage.external_chat_message_id && !selectionMode && (
                                    <div
                                      className="message-hover-actions-wrapper"
                                      style={{
                                        position: "absolute",
                                        top: "-42px",
                                        right: threadParentMessage.external_chat_platform_packet_type?.external_chat_platform_packet_type_key === "AGENT_MESSAGE" ? "0" : "auto",
                                        left: threadParentMessage.external_chat_platform_packet_type?.external_chat_platform_packet_type_key === "USER_MESSAGE" ? "0" : "auto",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        zIndex: 10000
                                      }}
                                    >
                                      <button
                                        className="reply-message-btn"
                                        onClick={() => setThreadReplyingToMessage(threadParentMessage)}
                                        title="Reply"
                                        style={{
                                          background: "#ffffff",
                                          border: "1px solid #e5e7eb",
                                          width: "34px",
                                          height: "34px",
                                          cursor: "pointer",
                                          borderRadius: "50%",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          color: "#6b7280",
                                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                                          transition: "all 0.2s ease"
                                        }}
                                      >
                                        <i className="ti ti-arrow-back-up fs-18" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                                {chatTypeFilter === "INTERNAL" && threadParentMessage.reactions && threadParentMessage.reactions.length > 0 && (
                                  <div className="message-reactions-container">
                                    {Object.entries(groupReactions(threadParentMessage.reactions)).map(([type, reactionList]: [string, any]) => {
                                      const hasMe = reactionList.some((r: any) => r.is_me);
                                      return (
                                        <OverlayTrigger
                                          key={type}
                                          placement="top"
                                          overlay={
                                            <Tooltip id={`reaction-tooltip-thread-parent-${threadParentMessage.external_chat_message_id}-${type}`}>
                                              <div className="reaction-tooltip-content">
                                                {reactionList.map((r: any) => (
                                                  <div key={r.external_chat_message_reaction_id} className="reaction-user-item">
                                                    <img
                                                      src={r.member_profile_picture || dummy}
                                                      alt={r.member_name}
                                                      style={{ width: "20px", height: "20px", borderRadius: "50%", objectFit: "cover" }}
                                                      onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                                                    />
                                                    <span className="user-name" style={{ fontSize: "11px", color: "#fff" }}>{r.member_name}</span>
                                                  </div>
                                                ))}
                                              </div>
                                            </Tooltip>
                                          }
                                        >
                                          <div className={`reaction-bubble ${hasMe ? 'is-me' : ''}`} onClick={() => handleReaction(threadParentMessage, type)}>
                                            <span className="reaction-emoji">{type}</span>
                                            {reactionList.length > 0 && (
                                              <span className="reaction-count">{reactionList.length}</span>
                                            )}
                                          </div>
                                        </OverlayTrigger>
                                      );
                                    })}
                                  </div>
                                )}
                                <div className="message-time">
                                  {new Date(threadParentMessage.created_datetime).toLocaleString("en-US", {
                                    month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true
                                  })}
                                  {threadParentMessage.external_chat_platform_packet_type?.external_chat_platform_packet_type_key === "AGENT_MESSAGE" && (
                                    <span className="message-sender">
                                      {" • "}
                                      {getAgentOrBotName(threadParentMessage, "Agent")}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="thread-replies-divider">
                            <span>{threadMessages.length} {threadMessages.length === 1 ? "reply" : "replies"}</span>
                          </div>

                          <div className="thread-messages-list" style={{ paddingTop: "20px" }}>
                            {threadLoading ? (
                              <div className="d-flex justify-content-center align-items-center py-4">
                                <span className="spinner-border spinner-border-sm text-primary" />
                              </div>
                            ) : threadMessages.length === 0 ? (
                              <div className="text-center text-muted py-4" style={{ fontSize: "13px" }}>
                                No replies yet
                              </div>
                            ) : (
                              threadMessages.map((tm) => {
                                const isAgent = tm.external_chat_platform_packet_type?.external_chat_platform_packet_type_key === "AGENT_MESSAGE" ||
                                  (tm as any).sender_type === "AGENT" ||
                                  (tm.external_sender_info?.sender_email === auth?.email);
                                const senderName = isAgent
                                  ? getAgentOrBotName(tm, tm.external_sender_name || auth?.full_name || "Agent")
                                  : (tm.external_sender_info?.sender_name || tm.external_sender_name || (tm as any).external_chat_end_user?.username || selectedConversation?.username || selectedConversation?.first_name || "User");
                                const senderAvatar = isAgent ? (auth?.avatar_url || dummy) : (tm.external_sender_info?.member_profile_url || dummy);

                                return (
                                  <div
                                    key={tm.external_chat_message_id}
                                    className={`message-item ${isAgent ? "message-sent" : "message-received"}`}
                                    style={{ padding: "8px 24px", margin: 0, position: 'relative', zIndex: hoveredMessageId === tm.external_chat_message_id ? 1000 : 1 }}
                                  >
                                    {!isAgent && (
                                      <div className="message-avatar">
                                        <img
                                          src={senderAvatar}
                                          alt={senderName}
                                          className="rounded-circle"
                                          onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                                        />
                                      </div>
                                    )}
                                    <div className="message-content-wrapper" style={{ maxWidth: "100%", position: "relative" }}>
                                      {!isAgent && (
                                        <div className="external-sender-name">
                                          {senderName}
                                        </div>
                                      )}
                                      <div
                                        id={`msg-${tm.external_chat_message_id}`}
                                        className={(tm.external_chat_packet_type?.external_chat_packet_type_key === "STICKER" || (tm as any).google_chat_meeting_link) ? "" : "message-bubble"}
                                        style={{ position: "relative" }}
                                        onMouseEnter={() => setHoveredMessageId(tm.external_chat_message_id)}
                                        onMouseLeave={() => setHoveredMessageId(null)}
                                      >
                                        {renderMessageContent(tm as any)}
                                        {hoveredMessageId === tm.external_chat_message_id && !selectionMode && (
                                          <div
                                            className="message-hover-actions-wrapper"
                                            style={{
                                              position: "absolute",
                                              top: "-42px",
                                              right: isAgent ? "0" : "auto",
                                              left: !isAgent ? "0" : "auto",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "8px",
                                              zIndex: 10000
                                            }}
                                          >
                                            <div
                                              className="quick-reactions-pill"
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                background: "#ffffff",
                                                border: "1px solid #e5e7eb",
                                                borderRadius: "20px",
                                                padding: "2px 8px",
                                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                                                gap: "2px"
                                              }}
                                            >
                                              {["❤️", "👍", "🙏"].map((emoji) => (
                                                <button
                                                  key={emoji}
                                                  className="reaction-btn-mini"
                                                  onClick={() => handleReaction(tm, emoji)}
                                                  style={{
                                                    background: "none",
                                                    border: "none",
                                                    padding: "4px",
                                                    fontSize: "18px",
                                                    cursor: "pointer",
                                                    borderRadius: "50%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    transition: "all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)"
                                                  }}
                                                >
                                                  {emoji}
                                                </button>
                                              ))}
                                            </div>
                                            <OverlayTrigger
                                              trigger="click"
                                              rootClose
                                              placement={isAgent ? "top-end" : "top-start"}
                                              show={reactionPickerMessageId === tm.external_chat_message_id}
                                              onToggle={(nextShow) => setReactionPickerMessageId(nextShow ? tm.external_chat_message_id : null)}
                                              overlay={
                                                <Popover
                                                  id={`reaction-picker-thread-${tm.external_chat_message_id}`}
                                                  className="reaction-picker-popover"
                                                  style={{ border: "none", background: "transparent", boxShadow: "none", zIndex: 999999 }}
                                                >
                                                  <EmojiPicker
                                                    theme={Theme.LIGHT}
                                                    onEmojiClick={(emojiData) => handleReaction(tm, emojiData.emoji)}
                                                    autoFocusSearch={false}
                                                    width={300}
                                                    height={320}
                                                    previewConfig={{ showPreview: false }}
                                                    skinTonesDisabled
                                                    searchPlaceHolder="Search emojis"
                                                    lazyLoadEmojis={true}
                                                  />
                                                </Popover>
                                              }
                                            >
                                              <button
                                                className="more-reactions-btn"
                                                style={{
                                                  background: "#ffffff",
                                                  border: "1px solid #e5e7eb",
                                                  width: "34px",
                                                  height: "34px",
                                                  cursor: "pointer",
                                                  borderRadius: "50%",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                  color: "#6b7280",
                                                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)"
                                                }}
                                              >
                                                <i className={`ti ti-mood-plus fs-18`} />
                                              </button>
                                            </OverlayTrigger>
                                            <button
                                              className="reply-message-btn"
                                              onClick={() => setThreadReplyingToMessage(tm)}
                                              title="Reply"
                                              style={{
                                                background: "#ffffff",
                                                border: "1px solid #e5e7eb",
                                                width: "34px",
                                                height: "34px",
                                                cursor: "pointer",
                                                borderRadius: "50%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "#6b7280",
                                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                                                transition: "all 0.2s ease"
                                              }}
                                            >
                                              <i className="ti ti-arrow-back-up fs-18" />
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                      {tm.reactions && tm.reactions.length > 0 && (
                                        <div className="message-reactions-container">
                                          {Object.entries(groupReactions(tm.reactions)).map(([type, reactionList]: [string, any]) => {
                                            const hasMe = reactionList.some((r: any) => r.is_me);
                                            return (
                                              <OverlayTrigger
                                                key={type}
                                                placement="top"
                                                overlay={
                                                  <Tooltip id={`reaction-tooltip-thread-${tm.external_chat_message_id}-${type}`}>
                                                    <div className="reaction-tooltip-content">
                                                      {reactionList.map((r: any) => (
                                                        <div key={r.external_chat_message_reaction_id} className="reaction-user-item">
                                                          <img
                                                            src={r.member_profile_picture || dummy}
                                                            alt={r.member_name}
                                                            style={{ width: "20px", height: "20px", borderRadius: "50%", objectFit: "cover" }}
                                                            onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                                                          />
                                                          <span className="user-name" style={{ fontSize: "11px", color: "#fff" }}>{r.member_name}</span>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  </Tooltip>
                                                }
                                              >
                                                <div className={`reaction-bubble ${hasMe ? 'is-me' : ''}`} onClick={() => handleReaction(tm, type)}>
                                                  <span className="reaction-emoji">{type}</span>
                                                  {reactionList.length > 0 && (
                                                    <span className="reaction-count">{reactionList.length}</span>
                                                  )}
                                                </div>
                                              </OverlayTrigger>
                                            );
                                          })}
                                        </div>
                                      )}
                                      <div className="message-time">
                                        {new Date(tm.created_datetime).toLocaleTimeString("en-US", {
                                          hour: "numeric", minute: "2-digit", hour12: true
                                        })}
                                        {isAgent && (
                                          <span className="message-sender">
                                            {" • "}
                                            {senderName}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      </OverlayScrollbarsComponent>

                      <div className="thread-input-area">
                        {threadReplyingToMessage && (
                          <div className="reply-preview-container" style={{ margin: "0 10px 8px 10px" }}>
                            <div className="reply-preview-content">
                              <div className="reply-preview-sender">
                                Replying to {(threadReplyingToMessage as any).external_sender_name || (threadReplyingToMessage as any).sender?.first_name || "User"}
                              </div>
                              <div className="reply-preview-text">
                                {getMessagePreviewText(threadReplyingToMessage)}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="reply-preview-close"
                              onClick={() => setThreadReplyingToMessage(null)}
                            >
                              <i className="ti ti-x fs-16" />
                            </button>
                          </div>
                        )}
                        {threadPendingFiles.length > 0 && (
                          <div className="thread-pending-files">
                            {threadPendingFiles.map(({ file, url }, index) => (
                              <div key={url} className="thread-pending-file">
                                {file.type.startsWith("image/") ? (
                                  <img src={url} alt={file.name} className="thread-pending-file-thumb" />
                                ) : (
                                  <div className="thread-pending-file-thumb thread-pending-file-icon">
                                    <i className="ti ti-file" />
                                  </div>
                                )}
                                <div className="thread-pending-file-info">
                                  <div className="thread-pending-file-name" title={file.name}>{file.name}</div>
                                  <div className="thread-pending-file-size">{(file.size / 1024).toFixed(1)} KB</div>
                                </div>
                                <button
                                  type="button"
                                  className="thread-pending-file-remove"
                                  onClick={() => removeThreadPendingFile(index)}
                                  title="Remove file"
                                  disabled={threadSending}
                                >
                                  <i className="ti ti-x" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <form onSubmit={handleSendThreadMessage} className="thread-input-form">
                          <div className="thread-input-wrapper">
                            <button
                              type="button"
                              className="thread-attach-btn"
                              onClick={() => threadFileInputRef.current?.click()}
                              disabled={threadSending}
                              title="Attach files"
                            >
                              <i className="ti ti-paperclip" />
                            </button>
                            <input
                              ref={threadFileInputRef}
                              type="file"
                              multiple
                              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
                              style={{ display: "none" }}
                              onChange={handleThreadFileAttachment}
                            />
                            <input
                              ref={threadInputRef}
                              type="text"
                              className="thread-input"
                              placeholder="Reply..."
                              value={threadInput}
                              onChange={(e) => setThreadInput(e.target.value)}
                              disabled={false}
                              autoFocus
                            />
                            <button
                              type="submit"
                              className="thread-send-btn"
                              disabled={threadSending || (!threadInput.trim() && threadPendingFiles.length === 0)}
                            >
                              {threadSending ? (
                                <span className="spinner-border spinner-border-sm" />
                              ) : (
                                <i className="ti ti-send" />
                              )}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  ) : (
                    <OverlayScrollbarsComponent
                      style={{ height: "calc(100vh - 150px)", width: "100%" }}
                      events={{
                        scroll: (instance) => {
                          const { viewport } = instance.elements();
                          const { scrollTop, scrollHeight, clientHeight } = viewport;
                          const state = memberListStateRef.current;
                          if (scrollHeight - scrollTop <= clientHeight + 100) {
                            if (
                              state?.rightPanelMode === "SPACE" &&
                              state?.hasMoreMembers &&
                              !state?.loadingNextMembers &&
                              state?.selectedConversation?.external_chat_bot_channel_account?.external_chat_bot_channel_account_id
                            ) {
                              fetchGroupMembers(
                                state.selectedConversation.external_chat_bot_channel_account.external_chat_bot_channel_account_id,
                                state.debouncedGroupMembersSearch,
                                state.groupMembersPage + 1,
                                true
                              );
                            }
                          }
                        }
                      }}
                    >
                      <div className="mobile-back-btn-right">
                        <button
                          className="text-muted me-2"
                          onClick={handleMobileBackToMessages}
                          type="button"
                        >
                          <i className="ti ti-circle-arrow-left" />
                          <span className="ms-2">Back</span>
                        </button>
                      </div>

                      {/* Right Panel Body */}
                      {rightPanelMode === "SPACE" ? (
                        <div className="space-view">
                          <div className="contact-header text-center">
                            <div className="contact-avatar-large mx-auto">
                              {(() => {
                                const spaceName = selectedConversation.external_chat_bot_channel_account?.account_name || "Space";
                                const initial = spaceName.charAt(0).toUpperCase();
                                const bgColor = getAvatarColor(spaceName);
                                return (
                                  <div
                                    className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold mx-auto"
                                    style={{ width: '80px', height: '80px', backgroundColor: bgColor, fontSize: '42px' }}
                                  >
                                    {initial}
                                  </div>
                                );
                              })()}
                            </div>
                            <h5 className="contact-name mt-3">{selectedConversation.external_chat_bot_channel_account?.account_name || "Space"}</h5>
                            <span className="text-muted">Google Chat Space</span>
                          </div>

                          <div className="detail-section">
                            <div className="section-header d-flex justify-content-between align-items-center px-3 mb-2">
                              <h6 className="mb-0">Space Members ({spaceMemberCount})</h6>
                            </div>

                            <div className="px-3 mb-3">
                              <div className="input-group input-group-sm">
                                <span className="input-group-text bg-transparent border-end-0">
                                  <i className="ti ti-search text-muted" />
                                </span>
                                <input
                                  type="text"
                                  className="form-control border-start-0 ps-0"
                                  placeholder="Search by name or email..."
                                  value={groupMembersSearch}
                                  onChange={(e) => setGroupMembersSearch(e.target.value)}
                                />
                              </div>
                            </div>

                            <div className="member-list px-3">
                              {groupMembersLoading ? (
                                <div className="text-center py-3">
                                  <span className="spinner-border spinner-border-sm text-primary" />
                                </div>
                              ) : groupMembers.length > 0 ? (
                                <>
                                  {groupMembers.map((member) => (
                                    <div
                                      key={member.external_chat_group_member_id}
                                      className="member-item d-flex align-items-center py-2 border-bottom"
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => handleSenderClick({
                                        name: member.member_name || (member.user?.first_name ? `${member.user.first_name} ${member.user.last_name || ''}` : member.user?.email) || 'Unknown',
                                        email: member.member_email || member.user?.email || '',
                                        image: member.member_profile_picture || member.user?.avatar_url || '',
                                        external_chat_group_member_id: member.external_chat_group_member_id
                                      })}
                                    >
                                      <div className="avatar avatar-sm me-3">
                                        <img src={member.member_profile_picture || member.user?.avatar_url || dummy}
                                          alt={member.member_name || member.user?.first_name || 'Member'}
                                          className="rounded-circle"
                                          onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                                        />
                                      </div>
                                      <div className="member-info overflow-hidden">
                                        {editingNameType === "member" && editingId === member.external_chat_group_member_id ? (
                                          <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={tempNameValue}
                                            onChange={(e) => setTempNameValue(e.target.value)}
                                            onBlur={() => handleNameSave("member", member.external_chat_group_member_id, tempNameValue)}
                                            onKeyDown={(e) => {
                                              if (e.key === "Enter") handleNameSave("member", member.external_chat_group_member_id, tempNameValue);
                                              if (e.key === "Escape") { setEditingNameType(null); setEditingId(null); }
                                            }}
                                            autoFocus
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                        ) : (
                                          <div
                                            className="member-name text-truncate fw-semibold"
                                            style={{ fontSize: '13px' }}
                                            onClick={(e) => {
                                              if (chatTypeFilter === "INTERNAL") {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setEditingNameType("member");
                                                setEditingId(member.external_chat_group_member_id);
                                                setTempNameValue(member.member_name || (member.user?.first_name ? `${member.user.first_name} ${member.user.last_name || ''}` : member.user?.email) || "");
                                              }
                                            }}
                                          >
                                            {member.member_name || (member.user?.first_name ? `${member.user.first_name} ${member.user.last_name || ''}` : member.user?.email) || 'Unknown'}
                                          </div>
                                        )}
                                        <div className="member-email text-truncate text-muted" style={{ fontSize: '11px' }}>{member.member_email || member.user?.email || ''}</div>
                                      </div>
                                    </div>
                                  ))}
                                  {loadingNextMembers && (
                                    <div className="text-center py-2">
                                      <span className="spinner-border spinner-border-sm text-primary" />
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="text-center text-muted py-2">No members found</div>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : rightPanelMode === "SENDER" ? (
                        <div className="sender-view">
                          <div className="contact-header text-center">
                            <div className="contact-avatar-large mx-auto">
                              <img
                                src={selectedSender?.image || dummy}
                                className="rounded-circle"
                                alt="sender"
                                style={{ width: '100px', height: '80px', objectFit: 'cover' }}
                                onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                              />
                            </div>
                            <h5
                              className="contact-name mt-3"
                              onClick={(e) => {
                                if (chatTypeFilter === "INTERNAL") {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setEditingNameType("sender");
                                  setTempNameValue(selectedSender?.name || "");
                                }
                              }}
                              style={{ cursor: chatTypeFilter === "INTERNAL" ? 'pointer' : 'default' }}
                            >
                              {editingNameType === "sender" ? (
                                <input
                                  type="text"
                                  className="form-control form-control-sm mx-auto"
                                  style={{ maxWidth: '200px' }}
                                  value={tempNameValue}
                                  onChange={(e) => setTempNameValue(e.target.value)}
                                  onBlur={() => handleNameSave("sender", undefined, tempNameValue)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") handleNameSave("sender", undefined, tempNameValue);
                                    if (e.key === "Escape") setEditingNameType(null);
                                  }}
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                selectedSender?.name || "Sender"
                              )}
                            </h5>
                            {/* <div className="contact-actions mt-3">
                            {selectedSender?.email && selectedSender.email !== "No Email" && (
                              <a href={`mailto:${selectedSender.email}`} className="btn btn-sm btn-light me-2">
                                <i className="ti ti-mail" />
                              </a>
                            )}
                          </div> */}
                          </div>

                          <div className="detail-section mt-4 px-3">
                            <div className="detail-row mb-3">
                              <div className="detail-label text-muted mb-1" style={{ fontSize: '12px' }}>
                                <span className="mt-3"><i className="ti ti-mail me-2" />
                                  Email
                                </span>
                              </div>
                              <div className="detail-value fw-semibold">{selectedSender?.email || "No Email"}</div>
                            </div>
                            <div className="detail-row mb-3">
                              <div className="detail-label text-muted mb-1" style={{ fontSize: '12px' }}>
                                <i className="ti ti-brand-google me-2" />
                                Platform
                              </div>
                              <div className="detail-value">Google Chat</div>
                            </div>

                            {/* We can go back to space view if it is a space */}
                            {selectedConversation.external_chat_bot_channel_account?.chat_type === "SPACE" && (
                              <button
                                className="btn btn-primary w-100 mt-3"
                                onClick={() => {
                                  setRightPanelMode("SPACE");
                                  handleHeaderClick(); // Refetch members effectively
                                }}
                              >
                                Back to Space
                              </button>
                            )}
                          </div>
                        </div>

                      ) : (
                        <div className="standard-view">
                          <div className="contact-header">
                            <div className="contact-avatar-large">
                              {selectedConversation.contact?.avatar_url ? (
                                <img
                                  src={selectedConversation.contact?.avatar_url}
                                  className="rounded-circle"
                                  alt="image"
                                  onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                                />
                              ) : (
                                <img
                                  src={dummy}
                                  className="rounded-circle"
                                  alt="image"
                                  onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                                />
                              )}
                            </div>
                            <h5 className="contact-name">
                              {selectedConversation.username ||
                                `${selectedConversation.first_name} ${selectedConversation.last_name}`}
                            </h5>
                            {selectedConversation.contact &&
                              (selectedConversation.contact.primary_email ||
                                selectedConversation.contact.primary_phone) && (
                                <div className="contact-actions">
                                  {selectedConversation.contact.primary_email && (
                                    <a
                                      href={`mailto:${selectedConversation.contact.primary_email}`}
                                      className="btn btn-sm btn-light me-2"
                                      title="Send Email"
                                    >
                                      <i className="ti ti-mail" />
                                    </a>
                                  )}
                                  {selectedConversation.contact.primary_phone && (
                                    <a
                                      href={`tel:${selectedConversation.contact.primary_phone}`}
                                      className="btn btn-sm btn-light"
                                      title="Call"
                                    >
                                      <i className="ti ti-phone" />
                                    </a>
                                  )}
                                </div>
                              )}
                          </div>

                          {/* Standard fields - Suppress for Google Chat as per Scenario 4 */}
                          {selectedConversation.external_chat_tenant_bot?.external_chat_platform?.external_chat_platform_key !== "GOOGLE_CHAT" && (
                            <>
                              <div className="detail-section">
                                <div
                                  className="section-header"
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <h6>Contact</h6>
                                  {(isClientAdmin || isSalesUser) && (
                                    <button
                                      className="btn btn-sm p-0"
                                      onClick={() => {
                                        setSelectedContactId(
                                          selectedConversation.contact?.contact_id ||
                                          null,
                                        );
                                        setShowContactModal(true);
                                      }}
                                      title={
                                        selectedConversation.contact?.contact_id
                                          ? "Change Contact"
                                          : "Add Contact"
                                      }
                                    >
                                      <i
                                        className={`ti ${selectedConversation.contact?.contact_id
                                          ? "ti-edit"
                                          : "ti-plus"
                                          }`}
                                      />
                                    </button>
                                  )}
                                </div>
                                {selectedConversation.contact ? (
                                  <div className="section-content">
                                    <div className="detail-row">
                                      <div className="detail-label">
                                        <i className="ti ti-user me-2" />
                                        Contact Name {selectedConversation.contact?.contact_id ? (<a href={`/contacts/${selectedConversation.contact?.contact_id}`} target="_blank" rel="noopener noreferrer"><i className="ti ti-external-link ms-2" style={{ color: "blue" }} /></a>) : null}
                                      </div>
                                      <div className="detail-value">
                                        <span>
                                          {selectedConversation.contact?.full_name
                                            ? selectedConversation.contact.full_name
                                            : "No name"}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="detail-row">
                                      <div className="detail-label">
                                        <i className="ti ti-mail me-2" />
                                        Email
                                      </div>
                                      <div className="detail-value">
                                        <span>
                                          {selectedConversation.contact?.primary_email
                                            ? selectedConversation.contact.primary_email
                                            : "No email"}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="detail-row">
                                      <div className="detail-label">
                                        <i className="ti ti-phone me-2" />
                                        Phone
                                      </div>
                                      <div className="detail-value">
                                        <span>
                                          {selectedConversation.contact?.primary_phone
                                            ? selectedConversation.contact.primary_phone
                                            : "No phone"}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  (isClientAdmin || isSalesUser) && (
                                    <div className="mb-3">
                                      <button
                                        className="btn btn-light btn-sm mx-3"
                                        onClick={() => {
                                          setSelectedContactId(null);
                                          setShowContactModal(true);
                                        }}
                                      >
                                        <i className="fa fa-plus me-2" />
                                        Create Contact / Lead
                                      </button>
                                    </div>
                                  )
                                )}
                              </div>

                              <div className="detail-section">
                                <div className="section-header">
                                  <h6 className="d-flex align-items-center gap-2 mb-0">
                                    Assigned Teams
                                    {selectedConversationTeams.length > 0 && (
                                      <span className="team-count-badge">{selectedConversationTeams.length}</span>
                                    )}
                                  </h6>
                                  {updatingTeams && <span className="spinner-border spinner-border-sm text-primary" role="status" />}
                                </div>
                                <div className="section-content pt-0">
                                  <div className="team-chip-list">
                                    {selectedConversationTeams.map((team) => (
                                      <span className="team-chip" key={team.assigned_team_id} title={team.name || "Unknown"}>
                                        <i className="ti ti-users-group" />
                                        <span className="team-chip-name">{team.name || "Unknown"}</span>
                                        {canAssignTeamAndUser && (
                                          <button
                                            type="button"
                                            className="team-chip-remove"
                                            onClick={() => handleRemoveTeam(team)}
                                            disabled={updatingTeams}
                                            aria-label={`Remove ${team.name || "team"}`}
                                          >
                                            <i className="ti ti-x" />
                                          </button>
                                        )}
                                      </span>
                                    ))}
                                    {canAssignTeamAndUser && (
                                      <button
                                        type="button"
                                        className="team-chip team-chip-add"
                                        onClick={openManageTeamsModal}
                                        disabled={updatingTeams}
                                      >
                                        <i className="fa fa-plus" />
                                        Add Team
                                      </button>
                                    )}
                                    {!canAssignTeamAndUser && selectedConversationTeams.length === 0 && (
                                      <span className="text-muted fs-13">No team assigned to this channel</span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="detail-section">
                                <div
                                  className="section-header"
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <h6>Assigned User</h6>
                                  {canAssignTeamAndUser && (
                                    <button
                                      className="btn btn-sm p-0"
                                      onClick={openAssignUserModal}
                                      title={
                                        selectedConversation.assigned_user?.user_id
                                          ? "Change Assigned User"
                                          : "Assign User"
                                      }
                                    >
                                      <i
                                        className={`ti ${selectedConversation.assigned_user?.user_id
                                          ? "ti-edit"
                                          : "ti-plus"
                                          }`}
                                      />
                                    </button>
                                  )}
                                </div>
                                {selectedConversation.assigned_user ? (
                                  <div className="section-content">
                                    <div className="detail-row">
                                      <div className="detail-label">
                                        <i className="ti ti-user me-2" />
                                        Username
                                      </div>
                                      <div className="detail-value">
                                        <span>
                                          {selectedConversation.assigned_user?.full_name
                                            ? selectedConversation.assigned_user.full_name
                                            : "No Name"}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  canAssignTeamAndUser && (
                                    <div className="mb-3">
                                      <button
                                        className="btn btn-light btn-sm mx-3"
                                        onClick={openAssignUserModal}
                                      >
                                        <i className="fa fa-plus me-2" />
                                        Assign User
                                      </button>
                                    </div>
                                  )
                                )}
                              </div>

                              {selectedConversation.is_group && (
                                <div className="detail-section">
                                  <div className="section-header">
                                    <h6>Group Members</h6>
                                  </div>
                                  <div className="section-content">
                                    {fetchingGroupMembers ? (
                                      <div className="text-center p-3">
                                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                                          <span className="visually-hidden">Loading...</span>
                                        </div>
                                      </div>
                                    ) : groupMembers.length > 0 ? (
                                      <ul className="list-group list-group-flush">
                                        {groupMembers.map((member) => (
                                          <li key={member.external_chat_group_member_id} className="list-group-item d-flex align-items-center px-0 border-0 py-2">
                                            <div className="avatar avatar-sm me-2">
                                              <img
                                                src={member.member_profile_picture || member.user?.avatar_url || dummy}
                                                alt={member.member_name || member.user?.first_name || 'Member'}
                                                className="rounded-circle"
                                                onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                                              />
                                            </div>
                                            <div className="flex-grow-1 overflow-hidden">
                                              <h6 className="mb-0 text-truncate" style={{ fontSize: '13px' }}>
                                                {member.member_name || (member.user?.first_name ? `${member.user.first_name} ${member.user.last_name || ''}` : member.user?.email) || 'Unknown'}
                                              </h6>
                                              {member.user_role && <span className="text-muted text-truncate d-block" style={{ fontSize: '11px' }}>{member.user_role}</span>}
                                            </div>
                                            <div className="ms-auto">
                                              <button
                                                className="btn btn-sm btn-icon text-danger"
                                                onClick={() => handleDeleteGroupMember(member.external_chat_group_member_id)}
                                                disabled={deletingGroupMemberId === member.external_chat_group_member_id}
                                                title="Remove Member"
                                              >
                                                {deletingGroupMemberId === member.external_chat_group_member_id ? (
                                                  <span className="spinner-border spinner-border-sm" />
                                                ) : (
                                                  <i className="ti ti-trash"></i>
                                                )}
                                              </button>
                                            </div>
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <div className="text-muted text-center p-2" style={{ fontSize: '12px' }}>
                                        No members found.
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </OverlayScrollbarsComponent>
                  )}
                </div>
              </div>
            )}
            {/* End Right Panel */}
          </div>
        </div>
        {/* End Content */}
      </div>
      {/* End Page Content */}

      {/* Media Preview Modal */}
      {showMediaPreview && previewFile && (() => {
        return (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.8)" }}
            tabIndex={-1}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">Preview</h4>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCancelPreview}
                    disabled={sending}
                  ></button>
                </div>
                <div className="modal-body text-center">
                  {previewFile.type.startsWith("image/") ? (
                    <img
                      src={previewUrl}
                      alt="Preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "500px",
                        borderRadius: "8px",
                      }}
                    />
                  ) : previewFile.type.startsWith("video/") ? (
                    <video
                      src={previewUrl}
                      controls
                      style={{
                        maxWidth: "100%",
                        maxHeight: "500px",
                        borderRadius: "8px",
                      }}
                    />
                  ) : previewFile.type.startsWith("audio/") ? (
                    <audio src={previewUrl} controls style={{ width: "100%" }} />
                  ) : previewFile.type === "application/pdf" ? (
                    <object
                      data={previewUrl}
                      type="application/pdf"
                      aria-label={previewFile.name}
                      style={{ width: "100%", height: "500px", borderRadius: "8px" }}
                    />
                  ) : (
                    <div className="msg-upload-file-preview">
                      <i className="bi bi-file-earmark-text" />
                      <span>{(previewFile.size / 1024).toFixed(1)} KB</span>
                    </div>
                  )}
                  <p className="mt-3 text-muted">{previewFile.name}</p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={handleCancelPreview}
                    disabled={sending}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSendMedia}
                    disabled={sending}
                  >
                    {sending ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-send me-1" />
                        Send
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}


      {/* Image Viewer Modal */}
      {showImageViewer && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
          onClick={() => {
            setShowImageViewer(false);
            setImageZoom(1);
          }}
        >
          {/* Close Button */}
          <button
            onClick={() => {
              setShowImageViewer(false);
              setImageZoom(1);
            }}
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              background: "rgba(255, 255, 255, 0.1)",
              border: "none",
              color: "white",
              fontSize: "24px",
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)")
            }
          >
            <i className="ti ti-x" />
          </button>

          {/* Zoom Controls */}
          <div
            style={{
              position: "absolute",
              bottom: "40px",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              gap: "10px",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              padding: "10px 20px",
              borderRadius: "30px",
              zIndex: 99999,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setImageZoom(Math.max(0.5, imageZoom - 0.25))}
              disabled={imageZoom <= 0.5}
              style={{
                background: "rgba(255, 255, 255, 0.1)",
                border: "none",
                color: "white",
                fontSize: "18px",
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                cursor: imageZoom <= 0.5 ? "not-allowed" : "pointer",
                opacity: imageZoom <= 0.5 ? 0.5 : 1,
              }}
              title="Zoom Out"
            >
              <i className="fa-solid fa-minus" />
            </button>

            <button
              onClick={() => setImageZoom(1)}
              style={{
                background: "rgba(255, 255, 255, 0.1)",
                border: "none",
                color: "white",
                fontSize: "14px",
                padding: "0 16px",
                height: "36px",
                borderRadius: "18px",
                cursor: "pointer",
                minWidth: "60px",
              }}
              title="Reset Zoom"
            >
              {Math.round(imageZoom * 100)}%
            </button>

            <button
              onClick={() => setImageZoom(Math.min(3, imageZoom + 0.25))}
              disabled={imageZoom >= 3}
              style={{
                background: "rgba(255, 255, 255, 0.1)",
                border: "none",
                color: "white",
                fontSize: "18px",
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                cursor: imageZoom >= 3 ? "not-allowed" : "pointer",
                opacity: imageZoom >= 3 ? 0.5 : 1,
              }}
              title="Zoom In"
            >
              <i className="fa fa-plus" />
            </button>
          </div>

          {/* Image */}
          <img
            src={viewerImageUrl}
            alt="Full size"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              objectFit: "contain",
              transform: `scale(${imageZoom})`,
              transition: "transform 0.2s ease",
              cursor: imageZoom > 1 ? "move" : "default",
            }}
            onWheel={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (e.deltaY < 0) {
                setImageZoom(Math.min(3, imageZoom + 0.1));
              } else {
                setImageZoom(Math.max(0.5, imageZoom - 0.1));
              }
            }}
          />
        </div>
      )}

      {/* New Chat Modal */}
      {showNewChatModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999 }}
          tabIndex={-1}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">New Internal Chat</h5>
                <button
                  type="button"
                  onClick={closeNewChatModal}
                  disabled={newChatSending}
                ><i className="ti ti-x"></i></button>
              </div>
              <form onSubmit={handleNewChatSend}>
                <div className="modal-body">
                  {newChatError && (
                    <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '14px' }}>
                      <i className="ti ti-alert-circle me-2" />
                      {newChatError}
                    </div>
                  )}
                  <div className="mb-3 position-relative">
                    <label className="form-label">Recipient Email or Name</label>
                    <div className="position-relative">
                      <input
                        type="email"
                        className={`form-control ${newChatError ? 'is-invalid' : ''}`}
                        placeholder="Enter recipient email"
                        value={newChatEmail}
                        onChange={(e) => {
                          setNewChatEmail(e.target.value);
                          setIsNewChatMemberSelected(false);
                          if (newChatError) setNewChatError("");
                        }}
                        autoComplete="off"
                        required
                        disabled={newChatSending}
                      />
                      {searchingUsers && (
                        <div className="position-absolute end-0 top-50 translate-middle-y me-2">
                          <span className="spinner-border spinner-border-sm text-primary" />
                        </div>
                      )}
                    </div>
                    {showUserDropdown && filteredUsers.length > 0 && (
                      <div
                        className="dropdown-menu show w-100 mt-1 shadow-sm border"
                        style={{
                          maxHeight: '250px',
                          overflowY: 'auto',
                          zIndex: 1000
                        }}
                      >
                        {filteredUsers.map((user) => (
                          <button
                            key={user.external_chat_group_member_id}
                            type="button"
                            className="dropdown-item d-flex align-items-center py-2"
                            onClick={() => {
                              setNewChatEmail(user.member_email);
                              setIsNewChatMemberSelected(true);
                              setShowUserDropdown(false);
                              setFilteredUsers([]);
                            }}
                          >
                            <img
                              src={user.member_profile_picture || dummy}
                              alt={user.member_name}
                              className="rounded-circle me-2"
                              style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                              onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                            />
                            <div className="flex-grow-1 overflow-hidden">
                              <div className="fw-semibold text-truncate" style={{ fontSize: '13px' }}>{user.member_name}</div>
                              <div className="text-muted text-truncate" style={{ fontSize: '11px' }}>{user.member_email}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Message</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="Type your message here..."
                      value={newChatText}
                      onChange={(e) => setNewChatText(e.target.value)}
                      disabled={newChatSending}
                    ></textarea>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Attachment</label>
                    <input
                      type="file"
                      className="form-control"
                      onChange={(e) => setNewChatFile(e.target.files?.[0] || null)}
                      disabled={newChatSending}
                    />
                  </div>
                  <span className="text-muted"><span className="text-danger">*</span> Text Message or Attachment is required</span>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={closeNewChatModal}
                    disabled={newChatSending}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={newChatSending || !newChatEmail.trim()}
                  >
                    {newChatSending ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-send me-1" />
                        Send message
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Contact Selection Modal */}
      {showContactModal && (() => {
        const totalPages = Math.ceil(totalContactsCount / contactPageSize);

        return (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
            onClick={() => {
              setShowContactModal(false);
              setContactNameFilter("");
              setContactEmailFilter("");
              setContactCompanyFilter("");
              setContactCurrentPage(1);
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "8px",
                padding: "24px",
                minWidth: "900px",
                maxWidth: "1100px",
                maxHeight: "80vh",
                display: "flex",
                flexDirection: "column",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <h5 style={{ margin: 0, fontWeight: 600 }}>Select Contact</h5>
                <button
                  onClick={() => {
                    setShowContactModal(false);
                    setContactNameFilter("");
                    setContactEmailFilter("");
                    setContactCurrentPage(1);
                  }}
                  style={{
                    background: "transparent",
                    border: "none",
                    fontSize: "24px",
                    cursor: "pointer",
                    padding: 0,
                    color: "#666",
                  }}
                >
                  <i className="ti ti-x" />
                </button>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: "12px",
                  marginBottom: "20px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: 500,
                      fontSize: "14px",
                    }}
                  >
                    Filter by Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by name..."
                    value={contactNameFilter}
                    onChange={(e) => {
                      setContactNameFilter(e.target.value);
                      setContactCurrentPage(1);
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: 500,
                      fontSize: "14px",
                    }}
                  >
                    Filter by Email
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by email..."
                    value={contactEmailFilter}
                    onChange={(e) => {
                      setContactEmailFilter(e.target.value);
                      setContactCurrentPage(1);
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: 500,
                      fontSize: "14px",
                    }}
                  >
                    Filter by Company
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by company..."
                    value={contactCompanyFilter}
                    onChange={(e) => {
                      setContactCompanyFilter(e.target.value);
                      setContactCurrentPage(1);
                    }}
                  />
                </div>
              </div>

              <div
                style={{
                  flex: 1,
                  overflow: "auto",
                  marginBottom: "16px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px",
                  minHeight: 0,
                }}
              >
                {contactsLoading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "60px",
                    }}
                  >
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <table className="table table-hover mb-0">
                    <thead
                      style={{
                        position: "sticky",
                        top: 0,
                        backgroundColor: "#f8f9fa",
                        zIndex: 1,
                      }}
                    >
                      <tr>
                        <th style={{ padding: "12px", fontWeight: 600 }}> </th>
                        <th style={{ padding: "12px", fontWeight: 600 }}>Name</th>
                        <th style={{ padding: "12px", fontWeight: 600 }}>Email</th>
                        <th style={{ padding: "12px", fontWeight: 600 }}>Company</th>
                        <th style={{ padding: "12px", fontWeight: 600 }}>Phone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.length > 0 ? (
                        contacts.map((contact: any) => (
                          <tr
                            key={contact.contact_id}
                            style={{
                              cursor: "pointer",
                              backgroundColor:
                                selectedContactId === contact.contact_id
                                  ? "#e3f2fd"
                                  : "transparent",
                            }}
                            onClick={() =>
                              setSelectedContactId(contact.contact_id)
                            }
                          >
                            <td style={{ padding: "12px" }}>
                              <input
                                type="radio"
                                name="contact-selection"
                                checked={selectedContactId === contact.contact_id}
                                onChange={() =>
                                  setSelectedContactId(contact.contact_id)
                                }
                                style={{ cursor: "pointer" }}
                              />
                            </td>
                            <td style={{ padding: "12px" }}>
                              {contact.salutation ? `${contact.salutation} ${contact.contact_full_name || ""}`.trim() || "N/A" : contact.contact_full_name || "N/A"}
                            </td>
                            <td style={{ padding: "12px" }}>
                              {contact.primary_email || "N/A"}
                            </td>
                            <td style={{ padding: "12px" }}>
                              {contact.company_name || "N/A"}
                            </td>
                            <td style={{ padding: "12px" }}>
                              {contact.primary_phone || "N/A"}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={5}
                            style={{
                              padding: "40px",
                              textAlign: "center",
                              color: "#999",
                            }}
                          >
                            {contactNameFilter || contactEmailFilter || contactCompanyFilter
                              ? "No contacts match your filters"
                              : "No contacts available"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>

              {totalContactsCount > contactPageSize && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "16px",
                    paddingTop: "12px",
                    borderTop: "1px solid #e0e0e0",
                  }}
                >
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setContactCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={contactCurrentPage === 1 || contactsLoading}
                  >
                    <i className="ti ti-chevron-left" />
                  </button>
                  <span style={{ fontSize: "14px", color: "#666" }}>
                    Page {contactCurrentPage} of {totalPages}
                  </span>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setContactCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={contactCurrentPage === totalPages || contactsLoading}
                  >
                    <i className="ti ti-chevron-right" />
                  </button>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  justifyContent: "space-between",
                  marginBottom: 0,
                }}
              >
                <button
                  className="btn btn-outline-success"
                  onClick={handleConvertToContactClick}
                >
                  <i className="ti ti-user-plus me-2" />
                  Convert to Contact
                </button>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    className="btn btn-light"
                    onClick={() => {
                      setShowContactModal(false);
                      setContactNameFilter("");
                      setContactEmailFilter("");
                      setContactCurrentPage(1);
                    }}
                  >
                    Cancel
                  </button>
                  {selectedConversation?.contact?.contact_id && (
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => {
                        handleAssignContact(null);
                        setContactNameFilter("");
                        setContactEmailFilter("");
                        setContactCompanyFilter("");
                        setContactCurrentPage(1);
                      }}
                    >
                      <i className="ti ti-trash me-2" />
                      Remove
                    </button>
                  )}
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      handleAssignContact(selectedContactId);
                      setContactNameFilter("");
                      setContactEmailFilter("");
                      setContactCompanyFilter("");
                      setContactCurrentPage(1);
                    }}
                    disabled={!selectedContactId || assigningContactAndLead}
                  >
                    <i className="ti ti-check me-2" />
                    Assign Contact Only
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={() => handleAssignContactAndCreateLead(selectedContactId)}
                    disabled={!selectedContactId || assigningContactAndLead}
                  >
                    {assigningContactAndLead ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-user-plus me-2" />
                        Assign Contact & Create Lead
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {showConvertToContactModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
          onClick={() => {
            setShowConvertToContactModal(false);
            setConvertContactName("");
            setConvertContactEmail("");
            setConvertContactPhone("");
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "24px",
              minWidth: "600px",
              maxWidth: "700px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h5 style={{ margin: 0, fontWeight: 600 }}>Convert to Contact</h5>
              <button
                onClick={() => {
                  setShowConvertToContactModal(false);
                  setConvertContactName("");
                  setConvertContactEmail("");
                  setConvertContactPhone("");
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  padding: 0,
                  color: "#666",
                }}
              >
                <i className="ti ti-x" />
              </button>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <p style={{ color: "#666", fontSize: "14px", marginBottom: "16px" }}>
                Create a new contact from this chat. Email and phone are optional.
              </p>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontWeight: 500,
                    fontSize: "14px",
                  }}
                >
                  Name
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter name..."
                  value={convertContactName}
                  onChange={(e) => setConvertContactName(e.target.value)}
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontWeight: 500,
                    fontSize: "14px",
                  }}
                >
                  Email
                </label>
                <input
                  type="email"
                  className={`form-control ${convertEmailError ? "is-invalid" : ""}`}
                  placeholder="Enter email address..."
                  value={convertContactEmail}
                  onChange={(e) => {
                    setConvertContactEmail(e.target.value);
                    if (convertEmailError) setConvertEmailError("");
                  }}
                />
                {convertEmailError && (
                  <div className="invalid-feedback">{convertEmailError}</div>
                )}
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontWeight: 500,
                    fontSize: "14px",
                  }}
                >
                  Phone
                </label>
                <input
                  type="tel"
                  className={`form-control ${convertPhoneError ? "is-invalid" : ""}`}
                  placeholder="Enter phone number..."
                  value={convertContactPhone}
                  onChange={(e) => {
                    setConvertContactPhone(filterPhoneInput(e.target.value));
                    if (convertPhoneError) setConvertPhoneError("");
                  }}
                  pattern="[\+]?[0-9\s\-\(\)]{7,}"
                  title="Please enter a valid phone number (7-15 digits)"
                />
                {convertPhoneError && (
                  <div className="invalid-feedback">{convertPhoneError}</div>
                )}
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontWeight: 500,
                    fontSize: "14px",
                  }}
                >
                  Assign User
                </label>
                <select
                  className="form-select"
                  value={convertAssignedUserId || ""}
                  onChange={(e) =>
                    setConvertAssignedUserId(
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                  disabled={loadingAssignableUsers}
                >
                  <option value="">{loadingAssignableUsers ? "Loading users..." : "-- Select User --"}</option>
                  {displayedUsers.map((user: any) => (
                    <option key={user.user_id} value={user.user_id}>
                      {user.user_first_name || user.full_name?.split(' ')[0] || ''} {user.user_last_name || user.full_name?.split(' ').slice(1).join(' ') || ''} {user.email ? `(${user.email})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontWeight: 500,
                    fontSize: "14px",
                  }}
                >
                  Assign Team
                </label>
                <select
                  className="form-select"
                  value={convertAssignedTeamId || ""}
                  onChange={(e) =>
                    setConvertAssignedTeamId(
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                >
                  <option value="">-- Select Team --</option>
                  {teams.map((team: any) => (
                    <option key={team.team_id} value={team.team_id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="btn btn-light"
                onClick={() => {
                  setShowConvertToContactModal(false);
                  setConvertContactName("");
                  setConvertContactEmail("");
                  setConvertContactPhone("");
                }}
                disabled={convertingContact}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConvertToContact}
                disabled={convertingContact || creatingContactAndLead}
              >
                {convertingContact ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="ti ti-check me-2" />
                    Create Contact Only
                  </>
                )}
              </button>
              <button
                className="btn btn-success"
                onClick={handleConvertToContactAndCreateLead}
                disabled={convertingContact || creatingContactAndLead}
              >
                {creatingContactAndLead ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="ti ti-user-plus me-2" />
                    Create Contact and Lead
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Assigned User Selection Modal */}
      {showAssignedUserModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowAssignedUserModal(false)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "24px",
              minWidth: "400px",
              maxWidth: "500px",
              maxHeight: "600px",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <h5 style={{ margin: 0 }}>Select Assigned User</h5>
              <button
                onClick={() => setShowAssignedUserModal(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                <i className="ti ti-x" />
              </button>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                }}
              >
                Assigned User
              </label>
              <select
                className="form-select"
                value={selectedAssignedUserId || ""}
                onChange={(e) =>
                  setSelectedAssignedUserId(
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
                style={{ marginBottom: "16px" }}
                disabled={loadingAssignableUsers}
              >
                <option value="">{loadingAssignableUsers ? "Loading users..." : "-- Select User --"}</option>
                {displayedUsers.map((user: any) => (
                  <option key={user.user_id} value={user.user_id}>
                    {user.user_first_name || user.full_name?.split(' ')[0] || ''} {user.user_last_name || user.full_name?.split(' ').slice(1).join(' ') || ''} {user.email ? `(${user.email})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="btn btn-light"
                onClick={() => setShowAssignedUserModal(false)}
              >
                Cancel
              </button>
              {selectedAssignedUserId && (
                <button
                  className="btn btn-outline-primary"
                  onClick={() => handleAssignUser(null)}
                >
                  <i className="ti ti-trash me-2" />
                  Remove
                </button>
              )}
              <button
                className="btn btn-primary"
                onClick={() => handleAssignUser(selectedAssignedUserId)}
                disabled={!selectedAssignedUserId || loadingAssignableUsers}
              >
                <i className="ti ti-check me-2" />
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {showAssignedTeamModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowAssignedTeamModal(false)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "24px",
              width: "500px",
              maxWidth: "calc(100vw - 32px)",
              maxHeight: "600px",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <h5 style={{ margin: 0 }}>Manage Teams</h5>
              <button
                onClick={() => setShowAssignedTeamModal(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                <i className="ti ti-x" />
              </button>
            </div>

            {selectedConversationTeams.length > 0 && (
              <div style={{ marginBottom: "20px" }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label style={{ fontWeight: "500", margin: 0 }}>Assigned Teams</label>
                  {removedTeamIds.length > 0 && (
                    <span className="text-danger fs-12">
                      {removedTeamIds.length} {removedTeamIds.length === 1 ? "team" : "teams"} will be removed
                    </span>
                  )}
                </div>
                <div className="team-chip-list">
                  {selectedConversationTeams.map((team) => {
                    const isRemoved = removedTeamIds.includes(team.assigned_team_id);
                    return (
                      <span
                        className={`team-chip${isRemoved ? " team-chip-removed" : ""}`}
                        key={team.assigned_team_id}
                        title={team.name || "Unknown"}
                      >
                        <i className="ti ti-users-group" />
                        <span className="team-chip-name">{team.name || "Unknown"}</span>
                        <button
                          type="button"
                          className={`team-chip-remove${isRemoved ? " team-chip-undo" : ""}`}
                          onClick={() => toggleTeamRemoval(team.assigned_team_id)}
                          disabled={updatingTeams}
                          aria-label={`${isRemoved ? "Keep" : "Remove"} ${team.name || "team"}`}
                          title={isRemoved ? "Undo" : "Remove"}
                        >
                          <i className={`ti ${isRemoved ? "ti-arrow-back-up" : "ti-x"}`} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                }}
              >
                Add Teams
              </label>
              <Select
                isMulti
                closeMenuOnSelect={false}
                placeholder="-- Select Teams --"
                noOptionsMessage={() => "All teams are already assigned"}
                options={teams
                  .filter((team: any) => !selectedConversationTeams.some((assigned) => assigned.assigned_team_id === team.team_id))
                  .map((team: any) => ({ value: team.team_id as number, label: team.name as string }))}
                value={selectedAssignedTeamIds.map((teamId) => ({
                  value: teamId,
                  label: teams.find((team: any) => team.team_id === teamId)?.name || String(teamId),
                }))}
                onChange={(options) => setSelectedAssignedTeamIds(options.map((option) => option.value))}
                menuPortalTarget={document.body}
                styles={{
                  control: (base) => ({ ...base, height: "auto", minHeight: "41px" }),
                  valueContainer: (base) => ({ ...base, maxHeight: "120px", overflowY: "auto", gap: "4px", padding: "4px 8px" }),
                  multiValue: (base) => ({ ...base, margin: 0 }),
                  menuPortal: (base) => ({ ...base, zIndex: 10000 }),
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="btn btn-light"
                onClick={() => setShowAssignedTeamModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveTeams}
                disabled={(selectedAssignedTeamIds.length === 0 && removedTeamIds.length === 0) || updatingTeams}
              >
                {updatingTeams ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                ) : (
                  <i className="ti ti-check me-2" />
                )}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Group Modal */}
      {/* Create Group Modal */}
      {showCreateGroupModal && (
        <>
          <div
            className="modal fade show"
            style={{ display: "block", marginLeft: "8%" }}
            role="dialog"
            onClick={() => {
              setShowCreateGroupModal(false);
              setSelectedGroupUserId("");
              setSelectedGroupUserRole("Sales Person");
            }}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              style={{ maxWidth: "500px", width: "100%" }}
            >
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header border-bottom">
                  <h4>{selectedConversation?.is_group ? "Manage Member" : "Create Group"}</h4>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowCreateGroupModal(false);
                      setSelectedGroupUserId("");
                      setSelectedGroupUserRole("Sales Person");
                    }}
                  />
                </div>
                <div className="modal-body">
                  {selectedConversation?.is_group && groupMembers.length > 0 && (
                    <div className="mb-4">
                      <label className="form-label">Current Members</label>
                      <ul className="list-group list-group-flush border rounded">
                        {groupMembers.map((member) => (
                          <li key={member.external_chat_group_member_id} className="list-group-item d-flex justify-content-between align-items-center py-2">
                            <div className="d-flex align-items-center">
                              <img
                                src={member.member_profile_picture || member.user?.avatar_url || dummy}
                                alt="Member"
                                className="rounded-circle me-2"
                                style={{ width: "28px", height: "28px", objectFit: "cover" }}
                                onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                              />
                              <div>
                                <div style={{ fontSize: "13px", fontWeight: "500", lineHeight: "1.2" }}>
                                  {member.member_name || (member.user?.first_name ? `${member.user.first_name} ${member.user.last_name || ''}` : member.user?.email) || 'Unknown'}
                                </div>
                                {member.user_role && <div style={{ fontSize: "11px", color: "#6c757d", lineHeight: "1.2" }}>{member.user_role}</div>}
                              </div>
                            </div>
                            <button
                              className="btn btn-sm btn-icon text-danger"
                              onClick={() => handleDeleteGroupMember(member.external_chat_group_member_id)}
                              disabled={deletingGroupMemberId === member.external_chat_group_member_id}
                              title="Remove Member"
                            >
                              {deletingGroupMemberId === member.external_chat_group_member_id ? (
                                <span className="spinner-border spinner-border-sm" />
                              ) : (
                                <i className="ti ti-trash"></i>
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">{selectedConversation?.is_group ? "Add New User" : "User"}</label>
                    <Select
                      options={tenantUsers.map(user => {
                        const firstName = user.user_first_name || user.full_name?.split(' ')[0] || '';
                        const lastName = user.user_last_name || user.full_name?.split(' ').slice(1).join(' ') || '';
                        return {
                          value: user.user_id,
                          label: `${firstName} ${lastName} ${user.email ? `(${user.email})` : ''}`.trim(),
                          user: user
                        };
                      })}
                      value={selectedGroupUserId ? (() => {
                        const user = tenantUsers.find(u => u.user_id === selectedGroupUserId);
                        if (!user) return null;
                        const firstName = user.user_first_name || user.full_name?.split(' ')[0] || '';
                        const lastName = user.user_last_name || user.full_name?.split(' ').slice(1).join(' ') || '';
                        return {
                          value: user.user_id,
                          label: `${firstName} ${lastName} ${user.email ? `(${user.email})` : ''}`.trim(),
                          user: user
                        };
                      })() : null}
                      onChange={(option: any) => setSelectedGroupUserId(option ? option.value : "")}
                      formatOptionLabel={(option: any) => {
                        const firstName = option.user?.user_first_name || option.user?.full_name?.split(' ')[0] || '';
                        const lastName = option.user?.user_last_name || option.user?.full_name?.split(' ').slice(1).join(' ') || '';
                        return (
                          <div className="d-flex align-items-center">
                            <img
                              src={option.user?.avatar_url || option.user?.profile_url || dummy}
                              alt="avatar"
                              style={{ width: "24px", height: "24px", borderRadius: "50%", marginRight: "8px", objectFit: "cover" }}
                              onError={(e) => { (e.target as HTMLImageElement).src = dummy; }}
                            />
                            <div style={{ lineHeight: "1.2" }}>
                              <div style={{ fontSize: "14px", fontWeight: "500" }}>{firstName} {lastName}</div>
                              {option.user?.email && <div style={{ fontSize: "12px", color: "#6c757d" }}>{option.user.email}</div>}
                            </div>
                          </div>
                        );
                      }}
                      placeholder="Select User"
                      isClearable
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Role</label>
                    <select
                      className="form-select"
                      value={selectedGroupUserRole}
                      onChange={(e) => setSelectedGroupUserRole(e.target.value)}
                    >
                      <option value="Sales Person">Sales Person</option>
                      <option value="Advisor">Advisor</option>
                      <option value="Sales Manager">Sales Manager</option>
                      <option value="Account Executive">Account Executive</option>
                      <option value="Regional Director">Regional Director</option>
                      <option value="VP of Sales">VP of Sales</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer border-top">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowCreateGroupModal(false);
                      setSelectedGroupUserId("");
                      setSelectedGroupUserRole("Sales Person");
                    }}
                    disabled={creatingGroupMember}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={async () => {
                      if (!selectedGroupUserId || !selectedConversation) return;
                      setCreatingGroupMember(true);
                      try {
                        await ChatService.createGroupMember({
                          tenant_id: Number(selectedTenantId),
                          external_chat_bot_channel_account_id: null,
                          external_chat_platform_id: 1, // Fixed ID from payload instructions
                          user_id: Number(selectedGroupUserId),
                          user_role: selectedGroupUserRole,
                          added_by: Number(userId),
                          external_chat_end_user_id: selectedConversation.external_chat_end_user_id,
                          from_portal: true,
                          member_id: "",
                          member_name: "",
                          member_email: "",
                          member_profile_picture: ""
                        });
                        setShowCreateGroupModal(false);
                        setSelectedGroupUserId("");
                        setSelectedGroupUserRole("Sales Person");
                        MySwal.fire({
                          icon: "success",
                          title: "Group Member Added",
                          timer: 2000,
                          showConfirmButton: false,
                        });
                        await fetchConversations();
                        if (selectedConversation) fetchMessages(selectedConversation);
                      } catch (error: any) {
                        let errorMessage = "Failed to add group member";
                        try {
                          const errorParts = error.message.split("Failed to create group member: ");
                          if (errorParts.length > 1) {
                            const errorJson = JSON.parse(errorParts[1]);
                            if (errorJson.error) {
                              errorMessage = errorJson.error;
                            }
                          }
                        } catch (e) {
                          // keep default
                        }

                        MySwal.fire({
                          icon: "error",
                          title: "Error",
                          text: errorMessage,
                        });
                      } finally {
                        setCreatingGroupMember(false);
                      }
                    }}
                    disabled={!selectedGroupUserId || creatingGroupMember}
                  >
                    {creatingGroupMember ? (selectedConversation?.is_group ? "Adding..." : "Creating...") : (selectedConversation?.is_group ? "Add Member" : "Create Group")}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => {
            setShowCreateGroupModal(false);
            setSelectedGroupUserId("");
            setSelectedGroupUserRole("Sales Person");
          }} />
        </>
      )}

    </>
  );
};

export default Chat;